import type { ClinicalQuestionDetails } from './doctor/clinical-question-details-view';
import type { ClinicalExaminationView } from './doctor/clinical-examination-view';
import {postDrawer} from './money/drawer-recovery';
import { postBillMutation, postPaymentVoid } from '@/dashboard/billing/bill-mutation-recovery';
/**
 * Typed dashboard API calls — maps backend `/api/*` shapes to UI types.
 */

import {
  portalApiDownload,
  portalApiPatch,
  portalApiDelete,
  portalApiGet,
  portalApiPost,
  portalApiPut,
} from '@/lib/portal-api';
import type {
  MoneyDayWire,
  MoneyDrawerEntryWire,
  MoneyClosedDayWire,
} from '@/dashboard/money/money-desk-view';
import type { MoneyWorkspaceWire } from '@/dashboard/money/money-workspace-view';
import type {
  CampaignLeadAttributionHealth,
  ClinicPerformanceRow,
  CocClinicSummary,
  ContactTimelineEventType,
  ContactTimelineResult,
  ContactTimelineSummary,
  DashboardMe,
  DashboardSummary,
  LeadsWorkspaceClinicOption,
  DoctorDayView,
  ClinicRankRow,
  LeadWalkinBridgeHealth,
  OwnerAnomalyIssue,
  OwnerHomeRange,
  OwnerHomeWire,
  OwnerSourceBreakdown,
  OwnerMarketingSpend,
  OwnerLeakageSnapshot,
  OwnerLeakageDataMode,
  TodayAppointmentsData,
  WorkstationData,
  ZoneSummary,
} from '@/dashboard/types';
import type { CallActionSummaryInput } from '@/dashboard/owner/call-action-view';
import type {
  Lead360Event,
  Lead360Input,
  Lead360SlaState,
  LeadOwnershipView,
} from '@/dashboard/owner/lead-360-view';
import type {
  CreatePatientPayload,
  CreatePatientResponse,
  MergeDecisionPayload,
  MergeDecisionResponse,
  MergeQueueResponse,
  PatientRecordResponse,
  PatientSearchResponse,
} from '@/dashboard/patients/patient-master-view';
import type { LeadPatientCrossmatchResult } from '@/dashboard/patients/lead-patient-crossmatch-view';
import type {
  Patient360SummaryResponse,
  Patient360AppointmentWire,
  Patient360PaymentItemWire,
  Patient360VoidedPaymentItemWire,
  Patient360TreatmentCheckoutWire,
  Patient360TreatmentJourneyWire,
  PatientNextVisitIntentWire,
  PaymentEvidenceState,
  FollowUpEvidenceState,
  CommercialConversionStateWire,
} from '@/dashboard/patients/patient-360-view';
import type { PatientCandidateCard } from '@/dashboard/patients/patient-master-view';
import type { PatientTimelineResponse } from '@/dashboard/patients/patient-timeline-view';
import type {
  WorkRoutingConfigRow,
  WorkRoutingLeadActionRow,
  WorkRoutingRouteResult,
} from '@/dashboard/owner/work-routing-qa-view';
import type {
  FollowupOwnershipSettingsResponse,
  FollowupOwnershipValues,
  RoutingLifecycleResult,
  RoutingSettingsResponse,
  RoutingSettingUpdateRequest,
  RoutingSettingUpdateResult,
} from '@/dashboard/work-routing/work-routing-settings-view';
import type {
  FollowupRow,
  NextVisitScheduleRowView,
} from '@/walkin/WalkinFollowupsPanel';
import type {
  BillingEntryContext,
  BillingQueueResponse,
  PatientPaymentRow,
  RecentPatientPaymentsResponse,
  RecordPatientPaymentResponse,
} from '@/dashboard/billing/billing-entry-view';
import type {
  CheckoutHandoffResponse,
  TreatmentDiscountWireMirror,
} from '@/dashboard/billing/checkout-handoff-view';
import type {
  OrgIntakeQueueResult,
  SetOrgIntakeClinicResult,
} from '@/dashboard/org-intake/org-intake-view';
import type { LeadInboxResult } from '@/dashboard/lead-inbox/lead-inbox-view';
import type { OwnerFunnelSnapshot } from '@/dashboard/owner/owner-funnel-view';
import type { ManagerTrackingView } from '@/dashboard/manager-tracking/manager-tracking-view';
import type { SlaLeakageResult } from '@/dashboard/sla-leakage/sla-leakage-view';
import type { CallcenterDailyReportWire } from '@/dashboard/callcenter/daily-report/callcenter-daily-report-view';
import type {
  BillDoctorOption,
  CreatePatientBillResponseMirror,
  PatientBillListResponseMirror,
  PatientBillResponseMirror,
} from '@/dashboard/billing/bill-view';
import { SLA_CLASS_ORDER } from '@/dashboard/sla-leakage/sla-leakage-view';

/**
 * Backend GET /api/dashboard/me — role + scope summary for the
 * signed-in user. Drives role-aware nav rendering in
 * `DashboardLayout` and the post-login home redirect.
 *
 * Concurrent callers are COALESCED into a single in-flight request. A dashboard
 * mount otherwise fires this from three independent components (the AppLayout
 * header, the DashboardLayout sidebar, and the /dashboard index redirect), each
 * doubled by React StrictMode -- four-plus identical /dashboard/me calls. On an
 * auth failure that fan-out burns the per-IP failure budget and lets the rate
 * limiter mask the real reason (the 2026-07-21 incident). Coalescing collapses
 * the mount into ONE request; the in-flight handle is cleared as soon as it
 * settles, so a later mount (or a re-login) always fetches fresh.
 */
let inFlightDashboardMe: Promise<DashboardMe> | null = null;

export async function fetchDashboardMe(): Promise<DashboardMe> {
  if (inFlightDashboardMe) return inFlightDashboardMe;
  const request = portalApiGet<DashboardMe>('/dashboard/me');
  inFlightDashboardMe = request;
  try {
    return await request;
  } finally {
    if (inFlightDashboardMe === request) inFlightDashboardMe = null;
  }
}

/**
 * Backend GET /api/walkins/workstation — receptionist workstation load.
 * `portalApiGet` already unwraps the `{ success, data }` envelope, so the
 * resolved value is the `WorkstationData` payload directly. Always live
 * (the WORKSTATION_V1_ENABLED gate was removed in S2-P5E-B1).
 */
export async function fetchWalkinWorkstation(): Promise<WorkstationData> {
  return portalApiGet<WorkstationData>('/walkins/workstation');
}

/**
 * Backend POST /api/walkins/:id/source (X12, api/routes/walkins.ts
 * correctWalkinSource) -- the audited correction of an already-captured
 * walk-in's source. `sourceDetail` is THREE-WAY: omit the key to preserve
 * the existing value, send `null` (or an empty string, which the backend
 * normalizes to the same clear intent) to clear it, or a string to set it.
 * `reason` is REQUIRED (the backend's own accountable-correction rule).
 * Every call writes exactly one WALKIN_SOURCE_CORRECTED audit row when it
 * succeeds; a stale read races another caller's correction and surfaces
 * `{ code: 'conflict' }` via a 409 PortalApiError, never a silent overwrite.
 * `portalApiPost` unwraps the `{ success, data }` envelope on 2xx; a
 * validation/conflict/audit failure surfaces as a PortalApiError with the
 * matching HTTP status (400/409/500) and the endpoint's own error body.
 *
 * `expectedSource`/`expectedSourceDetail`/`expectedSourceConfidence`
 * (H3-CORRECTIVE-2, 2026-09-10) are the FORM-OPEN-TIME baseline -- what
 * the caller believes is currently stored. REQUIRED TOGETHER when any is
 * sent (a partial baseline is a validation error, never a silently-
 * ignored hint). The server keys its compare-and-swap on THIS baseline
 * rather than a request-time re-read, so a form left open while another
 * correction lands is caught even when the two submissions do not
 * overlap in time -- a fresh-read-only compare-and-swap cannot catch that
 * sequential case.
 */
export interface CorrectWalkinSourceBody {
  readonly source: string;
  readonly sourceDetail?: string | null;
  readonly sourceConfidence?: string;
  readonly reason: string;
  readonly expectedSource?: string;
  readonly expectedSourceDetail?: string | null;
  readonly expectedSourceConfidence?: string;
}

export interface CorrectWalkinSourceResponse {
  readonly walkinId: number;
  readonly previousSource: string;
  readonly nextSource: string;
}

export async function correctWalkinSource(
  walkinId: number | string,
  body: CorrectWalkinSourceBody,
): Promise<CorrectWalkinSourceResponse> {
  return portalApiPost<CorrectWalkinSourceResponse>(
    `/walkins/${encodeURIComponent(String(walkinId))}/source`,
    body,
  );
}

/**
 * Backend GET /api/appointments/today - today's UPCOMING appointments for a
 * clinic (standalone read; PHI-bounded). `portalApiGet` unwraps the
 * `{ success, data }` envelope, so the resolved value is the
 * `TodayAppointmentsData` payload directly. When `clinicId` is omitted the
 * backend resolves the caller's active clinic from session scope; passing it
 * pins the read to a clinic the caller already holds (owner org-bound).
 */
/**
 * LES-057 (SEP15-FRONT-DESK-APPOINTMENT-TO-VISIT-VERTICAL-V1): normalize the
 * day-read wire ONCE at this API-entry boundary. The SEP15 additive board
 * fields (assignedDoctorName / canonicalStatus) fill their truthful
 * absent-default (null) so an older/partial payload (a seeded e2e fixture, a
 * backend mid-rollout) can never crash a consumer on an undefined shape.
 * Existing fields pass through untouched.
 */
export function normalizeTodayAppointments(
  data: TodayAppointmentsData,
): TodayAppointmentsData {
  const appointments = Array.isArray(data?.appointments)
    ? data.appointments.map((a) => ({
        ...a,
        assignedDoctorName: a.assignedDoctorName ?? null,
        canonicalStatus: a.canonicalStatus ?? null,
        // DAY-BOARD-DOCTOR-ASSIGNMENT-VERTICAL-V1: the roster id beside the
        // name; same LES-057 truthful absent-default.
        assignedDoctorId: a.assignedDoctorId ?? null,
      }))
    : [];
  // The assign picker's roster: absent/malformed -> [] (the picker then
  // renders its honest empty state, never a crash).
  const clinicDoctors = Array.isArray(data?.clinicDoctors)
    ? data.clinicDoctors
        .filter(
          (d) =>
            d !== null &&
            typeof d === 'object' &&
            Number.isInteger(d.id) &&
            typeof d.name === 'string' &&
            d.name.trim() !== '',
        )
        // APPOINTMENT-BOOK-V1 (S1b): specialty gets the SAME LES-057
        // treatment -- one invariant shape for every consumer, so an older
        // payload renders the honest "Specialty not set" instead of
        // undefined. The id/name filter above is UNCHANGED.
        .map((d) => ({
          ...d,
          specialty:
            typeof d.specialty === 'string' && d.specialty.trim() !== ''
              ? d.specialty.trim()
              : null,
        }))
    : [];
  return { ...data, appointments, clinicDoctors };
}

export async function fetchTodayAppointments(
  clinicId?: number,
  view?: 'board',
  date?: string,
  endDate?: string,
): Promise<TodayAppointmentsData> {
  const params: string[] = [];
  if (clinicId !== undefined) {
    params.push(`clinicId=${encodeURIComponent(String(clinicId))}`);
  }
  // TRAIN-B: the clinic day board is the SAME read widened server-side
  // (full-day statuses + ops-status), fenced to the board roles.
  if (view === 'board') params.push('view=board');
  // APPOINTMENT-BOOK-V1: the planning date ('YYYY-MM-DD', IST). Omitted =
  // today, byte-identical for every existing caller. The SERVER owns the
  // shape check (a malformed value returns 400) -- one validator.
  if (date !== undefined && date !== '') {
    params.push(`date=${encodeURIComponent(date)}`);
  }
  if (endDate) params.push(`endDate=${encodeURIComponent(endDate)}`);
  const q = params.length > 0 ? `?${params.join('&')}` : '';
  const data = await portalApiGet<TodayAppointmentsData>(
    `/appointments/today${q}`,
  );
  // LES-057: one boundary, one invariant shape for every consumer.
  return normalizeTodayAppointments(data);
}

/**
 * DASHBOARD-FANOUT-AT-119-V1 -- the ALL-CLINICS day board: ONE request
 * covering every clinic in the caller's verified scope, replacing one
 * request per clinic. Same endpoint, same board view, same rows; each row
 * carries its own `clinicId` so the caller groups them and runs the SAME
 * pure derivations per clinic it ran before.
 *
 * The server re-tests every id against the caller's own scope (a foreign id
 * is a scope error, never a silent narrowing), so this widens the number of
 * round trips downward and the visible scope not at all.
 */
export async function fetchTodayAppointmentsForClinics(
  clinicIds: readonly number[],
  date?: string,
): Promise<TodayAppointmentsData> {
  const params: string[] = [
    `clinicIds=${encodeURIComponent(clinicIds.join(','))}`,
    'view=board',
  ];
  if (date !== undefined && date !== '') {
    params.push(`date=${encodeURIComponent(date)}`);
  }
  const data = await portalApiGet<TodayAppointmentsData>(
    `/appointments/today?${params.join('&')}`,
  );
  // LES-057: the SAME boundary normalizer every other consumer goes through.
  return normalizeTodayAppointments(data);
}

/**
 * SHIP-LANE-1 OWNER TRUTH CORRECTIONS (FIX 1): the ONE query builder for the
 * owner selector-bar filters the Reports bands pass through. `from`/`to` are
 * the window the SERVER already resolved for the selected preset (echoed on
 * the funnel snapshot as `range.from` / `range.to`) -- this client never
 * re-derives a date window, so there is no second range resolver. `clinicId`
 * maps to the `clinic_id` query name the owner health routes validate against
 * the caller's own reach (an out-of-scope id is a server 400, never a client
 * decision). Empty/absent values are omitted so the endpoint default stands.
 */
function ownerFilterQuery(params?: {
  from?: string;
  to?: string;
  clinicId?: string | null;
}): string {
  const q: string[] = [];
  if (params?.from) q.push(`from=${encodeURIComponent(params.from)}`);
  if (params?.to) q.push(`to=${encodeURIComponent(params.to)}`);
  if (params?.clinicId) {
    q.push(`clinic_id=${encodeURIComponent(params.clinicId)}`);
  }
  return q.length > 0 ? `?${q.join('&')}` : '';
}

/**
 * Backend GET /api/owner/analytics — walk-in source breakdown for the
 * last 7 days. `portalApiGet` unwraps the `{ success, data }` envelope,
 * so the resolved value is the `OwnerSourceBreakdown` payload directly.
 */
export async function fetchOwnerSourceBreakdown(): Promise<OwnerSourceBreakdown> {
  return portalApiGet<OwnerSourceBreakdown>('/owner/analytics');
}


/** Backend GET /api/owner/dashboard */
export interface OwnerDashboardApiResponse {
  summary: Record<string, unknown>;
  clinics: Record<string, unknown>[];
}

export async function fetchOwnerDashboard(date?: string): Promise<{
  summary: DashboardSummary;
  clinics: ClinicPerformanceRow[];
}> {
  const q = date ? `?date=${encodeURIComponent(date)}` : '';
  const raw = await portalApiGet<OwnerDashboardApiResponse>(`/owner/dashboard${q}`);
  const summary = raw.summary as unknown as DashboardSummary;
  const clinics = (raw.clinics ?? []).map(mapOwnerClinicRow);
  return { summary, clinics };
}

/**
 * OWNER-OPERATING-SYSTEM-V1: GET /api/owner/home -- the Home digest wire
 * (selected window + O-2 like-window comparisons + month so far + issues).
 * Params mirror the approved selector controls (HOME RULING).
 */
export async function fetchOwnerHome(params?: {
  range?: OwnerHomeRange;
  from?: string;
  to?: string;
  clinicId?: number | null;
}): Promise<OwnerHomeWire> {
  const q: string[] = [];
  if (params?.range) q.push(`range=${encodeURIComponent(params.range)}`);
  if (params?.from) q.push(`from=${encodeURIComponent(params.from)}`);
  if (params?.to) q.push(`to=${encodeURIComponent(params.to)}`);
  if (params?.clinicId !== undefined && params?.clinicId !== null) {
    q.push(`clinicId=${encodeURIComponent(String(params.clinicId))}`);
  }
  const suffix = q.length > 0 ? `?${q.join('&')}` : '';
  return portalApiGet<OwnerHomeWire>(`/owner/home${suffix}`);
}

/**
 * ORG-CONFIG-ACCESS-AND-PRODUCT-MAPPING-V1: the catalog -> reporting-row
 * mapping worksheet. Server-fenced by the ORGANIZATION-SCOPED CONFIG
 * CAPABILITY (config_management.authorized_roles) -- this client carries no
 * role logic and makes no access decision of its own.
 */
export async function fetchCatalogRowMapping(): Promise<
  import('./types').CatalogMappingViewWire
> {
  return portalApiGet<import('./types').CatalogMappingViewWire>(
    '/config/catalog-row-mapping',
  );
}

/**
 * Set (rowKey = a row key) or CLEAR (rowKey = null) ONE catalog item's
 * reporting-row mapping, returning the refreshed worksheet. The server
 * re-checks the capability, validates the row key against the org's own rows,
 * writes ONLY owner_money_console.config, and audits who/what/when.
 */
export async function saveCatalogRowMapping(payload: {
  catalogCode: string;
  rowKey: string | null;
}): Promise<import('./types').CatalogMappingViewWire> {
  return portalApiPost<import('./types').CatalogMappingViewWire>(
    '/config/catalog-row-mapping',
    payload,
  );
}

/**
 * OWNER-MONEY-CONSOLE-V1 (M6): GET /api/owner/money-console -- the owner/
 * CEO daily money console (per-account daily/EOD + all-clinics rollup).
 * Owner-fenced server-side; read-only.
 */
export async function fetchOwnerMoneyConsole(params?: {
  range?: import('./types').OwnerMoneyConsoleRange;
  from?: string;
  to?: string;
  date?: string;
  clinicId?: number | null;
  doctorId?: number | null;
}): Promise<import('./types').OwnerMoneyConsoleResponse> {
  const q: string[] = [];
  if (params?.range) q.push(`range=${encodeURIComponent(params.range)}`);
  if (params?.from) q.push(`from=${encodeURIComponent(params.from)}`);
  if (params?.to) q.push(`to=${encodeURIComponent(params.to)}`);
  if (params?.date) q.push(`date=${encodeURIComponent(params.date)}`);
  if (params?.clinicId !== undefined && params?.clinicId !== null) {
    q.push(`clinicId=${encodeURIComponent(String(params.clinicId))}`);
  }
  if (params?.doctorId !== undefined && params?.doctorId !== null) {
    q.push(`doctorId=${encodeURIComponent(String(params.doctorId))}`);
  }
  const suffix = q.length > 0 ? `?${q.join('&')}` : '';
  return portalApiGet<import('./types').OwnerMoneyConsoleResponse>(
    `/owner/money-console${suffix}`,
  );
}

/**
 * Backend GET /api/owner/anomalies — returns an array of unresolved daily_flag
 * rows scoped to the owner's clinics. Phase 1: read-only top-issues feed for
 * the 30-second view. Backend already audits access, applies severity/date
 * sort, and limits the result; we re-pass `limit` so callers can request a
 * smaller slice (the owner dashboard uses limit=3).
 */
export async function fetchOwnerAnomalies(
  date?: string,
  limit?: number,
): Promise<OwnerAnomalyIssue[]> {
  const params: string[] = [];
  if (date) params.push(`date=${encodeURIComponent(date)}`);
  if (limit !== undefined) {
    params.push(`limit=${encodeURIComponent(String(limit))}`);
  }
  const q = params.length > 0 ? `?${params.join('&')}` : '';
  const rows = await portalApiGet<Record<string, unknown>[]>(
    `/owner/anomalies${q}`,
  );
  return (rows ?? []).map((row) => ({
    clinicId: String(row.clinicId ?? ''),
    clinicName: String(row.clinicName ?? ''),
    flagType: String(row.flagType ?? ''),
    description: String(row.description ?? ''),
    severity: String(row.severity ?? ''),
    date: String(row.date ?? ''),
  }));
}

function mapOwnerClinicRow(row: Record<string, unknown>): ClinicPerformanceRow {
  const billing = row.billingStatus;
  const bs =
    billing === 'submitted' || billing === 'pending' || billing === 'missing'
      ? billing
      : 'pending';
  return {
    clinicId: String(row.clinicId ?? ''),
    clinicName: String(row.clinicName ?? ''),
    revenue: Number(row.revenue ?? 0),
    patients: Number(row.patients ?? 0),
    noShowRate: Number(row.noShowRate ?? 0),
    ticketSize: Number(row.avgTicket ?? row.ticketSize ?? 0),
    billingStatus: bs,
    hasAnomalies: Boolean(row.hasAnomalies),
    // MONEY-COMPLETION-V1: honest nullables pass through as null (never
    // coerced to 0 -- an absent MIS row is not zero money).
    misRevenue: typeof row.misRevenue === 'number' ? row.misRevenue : null,
    recordedCollectionsMinorUnits:
      typeof row.recordedCollectionsMinorUnits === 'number'
        ? row.recordedCollectionsMinorUnits
        : null,
    // OWNER-OPERATING-SYSTEM-V1: the wire's live walk-in count, additive
    // pass-through for the Reports clinics table (never invented; 0 is the
    // wire's own value for a counted zero).
    walkins: Number(row.walkins ?? 0),
  };
}

/** Backend GET /api/coc/clinics — array */
export async function fetchCocClinics(): Promise<CocClinicSummary[]> {
  const rows = await portalApiGet<Record<string, unknown>[]>('/coc/clinics');
  return (rows ?? []).map((row) => ({
    clinicId: String(row.clinicId ?? ''),
    clinicName: String(row.clinicName ?? ''),
    doctorConfirmed: Boolean(row.doctorConfirmed),
    billingSubmitted: Boolean(row.billingSubmitted),
    openNoShows: Number(row.openNoShows ?? 0),
    patientsToday: Number(row.patientsToday ?? 0),
    revenueToday:
      row.revenueToday !== undefined && row.revenueToday !== null
        ? Number(row.revenueToday)
        : undefined,
    alertCount: Number(row.alertCount ?? 0),
    lastActivityMins: Number(row.lastActivityMins ?? 0),
  }));
}

// --- COC station (COC-STATION-V1, V4-P1) -----------------------------------
//
// The COC role's daily home + the manager workload view. Typed mirrors of
// api/routes/coc-station.ts wire shapes. PHI-free by construction upstream
// (carved names, labels, ids, controlled tokens parsed to typed fields).

export interface StationSlaChipView {
  readonly breachClass: string;
  readonly ageMinutes: number;
  readonly severity: 'neutral' | 'amber' | 'red';
}

export type StationSegmentView =
  | 'mine'
  | 'pool'
  | 'other'
  | 'converted_link_pending';

export interface StationNextActionView {
  readonly kind: 'claim' | 'record_outcome' | 'open_record';
  readonly text: string;
  readonly callbackAtIso: string | null;
}

export interface CocStationRowView {
  readonly leadId: number;
  readonly safeLeadId: string;
  readonly displayName: string;
  readonly clinicId: number | null;
  readonly clinicLabel: string | null;
  readonly serviceInterest: string | null;
  readonly stageLabel: string | null;
  readonly createdAtIso: string | null;
  readonly assignedStaffId: number | null;
  readonly assignedLabel: string | null;
  readonly isMine: boolean;
  readonly segment: StationSegmentView;
  readonly slaChips: readonly StationSlaChipView[];
  readonly lastAction: { readonly label: string; readonly atIso: string | null } | null;
  readonly lastOutcome: {
    readonly outcomeId: string | null;
    readonly outcomeLabel: string | null;
    readonly eventType: string;
    readonly atIso: string;
  } | null;
  readonly nextAction: StationNextActionView;
  readonly callbackDueAtIso: string | null;
  readonly convertedLinkPending: boolean;
}

export interface CocStationCountsView {
  readonly assignedToMe: number;
  readonly callbacksDue: number;
  readonly slaBreached: number;
  readonly newUnclaimed: number;
  readonly bookedToday: number;
}

export interface CocContactOutcomeView {
  readonly id: string;
  readonly label: string;
  readonly eventType: string;
  readonly callback: 'required' | 'optional' | 'none';
  readonly provisional: boolean;
}

export interface CocStationView {
  readonly role: string;
  readonly generatedAtIso: string;
  readonly counts: CocStationCountsView;
  readonly rows: readonly CocStationRowView[];
  readonly outcomes: readonly CocContactOutcomeView[];
  readonly clinics: readonly { clinicId: number; clinicLabel: string | null }[];
  readonly capped: boolean;
  /**
   * COC-STATION-INBOUND-CALLS-V1 -- the inbound-call activity block, the
   * SAME shape the command centre carries (one view type, never a second).
   *
   * OPTIONAL ON PURPOSE, and it is the honest type: a backend that predates
   * this package genuinely omits the field, so the surface substitutes an
   * empty block rather than trusting a shape it did not get. Rendered on
   * the COC station as MONITORING SIGNAL ONLY -- no next-action column
   * (the COC supervises inbound calling, it does not own it).
   */
  readonly inboundCalls?: InboundCallActivityView;
}

/** Backend GET /api/coc/station -- the COC daily home read. */
export async function fetchCocStation(): Promise<CocStationView> {
  return portalApiGet<CocStationView>('/coc/station');
}

// --- MISSED-APPOINTMENT-RECOVERY-V1 -----------------------------------------

/** One missed-appointment (no_show) recovery row -- mirrors the backend
 *  lib/missed-appointment-recovery.MissedAppointmentRowWire. PHI-safe: ids,
 *  the admin-plane display name, clinic label, times, the next-step label. */
export interface NoShowRecoveryRowView {
  readonly appointmentId: number;
  readonly appointmentTimeIso: string;
  readonly clinicId: number;
  readonly clinicLabel: string | null;
  readonly patientLabel: string | null;
  readonly leadId: number | null;
  readonly safeLeadId: string | null;
  readonly nextStepLabel: string;
  readonly ageMinutes: number | null;
}

export interface NoShowRecoverySummaryView {
  readonly rowCount: number;
  readonly oldestAgeMinutes: number | null;
  readonly capped: boolean;
}

export interface NoShowRecoveryView {
  readonly clinicIds: readonly number[];
  readonly rows: readonly NoShowRecoveryRowView[];
  readonly summary: NoShowRecoverySummaryView;
  readonly generatedAtIso: string;
}

/** Backend GET /api/coc/no-shows -- the missed-appointment recovery queue
 *  (no_show, newest miss first; the tile count on /manager/tracking equals
 *  this queue's length for the same scope). */
export async function fetchNoShowRecovery(
  clinicId?: number | 'all',
): Promise<NoShowRecoveryView> {
  const q =
    clinicId !== undefined && clinicId !== 'all' ? `?clinicId=${clinicId}` : '';
  return portalApiGet<NoShowRecoveryView>(`/coc/no-shows${q}`);
}

// --- COC-PATIENT-FOLLOWUP-VIEW-V1 -------------------------------------------

/** One open plan item on a patient-follow-up row -- mirrors the backend
 *  lib/domain/patient/treatment-opportunity.OpportunityPlanItemView. The D-5
 *  wire ceiling (rider R-84, narrow reading RULED): the shipped
 *  case-sheet-read-view.ts 'operational' tier fields only -- no catalog
 *  metadata, no clinical narrative. */
export interface PatientFollowupPlanItemView {
  readonly id: number;
  readonly procedureName: string;
  readonly estimatedValue: number | null;
  readonly priorityRank: number;
  readonly status: 'advised' | 'accepted' | 'declined' | 'pending';
  readonly declineReason: string | null;
  readonly completedAt: string | null;
  readonly createdAt: string;
}

/** COC-FOLLOWUP-CORE-JOURNEY-V1 (V2-P8 write half): the per-treatment-item
 *  terminal classifications the COC may record (RULING 2). `converted` is
 *  ABSENT -- it is payment-derived (REQ-TENANT-CONVERSION-FIRST-PAYMENT) and
 *  supersedes at read time; the COC never manufactures it. */
export type CocFollowupDisposition = 'declined' | 'unreachable' | 'cold';

/** The founder-CONFIRMED non-conversion reason vocabulary (DELTA 2026-08-19 E1)
 *  -- exactly two tokens, permitted ONLY under disposition='declined'
 *  (RULING 3). */
export type CocFollowupReason = 'cost' | 'other';

/** The current terminal state of ONE treatment item, mirrors the backend
 *  lib/domain/patient/coc-followup-attempt.CocItemTerminalState. `converted` is
 *  derived from payment evidence and supersedes a COC classification. */
export type CocItemTerminalState = 'open' | 'declined' | 'unreachable' | 'cold';

/** One recorded per-item disposition within a contact (ids + controlled tokens
 *  + timestamp only; PHI-free). `nextFollowupDueDate` (FOLLOWUP-DUE-DATE-
 *  PLUMBING-V1, migration 20260826_1) is the bare YYYY-MM-DD next follow-up
 *  calendar day recorded on this event; optional so an older payload omits it
 *  -- consumers read absence as null (none scheduled). */
export interface CocFollowupItemDispositionView {
  readonly id: number;
  readonly treatmentPlanItemId: number;
  readonly disposition: CocFollowupDisposition | null;
  readonly reason: CocFollowupReason | null;
  readonly nextFollowupDueDate?: string | null;
  readonly createdAtIso: string;
}

/** One recorded COC follow-up CONTACT (RULING 4/6): WHO/WHEN/patient + the per-
 *  item dispositions recorded in it. PHI-free -- ids + controlled tokens +
 *  timestamp + a supervisor-safe actor label; the raw actor principal stays
 *  server-side. */
export interface CocFollowupAttemptView {
  readonly id: number;
  readonly patientId: string;
  readonly clinicId: number;
  readonly actorRole: string;
  /** staff_users.id of the individual COC (RULING 7); null only if that staff
   *  record was later removed. */
  readonly actorStaffUserId: number | null;
  /** Supervisor-safe display name (staff_users.display_name) -- never the raw
   *  principal. */
  readonly actorLabel: string | null;
  readonly source: string;
  readonly createdAtIso: string;
  readonly items: readonly CocFollowupItemDispositionView[];
}

/** The current follow-up state of ONE open treatment item: the terminal state
 *  derived from the latest lawful disposition (RULING 2), plus who last touched
 *  it and when (supervisor-safe). Mirrors the backend
 *  lib/adapters/supabase/coc-followup-attempt-write-model.ItemFollowupState.
 *  Absent for an item before the founder applies migration 20260820_1 (honest,
 *  never a fake) -- the UI reads it as OPEN with no attribution. */
export interface CocItemFollowupState {
  readonly treatmentPlanItemId: number;
  readonly terminalState: CocItemTerminalState;
  readonly reason: CocFollowupReason | null;
  /** The item's CURRENT next follow-up calendar day (bare YYYY-MM-DD; the
   *  latest lawful disposition's date, RULING 2 -- FOLLOWUP-DUE-DATE-
   *  PLUMBING-V1). Optional so an older payload omits it; consumers read
   *  absence as null (no next follow-up scheduled). */
  readonly nextFollowupDueDate?: string | null;
  readonly lastActorLabel: string | null;
  readonly lastActorRole: string | null;
  readonly lastContactAtIso: string | null;
}

/** One patient-opportunity row -- mirrors the backend
 *  lib/domain/patient/treatment-opportunity.PatientFollowupOpportunity. The
 *  derived patient-opportunity grain (D-6/R-85): NEVER a lead row. */

/** FOLLOWUP-OWNERSHIP-ROUTING-CONFIG-V1: the caller-specific ownership
 *  truth the server enforced for one case -- WHO owns it now (a config
 *  value, e.g. the front desk before the boundary / the COC after) and
 *  whether THIS caller may record a contact on it. Display + deny input
 *  only; the server write fence is the boundary. */
export interface FollowupOwnershipRowView {
  readonly phase: 'before_boundary' | 'after_boundary';
  readonly ownerRole: string;
  readonly mayAct: boolean;
}

/** The followup_ownership routing config the response was composed under
 *  (the wire states the rule it applied -- the csdHandoffThresholdDays
 *  posture). */
export interface FollowupOwnershipConfigView {
  readonly ownerBeforeBoundary: string;
  readonly ownerAfterBoundary: string;
  readonly boundaryDays: number;
  readonly clockStartsAt: string;
  readonly windowReadRoles: readonly string[];
  /** FIRST-FOLLOWUP-SLA-KEY-V1: whole days after which a case with no
   *  recorded contact is overdue. REQUIRED inside this block -- the server
   *  fills it on every response (toFollowupOwnershipConfigWire), so if the
   *  block is present the number is present. The block itself stays
   *  optional for an older payload; this surface NEVER declares a fallback
   *  number of its own, which is the duplication this key removed. */
  readonly firstFollowupSlaDays: number;
}

export interface PatientFollowupRowView {
  readonly patientId: string;
  readonly umr: string;
  readonly patientName: string;
  readonly clinicId: number;
  readonly clinicLabel: string | null;
  readonly treatingDoctorName: string | null;
  readonly chiefComplaintCategory: string | null;
  readonly diagnosisCategory: string | null;
  readonly declinedItemCount: number;
  readonly openItems: readonly PatientFollowupPlanItemView[];
  readonly oldestOpenItemCreatedAt: string;
  readonly ageDays: number;
  readonly csdHandoffState: 'coc_follow_up' | 'csd_eligible';
  /** Optional so an older payload omits it; consumers treat absence as
   *  actionable (the pre-ownership behavior). */
  readonly followupOwnership?: FollowupOwnershipRowView;
  /** R-COC-ADVISED-SCOPE-01 (COC-ADVISED-VISIBILITY-V1): this patient's
   *  ADVISED (not-yet-decided) treatment items -- READ-ONLY additive
   *  context, never part of `openItems` and never a disposition target.
   *  Mirrors the backend PatientFollowupOwnedRow.advisedItems. Optional so
   *  an older payload omits it; consumers read it as []. */
  readonly advisedItems?: readonly PatientFollowupPlanItemView[];
}

/** One patient's canonical commercial conversion state on the COC follow-up
 *  wire (COC-RENDER-CLOSURE-V1, activity 20) -- mirrors the backend
 *  PatientConversionStateView (coc-followup-attempt-write-model.ts). Reuses
 *  the EXACT patient-360 conversion vocabulary (CommercialConversionStateWire)
 *  -- never a second conversion vocabulary. RENDER ONLY: no request on this
 *  wire ever writes a conversion state. */
export interface PatientFollowupConversionStateView {
  readonly patientId: string;
  readonly state: CommercialConversionStateWire;
}

export interface PatientFollowupQueueView {
  readonly clinicIds: readonly number[];
  readonly rows: readonly PatientFollowupRowView[];
  readonly csdHandoffThresholdDays: number;
  /** Optional so an older payload omits it (absence = the pre-ownership
   *  single-audience behavior). */
  readonly followupOwnership?: FollowupOwnershipConfigView;
  readonly generatedAtIso: string;
  readonly capped: boolean;
  /** COC-FOLLOWUP-CORE-JOURNEY-V1 (V2-P8): the current follow-up state of the
   *  OPEN treatment items on the wire, one entry per item that has a recorded
   *  disposition (RULING 2). An item with no recorded disposition is simply
   *  absent -- and the whole array is empty before the founder applies migration
   *  20260820_1 (honest, never a fake). Optional so an older payload omits it;
   *  consumers read it as []. */
  readonly itemFollowupStates?: readonly CocItemFollowupState[];
  /** COC-RENDER-CLOSURE-V1 (activity 20): the canonical payment-derived
   *  conversion state per patient on the wire -- BEST-EFFORT (a patient
   *  absent here means the lane was unreadable, never that they have not
   *  converted). Optional so an older payload omits it; consumers read it
   *  as []. */
  readonly conversionStates?: readonly PatientFollowupConversionStateView[];
}

/** Backend GET /api/coc/patient-followups -- the patient-side
 *  treatment-opportunity follow-up queue (pending/declined plan items with
 *  no later-accepted item, oldest first; config-backed CSD-handoff aging).
 *  VIEW ONLY: no disposition/reason-code writer rides this wire. */
export async function fetchCocPatientFollowups(
  clinicId?: number | 'all',
): Promise<PatientFollowupQueueView> {
  const q =
    clinicId !== undefined && clinicId !== 'all' ? `?clinicId=${clinicId}` : '';
  return portalApiGet<PatientFollowupQueueView>(`/coc/patient-followups${q}`);
}

/** COC-REPORTING-GRAINS-V1 (activity 21) -- mirrors the backend
 *  ClinicFollowupReportRow (lib/domain/patient/followup-report.ts).
 *  Plain counts + a money sum; never tinted (neutral numbers, ranked by
 *  the backend -- worst clinic first). */
export interface CocFollowupClinicReportRowView {
  readonly clinicId: number;
  readonly clinicLabel: string;
  readonly openCases: number;
  readonly openItems: number;
  readonly overdueCases: number;
  readonly estimatedValue: number;
}

/** Mirrors the backend TreatmentFollowupReportRow. `procedureName` is the
 *  RAW recorded name -- no mapping, no normalization (see the page's own
 *  module header). */
export interface CocFollowupTreatmentReportRowView {
  readonly procedureName: string;
  readonly openItems: number;
  readonly estimatedValue: number;
  readonly advisedItems: number;
}

export interface CocFollowupReportTotalsView {
  readonly openCases: number;
  readonly openItems: number;
  readonly overdueCases: number;
  readonly estimatedValue: number;
  readonly advisedItems: number;
}

/** Backend GET /api/coc/patient-followups/report -- the clinic-wise +
 *  treatment-wise COC follow-up report. Aggregated SERVER-SIDE, before
 *  the register's own presented-list cap applies, so these totals are
 *  always complete (never a partial number silently passed off as a
 *  total). VIEW ONLY. */
export interface CocFollowupReportView {
  readonly generatedAtIso: string;
  readonly byClinic: readonly CocFollowupClinicReportRowView[];
  readonly byTreatment: readonly CocFollowupTreatmentReportRowView[];
  readonly totals: CocFollowupReportTotalsView;
}

export async function fetchCocFollowupReport(): Promise<CocFollowupReportView> {
  return portalApiGet<CocFollowupReportView>('/coc/patient-followups/report');
}

/** One historical COC follow-up EVENT (PATIENT-COC-HISTORY-V1, SEPT15-
 *  REACHABILITY-PASS-V1 activity 19) -- mirrors the backend
 *  CocFollowupHistoryEvent (coc-followup-attempt-write-model.ts). ids +
 *  controlled tokens + a procedure name only; PHI-free. `disposition: null`
 *  means the item was discussed in this contact and left OPEN (RULING 4). */
export interface CocFollowupHistoryEventView {
  readonly id: number;
  readonly attemptId: number;
  readonly treatmentPlanItemId: number;
  readonly procedureName: string | null;
  readonly disposition: CocFollowupDisposition | null;
  readonly reason: CocFollowupReason | null;
  readonly nextFollowupDueDate?: string | null;
  readonly actorLabel: string | null;
  readonly actorRole: string;
  readonly clinicId: number;
  readonly createdAtIso: string;
}

export interface PatientCocFollowupHistoryView {
  readonly patientId: string;
  /** every lawful event for this patient, NEWEST-FIRST -- never collapsed to
   *  one row per item (the activity-19 fix). */
  readonly events: readonly CocFollowupHistoryEventView[];
  readonly generatedAtIso: string;
}

/** Backend GET /api/coc/patient-followups/:patientId/history -- the
 *  patient's FULL ordered COC follow-up history (every attempt+disposition,
 *  open or since closed/converted, newest-first). ADDITIVE alongside
 *  fetchCocPatientFollowups above; VIEW ONLY. */
export async function fetchCocPatientFollowupHistory(
  patientId: string,
): Promise<PatientCocFollowupHistoryView> {
  return portalApiGet<PatientCocFollowupHistoryView>(
    `/coc/patient-followups/${patientId}/history`,
  );
}

/** One item's disposition in a record-contact request (RULING 2/3/6):
 *  `disposition` omitted/null = discussed but left OPEN; `reason` (COST/OTHER)
 *  is allowed ONLY when disposition='declined'; `nextFollowupDueDate` (a bare
 *  YYYY-MM-DD calendar day -- the COC's own hand-picked per-item date, tenant
 *  grain ruling 2026-08-21 item 8) is allowed ONLY when the item is left OPEN
 *  (no disposition). */
export interface RecordCocFollowupItemArg {
  readonly treatmentPlanItemId: number;
  readonly disposition?: CocFollowupDisposition | null;
  readonly reason?: CocFollowupReason | null;
  readonly nextFollowupDueDate?: string | null;
}

/**
 * Backend POST /api/coc/patient-followups/attempts (COC-FOLLOWUP-CORE-JOURNEY-V1,
 * V2-P8 write half) -- record ONE real COC follow-up CONTACT covering one or
 * more of the patient's EXACT open treatment items (RULING 1/6). Body is
 * PHI-free: the patient id, the selected items each with an optional per-item
 * terminal disposition (declined/unreachable/cold) + a COST/OTHER reason only
 * under a decline, and an optional client-minted whole-contact idempotency key.
 * The target clinic, the per-item opportunity-in-scope check, and the durable
 * named actor (staff_users.id) are resolved SERVER-SIDE -- never from the
 * browser. A correction is simply a new contact re-recording the item; the
 * latest lawful disposition wins (RULING 2).
 */
export async function recordCocFollowupContact(args: {
  patientId: string;
  items: readonly RecordCocFollowupItemArg[];
  requestId?: string;
}): Promise<CocFollowupAttemptView> {
  return portalApiPost<CocFollowupAttemptView>('/coc/patient-followups/attempts', {
    patientId: args.patientId,
    items: args.items.map((it) => ({
      treatmentPlanItemId: it.treatmentPlanItemId,
      ...(it.disposition !== undefined && it.disposition !== null
        ? { disposition: it.disposition }
        : {}),
      ...(it.reason !== undefined && it.reason !== null
        ? { reason: it.reason }
        : {}),
      ...(it.nextFollowupDueDate !== undefined && it.nextFollowupDueDate !== null
        ? { nextFollowupDueDate: it.nextFollowupDueDate }
        : {}),
    })),
    ...(args.requestId !== undefined ? { requestId: args.requestId } : {}),
  });
}

export interface StationOperatorRowView {
  readonly staffUserId: number;
  readonly label: string;
  readonly assignedOpen: number;
  readonly overdueAssigned: number;
  readonly callbacksDue: number;
  readonly slaBreached: number;
  readonly outcomesRecordedToday: number;
  readonly convertedInWindow: number;
}

export interface CocStationManagerViewData {
  readonly role: string;
  readonly generatedAtIso: string;
  readonly view: {
    readonly operators: readonly StationOperatorRowView[];
    readonly pool: {
      readonly unclaimed: number;
      readonly unclaimedPastEscalation: number;
    };
    /**
     * SLA-ONE-DERIVATION-AND-STATION-STRIP-V1 (Refs #315): per-class SLA
     * breach counts over the SAME scoped station rows this view aggregates
     * (the ONE V4-P0 classifier's output; IntakeQueueHealth.slaBreachCounts
     * semantics). ADDITIVE: an older/partial payload omits it -- normalized
     * to all-five-zero at the fetch boundary (LES-057). Tokens + counts only.
     */
    readonly slaBreachCounts: Readonly<Record<string, number>>;
    readonly needsClinicAssignment: number;
    readonly conversionWindow: { readonly from: string; readonly toExclusive: string };
    readonly escalationMinutes: number;
    readonly capped: boolean;
  };
}

/**
 * SLA-ONE-DERIVATION-AND-STATION-STRIP-V1 (Refs #315): backward-compatibility
 * normalization at THE ONE wire-entry boundary for the station manager view
 * (the LES-057 posture, mirroring normalizeCallcenterQueue). The ADDITIVE
 * `view.slaBreachCounts` may be absent on an older/partial payload (a backend
 * mid-rollout, a seeded fixture predating the field): fill the truthful
 * absent-default (all five classes at 0 -> no strip) ONCE so every consumer
 * sees the invariant shape and none crashes or blocks the panel.
 */
export function normalizeCocStationManagerView(
  data: CocStationManagerViewData,
): CocStationManagerViewData {
  const counts = (data.view as Partial<CocStationManagerViewData['view']>)
    .slaBreachCounts;
  if (counts !== undefined) return data;
  return { ...data, view: { ...data.view, slaBreachCounts: zeroSlaBreachCounts() } };
}

/** Backend GET /api/coc/station/manager -- workload + accountability. The
 *  response passes through normalizeCocStationManagerView so an older/partial
 *  shape (missing the additive SLA counts) can never crash the panel. */
export async function fetchCocStationManagerView(): Promise<CocStationManagerViewData> {
  const data = await portalApiGet<CocStationManagerViewData>('/coc/station/manager');
  return normalizeCocStationManagerView(data);
}

export interface RecordedOutcomeView {
  readonly ok: true;
  readonly event: {
    readonly id: number;
    readonly leadId: number;
    readonly eventType: string;
    readonly occurredAt: string;
    readonly summary: string;
    readonly outcomeId: string | null;
    readonly callbackAt: string | null;
  };
}

/**
 * Backend POST /api/coc/lead-events -- record ONE contact outcome through the
 * EXISTING lead-events writer (COC-STATION-V1 disposition extras). The
 * eventType is the outcome's configured token (the caller passes the
 * vocabulary entry's own eventType; the server re-derives and rejects a
 * mismatch). callbackAtIso is UTC ISO (converted from the IST wall clock by
 * the ONE converter, lib/datetime-ist).
 */
export async function recordContactOutcome(args: {
  leadId: number;
  eventType: string;
  outcomeId: string;
  channel?: string;
  callbackAtIso?: string;
}): Promise<RecordedOutcomeView> {
  return portalApiPost<RecordedOutcomeView>('/coc/lead-events', {
    leadId: args.leadId,
    eventType: args.eventType,
    outcomeId: args.outcomeId,
    ...(args.channel !== undefined ? { channel: args.channel } : {}),
    ...(args.callbackAtIso !== undefined ? { callbackAt: args.callbackAtIso } : {}),
  });
}

export interface CocBillingRowUi {
  clinicId: number;
  clinicName: string;
  submitted: boolean;
  submittedAt: string | null;
  totalRevenue: number;
  patientCount: number;
  avgTicket: number;
  targetTicket: number;
  belowTarget: boolean;
}

export async function fetchCocBilling(): Promise<CocBillingRowUi[]> {
  const rows = await portalApiGet<Record<string, unknown>[]>('/coc/billing');
  return (rows ?? []).map((r) => ({
    clinicId: Number(r.clinicId ?? 0),
    clinicName: String(r.clinicName ?? ''),
    submitted: Boolean(r.submitted),
    submittedAt:
      r.submittedAt !== undefined && r.submittedAt !== null
        ? String(r.submittedAt)
        : null,
    totalRevenue: Number(r.totalRevenue ?? 0),
    patientCount: Number(r.patientCount ?? 0),
    avgTicket: Number(r.avgTicket ?? 0),
    targetTicket: Number(r.targetTicket ?? 15000),
    belowTarget: Boolean(r.belowTarget),
  }));
}

/** Backend GET /api/agm/dashboard */
export interface AgmDashboardApiResponse {
  zone: Record<string, unknown>;
  rankings: Record<string, unknown>[];
}

export async function fetchAgmDashboard(): Promise<{
  zone: ZoneSummary;
  rankings: ClinicRankRow[];
}> {
  const raw = await portalApiGet<AgmDashboardApiResponse>('/agm/dashboard');
  const z = raw.zone as unknown as ZoneSummary;
  const rankings = (raw.rankings ?? []).map((row) => ({
    rank: Number(row.rank ?? 0),
    clinicId: String(row.clinicId ?? ''),
    clinicName: String(row.clinicName ?? ''),
    revenue: Number(row.revenue ?? 0),
    patients: Number(row.patients ?? 0),
    avgTicket: Number(row.avgTicket ?? 0),
    noShowRate: Number(row.noShowRate ?? 0),
    trend: Number(row.trend ?? 0),
    hasAlerts: Boolean(row.hasAlerts),
  }));
  return { zone: z, rankings };
}

/** Backend GET /api/doctor/dashboard */
export interface DoctorDashboardApiResponse {
  view: Record<string, unknown>;
}

/** B2-v2 (DOCTOR-OS-BUILD-PHASE-1-V1): the doctor dashboard transport carries
 *  DAY FACTS only -- no incentive, target, or breakdown field exists on this
 *  clinical wire (NEVER-SHOW row 6; server-side structural absence pinned). */
export async function fetchDoctorDashboard(): Promise<{
  view: DoctorDayView;
}> {
  const raw = await portalApiGet<DoctorDashboardApiResponse>('/doctor/dashboard');
  const v = raw.view;
  const view: DoctorDayView = {
    doctorName: String(v.doctorName ?? 'Doctor'),
    clinicName: String(v.clinicName ?? ''),
    date: String(v.date ?? ''),
    shiftConfirmed: Boolean(v.shiftConfirmed),
    confirmedAt:
      v.confirmedAt !== undefined && v.confirmedAt !== null
        ? String(v.confirmedAt)
        : undefined,
    patientsBooked: Number(v.patientsBooked ?? 0),
    patientsSeen: Number(v.patientsSeen ?? 0),
    slotsRemaining: Number(v.slotsRemaining ?? 0),
  };
  return { view };
}

/**
 * Backend GET /api/owner/contact-timeline?phone=<raw>
 * Owner-scoped contact timeline for a given phone number. Returns
 * matched contacts plus a unified time-sorted event timeline across
 * contacts, walkins, messages, and attribution events.
 */
export async function fetchOwnerContactTimeline(
  phone: string,
): Promise<ContactTimelineResult> {
  const q = `?phone=${encodeURIComponent(phone)}`;
  const raw = await portalApiGet<Record<string, unknown>>(
    `/owner/contact-timeline${q}`,
  );

  const matchedContacts = ((raw.matchedContacts as unknown[]) ?? []).map(
    (r) => {
      const row = r as Record<string, unknown>;
      return {
        id: String(row.id ?? ''),
        // v1 spine: contacts.clinic_id is nullable. Coerce explicit
        // null / undefined / empty to null so the consumer can
        // render "clinic not yet assigned" instead of an empty
        // string masquerading as a clinic id.
        clinicId:
          row.clinicId == null || row.clinicId === ''
            ? null
            : String(row.clinicId),
        clinicName: row.clinicName == null ? null : String(row.clinicName),
        patientName: row.patientName == null ? null : String(row.patientName),
        age: row.age == null ? null : Number(row.age),
        totalVisits: Number(row.totalVisits ?? 0),
        convertedToPatient: Boolean(row.convertedToPatient),
      };
    },
  );

  const events = ((raw.events as unknown[]) ?? []).map((r) => {
    const row = r as Record<string, unknown>;
    return {
      type: row.type as ContactTimelineEventType,
      at: String(row.at ?? ''),
      clinicId: row.clinicId == null ? null : String(row.clinicId),
      clinicName: row.clinicName == null ? null : String(row.clinicName),
      summary: String(row.summary ?? ''),
      payload: (row.payload as Record<string, unknown>) ?? {},
    };
  });

  const s = (raw.summary as Record<string, unknown>) ?? {};
  const summary: ContactTimelineSummary = {
    contactCount: Number(s.contactCount ?? 0),
    walkinCount: Number(s.walkinCount ?? 0),
    messageCount: Number(s.messageCount ?? 0),
    attributionCount: Number(s.attributionCount ?? 0),
    totalEvents: Number(s.totalEvents ?? 0),
  };

  return { matchedContacts, events, summary };
}

/**
 * Backend GET /api/owner/leakage-snapshot — operational clinic+date
 * snapshot (JUNE30-LEAKAGE-DEMO-01). `mode` defaults backend-side to
 * 'real'; the June 30 demo panel explicitly requests 'demo'. `portalApiGet`
 * unwraps the envelope so the resolved value is the snapshot payload.
 */
export async function fetchOwnerLeakageSnapshot(opts: {
  days?: number;
  mode?: OwnerLeakageDataMode;
} = {}): Promise<OwnerLeakageSnapshot> {
  const params = new URLSearchParams();
  if (opts.days !== undefined) params.set('days', String(opts.days));
  if (opts.mode !== undefined) params.set('mode', opts.mode);
  const q = params.toString();
  return portalApiGet<OwnerLeakageSnapshot>(
    `/owner/leakage-snapshot${q ? `?${q}` : ''}`,
  );
}

/**
 * Backend GET /api/owner/campaign-lead-attribution-health (#59) — forward
 * attribution FIRST leg (campaign / source → lead). Owner-scoped, aggregate
 * counts only (no PHI). `portalApiGet` unwraps the `{ success, data }`
 * envelope, so the resolved value is the health payload directly. v0.1 surface
 * takes the owner selector-bar window + clinic when the caller supplies them
 * (FIX 1); with none supplied the endpoint defaults stand (from `2026-05-20` to
 * today, include_untagged).
 */
export async function fetchOwnerCampaignLeadAttributionHealth(params?: {
  from?: string;
  to?: string;
  clinicId?: string | null;
}): Promise<CampaignLeadAttributionHealth> {
  return portalApiGet<CampaignLeadAttributionHealth>(
    `/owner/campaign-lead-attribution-health${ownerFilterQuery(params)}`,
  );
}

/**
 * Backend GET /api/owner/lead-walkin-bridge-health (#60) — forward attribution
 * MIDDLE leg (lead → walk-in) via phone-fingerprint match. Owner-scoped,
 * aggregate counts only (no PHI, no revenue attribution). Two SEPARATE rates.
 * Takes the owner selector-bar window + clinic when the caller supplies them
 * (FIX 1); with none supplied the endpoint defaults stand (30-day window,
 * window breakdown on).
 */
export async function fetchOwnerLeadWalkinBridgeHealth(params?: {
  from?: string;
  to?: string;
  clinicId?: string | null;
}): Promise<LeadWalkinBridgeHealth> {
  return portalApiGet<LeadWalkinBridgeHealth>(
    `/owner/lead-walkin-bridge-health${ownerFilterQuery(params)}`,
  );
}


/**
 * Backend GET /api/owner/lead-360 (LEAD-360-READ-ENDPOINT-01) — owner-scoped,
 * read-only, PHI-safe `Lead360Input` records for ONE clinic. `clinicId` is
 * required; `limit` is optional (backend default 50, hard cap 200). The org is
 * resolved from the server-authenticated scope (never sent from the client).
 * `portalApiGet` unwraps the `{ success, data }` envelope, so the resolved value
 * is the `Lead360Input[]` array directly.
 *
 * SAFE ENDPOINT ONLY: this calls the PHI-safe lead-360 read path. It does NOT
 * call the contact timeline, the contact vault, or any PHI source, and never
 * enriches records with patient data.
 */
export async function fetchOwnerLead360(
  clinicId: string | number,
  limit?: number,
): Promise<Lead360Input[]> {
  const params: string[] = [`clinicId=${encodeURIComponent(String(clinicId))}`];
  if (limit !== undefined) {
    params.push(`limit=${encodeURIComponent(String(limit))}`);
  }
  const rows = await portalApiGet<Lead360Input[]>(
    `/owner/lead-360?${params.join('&')}`,
  );
  return rows ?? [];
}

/**
 * Backend GET /api/leads/workspace (LEADS-WORKSPACE-ROLE-SCOPED-01) —
 * role-scoped Leads Workspace list for owner / COC / receptionist.
 * Ships default-on for its audience (the internal-surface env flag was retired).
 * `clinicId` narrows WITHIN the caller's server-verified scope; null /
 * omitted lists exactly that verified scope ("all assigned clinics").
 * The server validates every clinicId with the existing audited scope
 * gate — this client never widens anything. `records` is the same
 * PHI-safe `Lead360Input[]` contract the owner lead-360 read serves.
 */
export interface LeadsWorkspaceResponse {
  role: DashboardMe['role'];
  clinicId: number | null;
  clinics: LeadsWorkspaceClinicOption[];
  records: Lead360Input[];
}

export async function fetchLeadsWorkspace(
  clinicId: string | number | null,
  limit?: number,
): Promise<LeadsWorkspaceResponse> {
  const params: string[] = [];
  if (clinicId !== null && String(clinicId).trim() !== '') {
    params.push(`clinicId=${encodeURIComponent(String(clinicId))}`);
  }
  if (limit !== undefined) {
    params.push(`limit=${encodeURIComponent(String(limit))}`);
  }
  const qs = params.length > 0 ? `?${params.join('&')}` : '';
  return portalApiGet<LeadsWorkspaceResponse>(`/leads/workspace${qs}`);
}

/**
 * CRM-LIST-VIEWS-V1 (V1-P7): the SAME workspace route WITH list params --
 * server-side filters / sort / count over the caller's verified scope
 * (fences before filters). The response adds the `list` meta block (count
 * truth, ignored-token disclosure, role-shaped assignee options). Every
 * param value is a controlled token or bounded integer from the list
 * contract -- never free text (the C-9 PHI ruling: search stays off the
 * URL and off this API call).
 */
export interface LeadsListAssigneeOption {
  value: string;
  label: string;
}

export interface LeadsListMeta {
  contract: 'list_v1';
  view: string | null;
  sort: string;
  appliedFilters: Record<string, string>;
  ignoredFilters: string[];
  filteredTotal: number | null;
  filteredTotalIsPartial: boolean;
  visibleRows: number;
  pageLimit: number;
  scanLimit: number | null;
  scanCapped: boolean;
  slaStateUnavailable?: boolean;
  assigneeUnresolved?: boolean;
  assigneeOptions: LeadsListAssigneeOption[];
  sourceFilterLawful: boolean;
}

export interface LeadsListResponse extends LeadsWorkspaceResponse {
  list: LeadsListMeta;
}

export async function fetchLeadsList(
  clinicId: string | number | null,
  limit: number,
  listParams: Record<string, string>,
): Promise<LeadsListResponse> {
  const params: string[] = [];
  if (clinicId !== null && String(clinicId).trim() !== '') {
    params.push(`clinicId=${encodeURIComponent(String(clinicId))}`);
  }
  params.push(`limit=${encodeURIComponent(String(limit))}`);
  for (const [key, value] of Object.entries(listParams)) {
    params.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  }
  return portalApiGet<LeadsListResponse>(`/leads/workspace?${params.join('&')}`);
}

/**
 * Backend POST /api/leads/:leadId/contact-reveal (CONTACT-REVEAL-AUDIT-01)
 * — the audited tap-to-reveal. Sends the REQUIRED quick-pick `purpose`
 * (UI default 'follow_up_call' — "Follow-up call") and `field: 'phone'`.
 * Every call writes exactly one CONTACT_REVEAL audit row server-side; a
 * re-reveal after the 60s client rehide is therefore a new audit row.
 * `portalApiPost` unwraps the `{ success, data }` envelope: the resolved
 * value is either `{ outcome: 'revealed', value, rehideSeconds }` or the
 * DESIGNED `{ outcome: 'no_value', reason }` (never an error state).
 * Denials surface as a 401 PortalApiError. The revealed value is never
 * stored, logged, or cached here — it goes straight to component state.
 */
export async function revealLeadContact(
  safeLeadId: string,
  purpose: string,
  // CALL-CENTER-AGENT-SCOPE-AND-DISPOSITION-CORE-V1 (additive): the
  // REQUIRED justification note when the selected purpose is 'other' (or a
  // config purpose flagged requires_note). Omitted for every other
  // purpose, so existing callers stay byte-identical.
  purposeNote?: string,
): Promise<
  | { outcome: 'revealed'; field: string; value: string; rehideSeconds: number }
  | { outcome: 'no_value'; field: string; reason: string }
> {
  return portalApiPost(
    `/leads/${encodeURIComponent(safeLeadId)}/contact-reveal`,
    {
      field: 'phone',
      purpose,
      ...(purposeNote !== undefined ? { purposeNote } : {}),
    },
  );
}

/**
 * Backend POST /api/leads/:leadId/contact-workflow (LEAD-CONTACT-FLOW-V1)
 * -- the ONE NEXT-card contact-workflow server action: permission check +
 * the ONE CONTACT_REVEAL audit row (purpose stamped SERVER-side:
 * 'first_contact' on a lead with no prior attempt, else 'follow_up_call')
 * + the in-card reveal + the SERVER-issued grant (issuedAt/expiresAt from
 * the org-config horizon lead_reveal.grant_seconds, seeded 960) + the
 * advance payload (the org's disposition vocabulary). The client only
 * renders the countdown; re-masking is a display act (no second call, no
 * second audit row). Denials surface as a 401 PortalApiError; a dark
 * CONTACT_REVEAL_V1_ENABLED environment surfaces 503. The revealed value
 * is never stored, logged, or cached here -- it goes straight to
 * component state and is dropped on re-mask.
 */
export interface ContactWorkflowStartPayload {
  readonly channel: 'phone' | 'whatsapp';
  readonly purpose: string;
  readonly reveal:
    | { readonly outcome: 'revealed'; readonly field: 'phone'; readonly value: string }
    | { readonly outcome: 'no_value'; readonly field: 'phone'; readonly reason: string };
  readonly grant: {
    readonly grantSeconds: number;
    readonly issuedAtIso: string;
    readonly expiresAtIso: string;
  } | null;
  readonly dispositionOptions: LeadDispositionOptionsResponse['dispositionOptions'];
}

export async function startLeadContactWorkflow(
  safeLeadId: string,
  channel: 'phone' | 'whatsapp',
): Promise<ContactWorkflowStartPayload> {
  return portalApiPost<ContactWorkflowStartPayload>(
    `/leads/${encodeURIComponent(safeLeadId)}/contact-workflow`,
    { channel },
  );
}

/**
 * Backend GET /api/leads/disposition-options
 * (CALL-CENTER-AGENT-SCOPE-AND-DISPOSITION-CORE-V1) -- the org's
 * config-backed panel vocabularies (reveal purposes + the disposition
 * chain), fail-safe seeded server-side. Workspace-audience gated; the
 * reveal purposes carry NO default selection in the UI. Vocabulary only,
 * never an authorization grant (the reveal + disposition routes
 * re-validate every submission server-side).
 */
export interface LeadDispositionOptionsResponse {
  readonly revealPurposeOptions: ReadonlyArray<{
    readonly id: string;
    readonly label: string;
    readonly requiresNote: boolean;
  }>;
  readonly dispositionOptions: {
    readonly methods: ReadonlyArray<{
      readonly id: string;
      readonly label: string;
      readonly results: ReadonlyArray<{
        readonly id: string;
        readonly label: string;
        readonly connected: boolean;
        // LEAD-WORKFLOW-TRUTH-CORRECTIVE-V1 (M1-M3): the lawful next-action ids
        // for this cell (config-backed). Omitted = unconstrained. The server
        // is authoritative; the panel filters its picklist to this set.
        readonly allowedNextActionIds?: readonly string[];
      }>;
    }>;
    readonly connectResults: ReadonlyArray<{
      readonly id: string;
      readonly label: string;
      readonly interested: boolean;
      // LEAD-WORKFLOW-TRUTH-CORRECTIVE-V1 (M1-M3): governs the connected branch.
      readonly allowedNextActionIds?: readonly string[];
    }>;
    readonly interestedDetails: ReadonlyArray<{
      readonly id: string;
      readonly label: string;
    }>;
    readonly nextActions: ReadonlyArray<{
      readonly id: string;
      readonly label: string;
      readonly requiresCallbackAt: boolean;
      readonly requiresNote: boolean;
      readonly terminalStage: 'lost' | null;
    }>;
  };
}

export async function fetchLeadDispositionOptions(): Promise<LeadDispositionOptionsResponse> {
  return portalApiGet<LeadDispositionOptionsResponse>('/leads/disposition-options');
}

/**
 * Backend POST /api/leads/:leadId/disposition
 * (CALL-CENTER-AGENT-SCOPE-AND-DISPOSITION-CORE-V1) -- ONE coherent save
 * for the contact-attempt chain (method / result / connect / detail /
 * next action + callback / note). The server validates the whole chain
 * and the agent ownership fence; a terminal closure rides the existing
 * stage machinery. Controlled tokens only; the bounded note (audit-plane)
 * never reaches lead_events.
 */
export interface LeadDispositionSaveResponse {
  readonly ok: true;
  readonly event: {
    readonly id: number;
    readonly leadId: number;
    readonly eventType: string;
    readonly occurredAt: string;
    readonly method: string;
    readonly resultId: string;
    readonly connectId: string | null;
    readonly detailId: string | null;
    readonly nextActionId: string;
    readonly callbackAt: string | null;
    readonly duplicateOfRetry: boolean;
  };
  readonly closure?: {
    readonly attempted: true;
    readonly ok: boolean;
    readonly reason: string | null;
  };
}

export async function saveLeadDisposition(
  safeLeadId: string,
  body: {
    method: string;
    resultId: string;
    connectId?: string;
    detailId?: string;
    nextActionId: string;
    callbackAt?: string;
    note?: string;
    /**
     * RV (LEAD-WORKFLOW-TRUTH-CORRECTIVE-V1): the controlled purpose token of a
     * contact reveal this outcome CLOSES. When supplied the server records the
     * reveal->outcome linkage on audit_events (the dormant ADR-0013 Stance-5
     * primitive; first writer). Optional -- omitted when no reveal preceded
     * this outcome. Passing it from the record host is a fenced follow-on (see
     * the run record: coupling the decoupled reveal + disposition render slots
     * is a reveal-flow redesign, out of scope for this corrective).
     */
    revealPurpose?: string;
  },
): Promise<LeadDispositionSaveResponse> {
  return portalApiPost(
    `/leads/${encodeURIComponent(safeLeadId)}/disposition`,
    body,
  );
}

/**
 * Backend GET /api/owner/marketing-spend (OWNER-MARKETING-SPEND-READ-01) —
 * owner-scoped, read-only aggregate of REAL stored Google Ads spend
 * (marketing_daily_metrics + latest marketing_sync_runs; mock accounts excluded).
 * Conversions are platform-reported (NOT AdoraCare walk-ins); no revenue
 * attribution. `portalApiGet` unwraps the `{ success, data }` envelope. This is
 * NOT the mock marketing-demo path.
 */
export async function fetchOwnerMarketingSpend(params?: {
  from?: string;
  to?: string;
}): Promise<OwnerMarketingSpend> {
  return portalApiGet<OwnerMarketingSpend>(
    `/owner/marketing-spend${ownerFilterQuery(params)}`,
  );
}


/**
 * Backend GET /api/agm/call-action-summary — GM/AGM zone-scoped Call Action
 * Summary (DEMO fixture, `dataMode:'demo'`); the backend scopes to the AGM's
 * zone clinics. Same envelope/shape contract as the owner fetcher above.
 */
export async function fetchAgmCallActionSummary(): Promise<CallActionSummaryInput> {
  return portalApiGet<CallActionSummaryInput>('/agm/call-action-summary');
}

/**
 * Backend GET /api/coc/call-action-summary — COC clinic-scoped Call Action
 * Summary (DEMO fixture, `dataMode:'demo'`); the backend scopes to the COC's
 * assigned clinics. Same envelope/shape contract as the owner fetcher above.
 */
export async function fetchCocCallActionSummary(): Promise<CallActionSummaryInput> {
  return portalApiGet<CallActionSummaryInput>('/coc/call-action-summary');
}

// ─── Patient Master (ADORACARE-PATIENT-MASTER-B2) ──────────────────────
// All five endpoints are dark behind PATIENT_MASTER_V1_ENABLED — the
// backend returns 503 patient_master_disabled before auth when off, so
// these fetchers are only reachable from routes the RequirePatientMaster
// guards already gate on the server-computed /me signals.

/**
 * Backend GET /api/registration-config — the arrival wizard's resolved
 * org config (INTAKE-V2-BUILD-V1; brief 13.1's required-field resolver).
 * Read-only, zero PHI: required-field tokens, gender requiredness, reason
 * chips, consent paths. Callers degrade to FALLBACK_REGISTRATION_CONFIG on
 * failure — a config fault can never block a registration (D3).
 */
export async function fetchRegistrationConfig(): Promise<
  import('@/dashboard/patients/register-wizard-model').RegistrationConfigView
> {
  return portalApiGet('/registration-config');
}

/**
 * Backend GET /api/patient-master/search?q=&mode= — phone / UMR / name
 * patient search (the EHNOTE section-13 trio). Zero, one, or MANY
 * candidates per phone is a normal outcome (family-shared numbers).
 */
export async function fetchPatientMasterSearch(
  q: string,
  mode?: 'phone' | 'umr' | 'name',
  pageScope?: {clinicId:number; page:number},
): Promise<PatientSearchResponse> {
  const params = [`q=${encodeURIComponent(q)}`];
  if (mode) params.push(`mode=${encodeURIComponent(mode)}`);
  if (pageScope) params.push(`clinicId=${pageScope.clinicId}`, `page=${pageScope.page}`);
  return portalApiGet<PatientSearchResponse>(
    `/patient-master/search?${params.join('&')}`,
  );
}

/** Revalidates the selected result and records its explicit administrative open. */
export async function openPatientSearchLead(q: string, leadId: number, clinicId: number, page = 0): Promise<import('@/dashboard/patients/patient-master-view').EnquiryGlanceCard> {
  return portalApiGet(`/patient-master/search/open-lead?q=${encodeURIComponent(q)}&leadId=${leadId}&clinicId=${clinicId}&page=${page}`);
}

/** R-CD-B1: the operator new-enquiry form vocabulary (config-backed
 *  sources, buckets, the caller's lawful clinics). */
export interface OperatorEnquiryConfigWire {
  sources: string[];
  treatmentTypes: string[];
  clinics: { clinicId: number; name: string }[];
}

export async function fetchOperatorEnquiryConfig(): Promise<OperatorEnquiryConfigWire> {
  return portalApiGet<OperatorEnquiryConfigWire>('/leads/enquiry-config');
}

export interface OperatorEnquiryPayload {
  phone: string;
  name: string;
  treatmentType: string;
  source: string;
  clinicId: number;
  requestId?: string;
}

export interface OperatorEnquiryCreatedWire {
  leadId: number;
  safeLeadId: string;
  attributionWarning?: string;
  contactWarning?: string;
}

/** R-CD-B1: births a LEAD through THE ONE capture engine -- rendered only
 *  as the NOT-FOUND outcome of the phone search (R-CD-R1). */
export async function createOperatorEnquiry(
  payload: OperatorEnquiryPayload,
): Promise<OperatorEnquiryCreatedWire> {
  return portalApiPost<OperatorEnquiryCreatedWire>('/leads/enquiries', payload);
}

/**
 * Backend POST /api/patient-master/patients — the 30-second front-desk
 * create. UMR is issued server-side (DB trigger). A minor REQUIRES
 * guardian + consent in the payload; the server rejects otherwise. A
 * deterministic ladder match returns `outcome: 'duplicate_match'`
 * (designed outcome, not an error).
 */
export async function createPatientMasterPatient(
  payload: CreatePatientPayload,
  expectedBearer?: string,
): Promise<CreatePatientResponse> {
  return portalApiPost<CreatePatientResponse>(
    '/patient-master/patients',
    payload,
    expectedBearer,
  );
}

/**
 * RECEPTION-OS-BUILD-V1 (F-14): the desk's walk-in arrival record with its
 * source -- POST /api/walkins/record (session-only; reuses THE ONE walk-in
 * writer server-side). Best-effort from the register flow: a failure never
 * blocks the registered patient; the caller surfaces the honest note.
 */
export async function recordWalkinArrival(payload: {
  readonly savedLeadId?: number;
  readonly patientId?: string;
  readonly reasonForVisit?: string;
  readonly requestId?: string;
  readonly sourceDetail?: string;
  readonly clinicId: number;
  readonly patientName: string;
  readonly phoneRaw: string;
  readonly source: string;
  readonly patientIsNew: boolean;
}, expectedBearer?: string): Promise<{ok: true; walkin_id: number}> {
  return portalApiPost<{ok: true; walkin_id: number}>('/walkins/record', payload, expectedBearer);
}

/**
 * Backend GET /api/patient-master/patients/:patientId — minimal patient
 * record view (demographics, identifiers, Tier-0 contact points,
 * guardians, consent records).
 */
export async function fetchPatientMasterRecord(
  patientId: string,
  expectedBearer?: string,
): Promise<PatientRecordResponse> {
  return portalApiGet<PatientRecordResponse>(
    `/patient-master/patients/${encodeURIComponent(patientId)}`,
    expectedBearer,
  );
}

/**
 * Backend POST /api/patient-master/patients/:patientId/contact-reveal
 * (PATIENT-CONTACT-PRIVACY-AND-AUDITED-REVEAL-V1, gap.f-25) — the audited
 * tap-to-reveal of ONE masked Patient Detail contact point. Sends the
 * REQUIRED `contactPointId` (the surrogate id the masked record read
 * carries) and a controlled quick-pick `purpose`. Every call writes exactly
 * one CONTACT_REVEAL audit row server-side (the SAME audit event and purpose
 * vocabulary as the lead reveal); a re-reveal after the 60s client rehide is
 * a new audit row. `portalApiPost` unwraps the `{ success, data }` envelope:
 * the resolved value is `{ outcome: 'revealed', value, rehideSeconds }` or
 * the DESIGNED `{ outcome: 'no_value', reason }`. Denials surface as a 401
 * PortalApiError (the value stays masked). The revealed value is never
 * stored, logged, or cached here — it goes straight to component state.
 */
export async function revealPatientContact(
  patientId: string,
  contactPointId: number,
  purpose: string,
  expectedBearer?: string,
): Promise<
  | { outcome: 'revealed'; field: string; value: string; rehideSeconds: number }
  | { outcome: 'no_value'; field: string; reason: string }
> {
  return portalApiPost(
    `/patient-master/patients/${encodeURIComponent(patientId)}/contact-reveal`,
    { contactPointId, purpose },
    expectedBearer,
  );
}

/**
 * Backend GET /api/patient-master/patients/:patientId/timeline
 * (PATIENT-CRM-TIMELINE-01) — the read-only patient 360 journey assembled
 * from existing sources (linked lead events + appointment projection +
 * advisory crossmatch). Dark behind PATIENT_MASTER_V1_ENABLED (503 before
 * auth when off), so this fetcher is only reachable from the flag-gated,
 * RequirePatientMaster-guarded page. PHI-ZERO payload: opaque ids + UMR +
 * controlled labels + status tokens + timestamps + counts. DISPLAY-ONLY —
 * it never links, merges, reveals, or creates.
 */
export async function fetchPatientTimeline(
  patientId: string,
): Promise<PatientTimelineResponse> {
  return portalApiGet<PatientTimelineResponse>(
    `/patient-master/patients/${encodeURIComponent(patientId)}/timeline`,
  );
}

/**
 * Backend GET /api/patient-master/patients/:patientId/summary360
 * (PATIENT-360-AND-EHNOTE-COEXISTENCE-SPINE-V1, V3-P5) -- the role-safe
 * OPERATIONAL Patient 360 summary: TODAY first, one state-aware primary
 * action, the honest journey strip, visit / payment / follow-up evidence,
 * and the truthful EHNote coexistence state. READ-ONLY; assembled from
 * EXISTING owners; dark behind PATIENT_MASTER_V1_ENABLED (503 before auth
 * when off). PHI-SAFE: no phone, no contact value, no clinical narrative
 * crosses this wire -- contact stays on the masked record read + the
 * audited reveal.
 */
export async function fetchPatient360Summary(
  patientId: string,
  selectedAppointmentId?: number,
): Promise<Patient360SummaryResponse> {
  return portalApiGet<Patient360SummaryResponse>(
    `/patient-master/patients/${encodeURIComponent(patientId)}/summary360` +
      (selectedAppointmentId === undefined ? '' : `?selectedAppointmentId=${encodeURIComponent(String(selectedAppointmentId))}`),
  );
}

/**
 * Backend GET /api/patient-master/merge-queue — pending merge
 * candidates with side-by-side compare cards (coc+ only; 401 below
 * that).
 */
export async function fetchPatientMasterMergeQueue(): Promise<MergeQueueResponse> {
  return portalApiGet<MergeQueueResponse>('/patient-master/merge-queue');
}

/**
 * Backend POST /api/patient-master/merge-queue/:queueId/decision —
 * approve (with explicit survivor) or reject one pending candidate.
 * Every decision writes an audit_events row server-side.
 */
export async function postPatientMasterMergeDecision(
  queueId: number,
  payload: MergeDecisionPayload,
): Promise<MergeDecisionResponse> {
  return portalApiPost<MergeDecisionResponse>(
    `/patient-master/merge-queue/${encodeURIComponent(String(queueId))}/decision`,
    payload,
  );
}

/**
 * Backend GET /api/patient-master/lead-crossmatch?leadId=
 * (FRONT-DESK-POSSIBLE-MATCH-SURFACE-01) — the guarded, advisory
 * lead → patient crossmatch verdict for ONE lead in view. Dark behind
 * PATIENT_MASTER_V1_ENABLED (503 before auth when off), so this fetcher is
 * only reachable from the flag-gated notice. Org / role come from the
 * server scope; the response carries ONLY the opaque verdict + a non-PHI
 * count — no phone / name / dob / fingerprint and no patient id.
 * `portalApiGet` unwraps the { success, data } envelope.
 *
 * DISPLAY-ONLY / SAFE ENDPOINT: a read of the shipped advisory service —
 * it never links, reveals, merges, or creates.
 */
export async function fetchLeadPatientCrossmatch(
  safeLeadId: string,
): Promise<LeadPatientCrossmatchResult> {
  return portalApiGet<LeadPatientCrossmatchResult>(
    `/patient-master/lead-crossmatch?leadId=${encodeURIComponent(safeLeadId)}`,
  );
}

// ─── Work-routing QA visibility (WORK-ROUTING-QA-PAGE-BUILD-DOCS-RUNBOOK-01) ──
// All three endpoints are dark behind WORK_ROUTING_VISIBLE_QA_ENABLED — the
// backend returns 503 work_routing_qa_disabled BEFORE auth when off, so these
// fetchers are only reached from the owner-only QA page AFTER the
// `workRoutingQaEnabled` /me signal confirms the flag is on. Owner-scoped on
// the server; the organization is resolved from the authenticated scope and a
// client-supplied organizationId is ignored. PHI-free (ids / roles / status /
// template / timestamps only). `portalApiGet`/`portalApiPost` unwrap the
// `{ success, data }` envelope.

/** Backend GET /api/work-routing/qa/routing-config — org-scoped config rows. */
export async function fetchWorkRoutingQaRoutingConfig(): Promise<
  WorkRoutingConfigRow[]
> {
  const rows = await portalApiGet<WorkRoutingConfigRow[]>(
    '/work-routing/qa/routing-config',
  );
  return rows ?? [];
}

/** Backend GET /api/work-routing/qa/lead-actions — org-scoped action rows. */
export async function fetchWorkRoutingQaLeadActions(): Promise<
  WorkRoutingLeadActionRow[]
> {
  const rows = await portalApiGet<WorkRoutingLeadActionRow[]>(
    '/work-routing/qa/lead-actions',
  );
  return rows ?? [];
}

/**
 * Backend POST /api/work-routing/qa/route-lead — route one existing lead
 * through `new_lead_followup`. The body carries ONLY `leadId`; the server
 * derives organization + clinic from the scoped lead row (never client
 * input). Returns the existing OPEN action (`outcome: 'existing'`, no
 * insert) or a freshly created one (`outcome: 'created'`).
 */
export async function routeWorkRoutingQaLead(
  leadId: number,
): Promise<WorkRoutingRouteResult> {
  return portalApiPost<WorkRoutingRouteResult>('/work-routing/qa/route-lead', {
    leadId,
  });
}

/**
 * Backend GET /api/work-routing/settings - the routing rules the caller may
 * configure (org-default + in-scope clinic overrides), the clinic picker, and
 * the template/role catalogs. PHI-free; organization scope is server-derived.
 */
export async function getWorkRoutingSettings(): Promise<RoutingSettingsResponse> {
  return portalApiGet<RoutingSettingsResponse>('/work-routing/settings');
}

/**
 * Backend PUT /api/work-routing/settings - create/update ONE role-only routing
 * rule. The body carries ONLY { clinicId, workflowTemplateCode, targetRole };
 * the server derives organization_id from the scope and rejects an out-of-scope
 * clinic, an org-default write by a non-owner, and a doctor/patient target role.
 */
export async function updateWorkRoutingSetting(
  request: RoutingSettingUpdateRequest,
): Promise<RoutingSettingUpdateResult> {
  return portalApiPut<RoutingSettingUpdateResult>(
    '/work-routing/settings',
    request,
  );
}

/**
 * Backend POST /api/work-routing/settings/:id/active - deactivate or reactivate
 * ONE routing rule via the existing is_active column (WORK-ROUTING-ADMIN-
 * GOVERNANCE-V1; no delete, no schema). Body carries ONLY { isActive }. The
 * server re-reads the rule org-scoped, enforces the same row-level authority as
 * the writer, the last-rule guard, and the reactivate collision guard, and
 * writes an attributed audit row. On failure portalApiPost throws a
 * PortalApiError the UI maps to a friendly message.
 */
export async function setWorkRoutingRuleActive(
  ruleId: number,
  isActive: boolean,
): Promise<RoutingLifecycleResult> {
  return portalApiPost<RoutingLifecycleResult>(
    `/work-routing/settings/${ruleId}/active`,
    { isActive },
  );
}

/**
 * Backend GET /api/work-routing/followup-ownership - the org's follow-up
 * ownership routing values + controlled vocabularies
 * (FOLLOWUP-OWNERSHIP-ROUTING-CONFIG-V1). PHI-free; canConfigureRouting is
 * the server gate.
 */
export async function getFollowupOwnershipSettings(): Promise<FollowupOwnershipSettingsResponse> {
  return portalApiGet<FollowupOwnershipSettingsResponse>(
    '/work-routing/followup-ownership',
  );
}

/**
 * Backend PUT /api/work-routing/followup-ownership - save the five follow-up
 * ownership values. The server enforces the page's org-default write policy
 * (owner-only), validates every value against the controlled vocabularies,
 * writes through THE ONE org_workflow_settings writer, and records one
 * attributed before/after audit act.
 */
export async function updateFollowupOwnershipSettings(
  values: FollowupOwnershipValues,
): Promise<FollowupOwnershipSettingsResponse> {
  return portalApiPut<FollowupOwnershipSettingsResponse>(
    '/work-routing/followup-ownership',
    values,
  );
}

// ─── S2-P2C appointment workbench mutations ───────────────────────────────
//
// PHI-free bodies: book sends ONLY { leadId, appointmentTime }; reschedule
// sends ONLY { appointmentTime } (the appointment id is in the PATH); cancel /
// no-show send NO payload ({} — the id is in the PATH). Never a phone / name /
// clinical field. The backend forces status, enforces the role gate + scope +
// IDOR, and is the security boundary. On failure portalApiPost throws a
// PortalApiError carrying { status, code } the UI maps to a friendly message.

/** PHI-free echo of a created booked appointment (mirror of backend). */
export interface BookedAppointmentResult {
  readonly outcome: 'created' | 'replayed';
  readonly appointment: {
    readonly id: number;
    /** Null on the FD-20 patient-keyed leg (no lead journey). */
    readonly leadId: number | null;
    readonly clinicId: number;
    readonly appointmentTime: string;
    readonly status: string;
  };
  readonly appointmentBookedEmitted: boolean;
  readonly appointmentEventEmitted: boolean;
  readonly eventWarning?: string;
  /**
   * THE STAGE-TRUTH SEAM'S HONEST OUTCOME (AUG-12-WALK-BLOCKERS-V1, D1):
   * the backend has always returned this; the wire type omitted it, so a
   * stage advance that no-opped or failed was INDISTINGUISHABLE from a
   * clean booking on every screen -- the founder's frozen "New lead"
   * thread. `ok:false` with an unlawful reason now renders a warning on
   * the workbench. workItemClosed reports the unclaimed-queue exit.
   */
  readonly stageAdvance?: {
    readonly attempted: boolean;
    readonly ok: boolean;
    readonly fromStage: string | null;
    readonly toStage: string;
    readonly reason: string | null;
    readonly workItemClosed: boolean;
  };
}

/** PHI-free echo of a mutated appointment (mirror of backend AppointmentActionResult). */
export interface AppointmentActionResultView {
  readonly outcome:
    | 'rescheduled'
    | 'cancelled'
    | 'no_show'
    | 'arrived'
    | 'checked_in'
    | 'fulfilled'
    | 'not_converted'
    // BACK-HALF-CLINICAL-WORKBENCH-UI-01: the categorical consultation outcomes
    // (append-only events over a fulfilled appointment; NO appointments.status
    // change). Additive to the union - existing consumers switch on a subset.
    | 'treatment_advised'
    | 'treatment_accepted'
    | 'treatment_declined'
    | 'lead_converted';
  readonly appointment: {
    readonly id: number;
    readonly leadId: number | null;
    readonly clinicId: number;
    readonly appointmentTime: string;
    readonly status: string;
  };
  readonly appointmentEventEmitted: boolean;
  readonly eventWarning?: string;
}

// --- AGENT-TO-PATIENT-SPINE-V1 (V3-P4): convert a lead to the patient golden
//     record. LINK-FIRST: dedupe by the lead's phone fingerprint + stamp
//     leads.patient_id via the ONE conversion hook (backend). PHI-bounded:
//     candidate cards carry umr / name / city only (no clinical field).

/** One patient the lead's phone matches (a family may share one number). */
/** One patient this lead's phone could belong to. Enough to tell family
 *  members apart -- name, UMR, gender, age, city, last visit. Never a phone. */
export interface PatientConvertCandidateView {
  readonly patientId: string;
  readonly umr: string;
  readonly fullName: string;
  readonly city: string | null;
  readonly gender?: string | null;
  readonly ageYears?: number | null;
  readonly lastVisitAt?: string | null;
}

/** The operator's explicit decision about WHO this enquiry is for
 *  (LEAD-ATTACH-CONFIRM-V1). Mirrors the intake door's shape (#402). The
 *  server attaches nothing without it -- this is a contract, not a hint. */
export type LeadPersonChoiceRequest =
  | { readonly action: 'link'; readonly patientId: string }
  | { readonly action: 'create_new' };

export interface ConvertLeadToPatientResultView {
  readonly outcome:
    | 'linked'
    | 'already_linked'
    | 'person_confirmation_required'
    | 'different_person_confirmed'
    | 'no_patient_record';
  readonly patientId?: string;
  readonly linkedPatient?: PatientConvertCandidateView;
  readonly candidates?: readonly PatientConvertCandidateView[];
  readonly patientIdStamped?: boolean;
  readonly milestoneCommitted?: boolean;
}

/** Backend POST /api/leads/:id/convert-to-patient. Omit patientChoice to SEE
 *  who this number could belong to; send one to act. A phone match never
 *  attaches on its own, so the first call always just looks. The backend is
 *  the security boundary (booking audience + the server-side candidate
 *  fence). */
export async function convertLeadToPatient(
  leadId: number,
  patientChoice?: LeadPersonChoiceRequest,
): Promise<ConvertLeadToPatientResultView> {
  return portalApiPost<ConvertLeadToPatientResultView>(
    `/leads/${leadId}/convert-to-patient`,
    patientChoice !== undefined ? { patientChoice } : {},
  );
}

// --- Lead bridge (PATIENT-MASTER-CONVERSION-AND-PAYMENT-LINK-COHERENCE-V1):
//     "Create patient from this lead" -- the guided create/link door behind
//     Convert's honest no_patient_record. The request NEVER carries a phone
//     (the copy is server-side via the audited vault reveal); the response
//     identity cards are the convert-candidate shape (umr / name / city).

export interface LeadBridgeResultView {
  readonly existingLinkReview?: boolean;
  readonly outcome:
    | 'linked'
    | 'already_linked'
    | 'person_confirmation_required'
    | 'created_and_linked'
    | 'created_not_linked'
    | 'no_phone_on_lead'
    | 'phone_unavailable';
  readonly patientId?: string;
  readonly linkedPatient?: PatientConvertCandidateView;
  readonly candidates?: readonly PatientConvertCandidateView[];
  readonly patientIdStamped?: boolean;
  readonly patientCreated?: boolean;
}

export interface LeadBridgeRequest {
  readonly clinicId?: number;
  readonly leadId: number;
  /** The operator's explicit decision about who this enquiry is for. Without
   *  it the server neither attaches an existing patient nor creates a new
   *  one -- it answers with who this number could belong to. */
  readonly patientChoice?: LeadPersonChoiceRequest;
  /** Operator-typed demographics for the create leg (never a phone). */
  readonly gender?: string;
  readonly ageYears?: number;
  readonly dob?: string;
  /** Only consumed when the lead was captured without a name. */
  readonly fullName?: string;
  readonly guardian?: { readonly name: string; readonly relationship: string };
  readonly consent?: { readonly grantedBy: string; readonly channel: string };
  /** RECEPTION-FIX-AND-POLISH-BATCH-V1 (FD-16 continue-from-enquiry): the
   *  full registration field set rides the bridge (typed city wins over
   *  the lead's copy; validated by THE ONE writer server-side). */
  readonly city?: string;
  readonly relationName?: string;
  readonly occupation?: string;
  readonly handlingCategory?: string;
  readonly externalMr?: string;
  readonly registrationPrivacyConsent?: {
    readonly grantedBy: string;
    readonly channel: string;
  };
}

/** Backend POST /api/patient-master/lead-bridge (dark behind the existing
 *  PATIENT_MASTER_V1_ENABLED gate; registration authority unchanged). */
export async function postLeadBridge(
  request: LeadBridgeRequest,
  expectedBearer?: string,
): Promise<LeadBridgeResultView> {
  return portalApiPost<LeadBridgeResultView>(
    '/patient-master/lead-bridge',
    request,
    expectedBearer,
  );
}

/** STITCH-2 SCOPE 10 (F10): one row of the clinic-scoped patients list. */
export interface PatientListItem {
  readonly patientId: string;
  readonly umr: string;
  readonly fullName: string;
  readonly gender: string | null;
  readonly ageYears: number | null;
  /** F-3: TRUE when the age is the stored approximate (no derivable DOB).
   *  Optional (additive contract); absent = not approximate. */
  readonly ageIsApproximate?: boolean;
  readonly city: string | null;
  readonly registeredClinicId: number | null;
  /** F-3 timezone corrective: the registered clinic's IANA timezone for TRUE
   *  clinic-local registered-date formatting (optional; absent -> default). */
  readonly registeredClinicTimezone?: string | null;
  readonly createdAt: string | null;
  readonly lastVisitAt: string | null;
}

export interface PatientListResponse {
  readonly clinicId: number;
  readonly patients: readonly PatientListItem[];
  readonly capped: boolean;
}

/** Backend GET /api/patient-master/list -- this-clinic patients,
 *  recent-first, demographics only (no phone on the wire). */
export async function fetchPatientMasterList(
  clinicId: number | null,
  limit?: number,
): Promise<PatientListResponse> {
  const params = new URLSearchParams();
  if (clinicId !== null) params.set('clinicId', String(clinicId));
  if (limit !== undefined) params.set('limit', String(limit));
  const qs = params.toString();
  return portalApiGet<PatientListResponse>(
    `/patient-master/list${qs ? `?${qs}` : ''}`,
  );
}

/** STITCH-2 SCOPE 8: one bookable slot on the picker grid (server truth). */
export interface DaySlotOption {
  readonly startIso: string;
  readonly label: string;
  readonly taken: boolean;
  readonly past: boolean;
}

/** Wire shape of GET /api/appointments/day-slots (server-computed grid). */
export interface DaySlotsResponse {
  /** Confirmed dated coverage for the next28days, independent of occupied slots. Not a recurring schedule. */
  readonly confirmedRosterDates?: readonly string[];
  readonly clinicId: number;
  readonly date: string;
  readonly slotMinutes: number;
  readonly capacityPerSlot: number;
  readonly windowText: string;
  readonly slots: readonly DaySlotOption[];
  /** WALK-IN-COMPLETION-V1 (grid-derived): the walk-in default offer,
   *  SELECTED FROM the `slots` grid (always an exact member;
   *  next-boundary-only V1 policy -- the earliest free, non-past grid slot,
   *  past the confirmation lead on the current IST date). Null when the day
   *  offers nothing. Optional so older wire shapes stay valid (absent reads
   *  as no offer). */
  readonly walkInOffer?: { readonly startIso: string; readonly label: string } | null;
}

/** Backend GET /api/appointments/day-slots?leadId=&date= -- the booking
 *  picker's slot grid for the lead's clinic on one IST date. The server
 *  computes the window + taken marks from the SAME booking-rules config the
 *  write route enforces (one rule source). An EMPTY date omits the param, so
 *  the SERVER resolves the current IST date (E-3 clinic-time law: the
 *  browser's timezone never picks the walk-in day). */
export async function fetchDaySlots(
  leadId: number,
  date: string,
): Promise<DaySlotsResponse> {
  const qs = `?leadId=${encodeURIComponent(String(leadId))}${
    date !== '' ? `&date=${encodeURIComponent(date)}` : ''
  }`;
  return portalApiGet<DaySlotsResponse>(`/appointments/day-slots${qs}`);
}

/** Backend POST /api/appointments/booked. Body is { leadId, appointmentTime }
 *  plus an OPTIONAL confirm-only `clinicId`.
 *
 *  THE CLINIC IS STILL NEVER CLIENT INPUT (contract unchanged, api/routes/
 *  appointments.ts): the server DERIVES the visit clinic from the org-scoped
 *  lead and a supplied `clinicId` may only CONFIRM it -- a mismatch is
 *  refused. CHANNEL-TO-BOOKED-APPOINTMENT-V1 sends the clinic the agent was
 *  SHOWN, so a branch changed by another operator between slot selection and
 *  Book is refused instead of silently booked at the wrong clinic. */
export async function bookAppointment(
  leadId: number,
  appointmentTime: string,
  clinicId?: number,
): Promise<BookedAppointmentResult> {
  return portalApiPost<BookedAppointmentResult>('/appointments/booked', {
    leadId,
    appointmentTime,
    ...(clinicId === undefined ? {} : { clinicId }),
  });
}

/** CHANNEL-TO-BOOKED-APPOINTMENT-V1 (Half B): the wire result of the
 *  branch-choice step. `already_set` means the lead ALREADY carried
 *  EXACTLY this clinic -- a benign retry, so the caller proceeds to book. */
export interface SetLeadBookingClinicResult {
  status: 'clinic_set' | 'already_set';
  leadId: number;
  clinicId: number;
  clinicLabel: string | null;
  keyMoved: boolean;
  workActionStamped: boolean;
}

/** Backend POST /api/leads/:leadId/booking-clinic -- the agent chooses the
 *  branch for a clinic-less (WhatsApp / chat) enquiry so it can be booked.
 *  Body is EXACTLY { clinicId }. The server gates on the BOOKING audience
 *  and on the caller HOLDING that clinic, then writes through THE ONE
 *  clinic-assignment core (set-once CAS + audit). It never books: the
 *  booking leg stays POST /api/appointments/booked, whose contract is
 *  unchanged. */
export async function setLeadBookingClinic(
  leadId: number,
  clinicId: number,
): Promise<SetLeadBookingClinicResult> {
  return portalApiPost<SetLeadBookingClinicResult>(
    `/leads/${encodeURIComponent(String(leadId))}/booking-clinic`,
    { clinicId },
  );
}

/** RECEPTION-FIX-AND-POLISH-BATCH-V1 (FD-20): the clinic-keyed slot grid
 *  for the registered-patient booking offer -- SAME endpoint, SAME rule
 *  source; the server scope-asserts the clinic. */
export async function fetchDaySlotsForClinic(
  clinicId: number,
  date: string,
  selection?: {doctorId: number; durationMinutes: number},
): Promise<DaySlotsResponse> {
  const qs = `?clinicId=${encodeURIComponent(String(clinicId))}${
    date !== '' ? `&date=${encodeURIComponent(date)}` : ''
  }`
    + (selection ? `&doctorId=${selection.doctorId}&durationMinutes=${selection.durationMinutes}` : '');
  return portalApiGet<DaySlotsResponse>(`/appointments/day-slots${qs}`);
}

/** FD-20: the patient-keyed booking leg of THE ONE booked writer -- a
 *  just-registered patient books the offered slot (no lead). Body is
 *  EXACTLY { patientId, clinicId, appointmentTime } plus the B1 walk-in
 *  marker (RECEPTION-MONEY-TRUTH-V1): walkIn true = the patient is
 *  physically at the desk booking TODAY -- the server births the row
 *  checked_in ("Ready for doctor") and the desk never walks the
 *  Confirm -> Arrived -> Check-in ceremony for them. */
export async function bookAppointmentForPatient(input: {
  patientId: string;
  clinicId: number;
  appointmentTime: string;
  walkIn?: boolean;
  descriptors?: PatientBookingSelection;
}): Promise<BookedAppointmentResult> {
  const { descriptors, ...legacy } = input;
  return portalApiPost<BookedAppointmentResult>('/appointments/booked', { ...legacy, ...descriptors });
}

/** DOCTOR-HANDS-V1 (BUILD 1): the slot grid for the consultation's OWN
 *  clinic, behind the doctor's three fences. SAME grid builder as the desk's
 *  day-slots read (ONE RULE SOURCE), so the picker and the write validation
 *  cannot drift. The clinic is derived server-side from the fenced
 *  appointment -- there is no clinicId to send. */
export async function fetchConsultNextVisitSlots(
  appointmentId: number,
  date: string,
  selection?: {doctorId: number; durationMinutes: number; appointmentId?: number},
): Promise<DaySlotsResponse> {
  const params = new URLSearchParams();
  if (date) params.set('date', date);
  if (selection) {
    params.set('doctorId', String(selection.doctorId));
    params.set('durationMinutes', String(selection.durationMinutes));
    if (selection.appointmentId !== undefined) params.set('appointmentId', String(selection.appointmentId));
  }
  const qs = params.size ? `?${params}` : '';
  /* Legacy time-only caller remains supported. */
  return portalApiGet<DaySlotsResponse>(
    `/doctor/consult/${appointmentId}/next-visit-slots${qs}`,
  );
}

/** DOCTOR-HANDS-V1 (BUILD 1): the doctor books the follow-up for the patient
 *  in front of her. Body is EXACTLY { appointmentTime } -- patient and clinic
 *  are DERIVED from the fenced consultation and are refused if sent, so this
 *  door can never reach another patient or another clinic. The write itself
 *  goes through THE ONE patient-keyed booking writer the desk already uses. */
export async function bookConsultNextVisit(
  appointmentId: number,
  appointmentTime: string,
): Promise<BookedAppointmentResult> {
  return portalApiPost<BookedAppointmentResult>(
    `/doctor/consult/${appointmentId}/next-visit-booking`,
    { appointmentTime },
  );
}

/** Narrow Return channel: patient/clinic/actor are derived by the server. */
export interface ConsultReturnAppointment {
  id: number; clinicId: number; patientId: string; appointmentTime: string;
  doctorId: number; durationMinutes: number; status: string; updatedAt: string;
  bookedByName?: string | null; bookedByCurrentActor?: boolean;
}
export type ConsultReturnExpected = Pick<ConsultReturnAppointment, 'appointmentTime' | 'doctorId' | 'durationMinutes' | 'updatedAt'>;
export interface ConsultReturnSelection {
  appointmentTime: string; doctorId: number; durationMinutes: number; requestId: string;
}
export interface ConsultReturnMutationResult {
  outcome: 'created' | 'rescheduled' | 'cancelled';
  appointment: ConsultReturnAppointment;
  replayed?: boolean;
}
export function fetchConsultReturnBooking(consultationId: number): Promise<{outcome: 'read'; appointment: ConsultReturnAppointment | null}> {
  return portalApiGet(`/doctor/consult/${consultationId}/next-visit-booking`);
}
export function createConsultReturnBooking(consultationId: number, input: ConsultReturnSelection): Promise<ConsultReturnMutationResult> {
  return portalApiPost(`/doctor/consult/${consultationId}/next-visit-booking`, input);
}
export function changeConsultReturnBooking(consultationId: number, input: ConsultReturnSelection & {appointmentId: number; expected: ConsultReturnExpected}): Promise<ConsultReturnMutationResult> {
  return portalApiPatch(`/doctor/consult/${consultationId}/next-visit-booking`, input);
}
export function cancelConsultReturnBooking(consultationId: number, input: {appointmentId: number; expected: ConsultReturnExpected; requestId: string}): Promise<ConsultReturnMutationResult> {
  return portalApiDelete(`/doctor/consult/${consultationId}/next-visit-booking`, input);
}

/** Backend POST /api/appointments/:id/confirm (AGENT-TO-PATIENT-SPINE-V1 / SLA
 *  item 4). booked -> confirmed. No PHI body (the id is in the PATH). Config-
 *  consulted authority (the call-centre agent may confirm -- Q10); the backend
 *  enforces the gate + scope + IDOR + the confirm<-booked transition guard. */
export async function confirmAppointment(
  appointmentId: number,
): Promise<AppointmentActionResultView> {
  return portalApiPost<AppointmentActionResultView>(
    `/appointments/${appointmentId}/confirm`,
    {},
  );
}

/** Backend POST /api/appointments/:id/reschedule. Body is ONLY { appointmentTime }. */
export async function rescheduleAppointment(
  appointmentId: number,
  appointmentTime: string,
  previousAppointmentTime?: string,
): Promise<AppointmentActionResultView> {
  return portalApiPost<AppointmentActionResultView>(
    `/appointments/${appointmentId}/reschedule`,
    { appointmentTime, ...(previousAppointmentTime ? { previousAppointmentTime } : {}) },
  );
}

/** Backend POST /api/appointments/:id/cancel. Body: { reasonCode } REQUIRED
 *  for every role that may cancel -- the cancel-with-reason picker's
 *  controlled token (rider R-08; validated server-side against the org's
 *  configured vocabulary). Omitting it is refused with 400 "Choose a
 *  cancellation reason." Never PHI, never free text. */
export async function cancelAppointment(
  appointmentId: number,
  reasonCode?: string,
  expectedAppointmentTime?: string,
): Promise<AppointmentActionResultView> {
  return portalApiPost<AppointmentActionResultView>(
    `/appointments/${appointmentId}/cancel`,
    {...(reasonCode !== undefined && reasonCode !== '' ? {reasonCode} : {}),
      ...(expectedAppointmentTime ? {expectedAppointmentTime} : {})},
  );
}

/** Backend POST /api/appointments/:id/no-show. No PHI body. */
export async function markAppointmentNoShow(
  appointmentId: number,
): Promise<AppointmentActionResultView> {
  return portalApiPost<AppointmentActionResultView>(
    `/appointments/${appointmentId}/no-show`,
    {},
  );
}

// ─── S2-P3 check-in / outcome mutations ───────────────────────────────────
//
// arrived / checked-in / fulfilled send NO PHI body ({} - the id is in the
// PATH). not-converted sends ONLY { reason } (a controlled token, never a
// free-text note). The backend enforces the role gate + scope + IDOR + the
// transition guard and is the security boundary.

/** Backend POST /api/appointments/:id/arrived. No PHI body. */
export async function markAppointmentArrived(
  appointmentId: number,
): Promise<AppointmentActionResultView> {
  return portalApiPost<AppointmentActionResultView>(
    `/appointments/${appointmentId}/arrived`,
    {},
  );
}

/** Backend POST /api/appointments/:id/checked-in. No PHI body. */
export async function markAppointmentCheckedIn(
  appointmentId: number,
  identityChecked = false,
  returningCheckIn = false,
): Promise<AppointmentActionResultView> {
  // Returning-patient check-in (Ravi, 16 Oct 2026): the server verifies an
  // earlier visit before older blank registration fields stop gating.
  return portalApiPost<AppointmentActionResultView>(
    `/appointments/${appointmentId}/checked-in`,
    returningCheckIn ? {identityChecked, returningCheckIn: true} : {identityChecked},
  );
}

/** DAY-BOARD-DOCTOR-ASSIGNMENT-VERTICAL-V1 (S1): the assigned/reassigned
 *  doctor echo. PHI-free (ids + tokens only). */
export interface AssignDoctorResultView {
  outcome: 'doctor_assigned';
  appointment: AppointmentActionResultView['appointment'];
  doctorId: number;
  previousDoctorId: number | null;
  alreadyAssigned: boolean;
}

/** Backend POST /api/appointments/:id/assign-doctor. Body is EXACTLY
 *  { doctorId, reasonCode? } (internal roster id and controlled token only). The
 *  backend enforces the config-consulted role gate + scope + IDOR + the
 *  clinic-roster validation + the CAS race guard. */
export async function assignAppointmentDoctor(
  appointmentId: number,
  doctorId: number,
  reasonCode?: string,
): Promise<AssignDoctorResultView> {
  return portalApiPost<AssignDoctorResultView>(
    `/appointments/${appointmentId}/assign-doctor`,
    { doctorId, ...(reasonCode ? { reasonCode } : {}) },
  );
}

// --- PATIENT-INTAKE-IDENTITY-AND-AUTOMATIC-CHECKIN-SPINE-V1 mutations -------
//
// The intake-session routes (dark behind PATIENT_INTAKE_SPINE_V1_ENABLED +
// PATIENT_MASTER_V1_ENABLED; 503 before auth). start/cancel send workflow
// tokens only. complete sends the intake form fields ONE WAY into the
// backend orchestration (create-or-link -> appointment link -> intake
// complete -> automatic check-in); the response carries ids + controlled
// tokens + PHI-bounded candidate cards -- never a stored contact value.

export interface IntakeSessionView {
  id: number;
  appointmentId: number;
  mode: 'digital' | 'paper';
  status: string;
  documentStatus: string;
  startedAt: string;
  completedAt: string | null;
}

export interface StartIntakeResult {
  session: IntakeSessionView;
  alreadyStarted: boolean;
}

export interface IntakeIdentityCandidateView {
  patientId: string;
  umr: string;
  fullName: string;
  gender: string;
  ageYears: number | null;
  city: string | null;
}

export interface IntakeCheckInView {
  checkedIn: boolean;
  alreadyCheckedIn: boolean;
  warning: string | null;
}

export interface CompleteIntakeResult {
  outcome:
    | 'linked'
    | 'already_linked'
    | 'created_and_linked'
    | 'created_not_linked'
    | 'identity_review_required'
    | 'registration_capture_required'
    | 'completed_reference'
    | 'appointment_identity_conflict';
  patientId: string | null;
  patientUmr: string | null;
  appointmentId: number;
  linkStatus: string | null;
  /** The DIRECT appointment->patient reference (founder corrective ruling
   * 2026-07-14): 'linked' | 'already_linked' | 'conflict' (check-in
   * stopped; review required) | 'failed' (honest partial; retry) | null
   * (review outcome). Rendered on the drawer's completion state so WALK 17
   * verifies it per scenario without DB access. */
  directAppointmentPatientLink:
    | 'linked'
    | 'already_linked'
    | 'conflict'
    | 'failed'
    | null;
  checkIn: IntakeCheckInView | null;
  doctorQueueReady: boolean;
  requiredAction: string | null;
  warnings: readonly string[];
  candidates: readonly IntakeIdentityCandidateView[];
  matchTier: string | null;
  /** AUG-12-WALK-2-REGISTRATION-TRUTH-V1 (F1/F3): on the
   *  registration_capture_required outcome, the exact tenant-required field
   *  labels still missing (plain words); absent otherwise. */
  missingRegistrationFields?: readonly string[];
  replayed: boolean;
}

export interface IntakeCompleteFields {
  fullName: string;
  mobile?: string;
  gender: string;
  dob?: string;
  ageYears?: number;
  city?: string;
  /** AUG-12-WALK-2-REGISTRATION-TRUTH-V1 (F3): the config-gated
   *  registration extras -- sent only when the org contract names them. */
  relationName?: string;
  occupation?: string;
  handlingCategory?: string;
  guardian?: { name: string; relationship: string };
  consent?: { grantedBy: string; channel: string };
  dataProcessingConsent?: { grantedBy: string; channel: string };
}

/** Backend POST /api/appointments/:id/intake/start. Body: { mode }. */
export async function startIntakeSession(
  appointmentId: number,
  mode: 'digital' | 'paper',
): Promise<StartIntakeResult> {
  return portalApiPost<StartIntakeResult>(
    `/appointments/${appointmentId}/intake/start`,
    { mode },
  );
}

/** Backend POST /api/appointments/:id/intake/complete. The requestId is the
 *  client-minted completion idempotency key: HOLD it across retries of the
 *  same submission (a replay returns the recorded outcome and writes
 *  nothing server-side). */
export type IntakeCorrectionField = 'full_name' | 'phone' | 'dob' | 'external_mr';
export type IntakeCorrectionReason =
  | 'booking_typo'
  | 'new_phone_number'
  | 'name_correction'
  | 'dob_correction'
  | 'different_person'
  | 'external_mr_missing'
  | 'other';

/** A patient-proposed identity correction (Section 3). When any differs from
 *  the evidence-backed value the backend routes to review (zero overwrite). */
export interface ProposedCorrectionInput {
  field: IntakeCorrectionField;
  reason: IntakeCorrectionReason;
  proposedValue?: string;
}

export async function completeIntakeSession(
  appointmentId: number,
  body: {
    requestId: string;
    fields?: IntakeCompleteFields;
    patientChoice?: { action: 'link'; patientId: string } | { action: 'create_new' };
    proposedCorrections?: ProposedCorrectionInput[];
    identityChecked?: boolean;
    registrationVersion?: string | null;
    recoverSavedRegistration?: true;
    /** FD-40: the patient's stated reason-for-visit (bounded 200); written
     *  server-side ONLY when the desk left the episode complaint empty. */
    complaint?: string;
  },
  expectedBearer?: string,
): Promise<CompleteIntakeResult> {
  return portalApiPost<CompleteIntakeResult>(
    `/appointments/${appointmentId}/intake/complete`,
    body,
    expectedBearer,
  );
}

// --- Intake prefill (PATIENT-INTAKE-PREFILL-AND-EXPERIENCE-V1) --------------

export type PrefillSourceType =
  | 'canonical_patient_master'
  | 'verified_contact'
  | 'patient_identifier'
  | 'appointment'
  | 'lead'
  | 'patient_proposed'
  | 'ai_ocr_draft';

export interface PrefillFieldDescriptor {
  field: string;
  displayValue: string | null;
  hasValue: boolean;
  sourceType: PrefillSourceType;
  sourceLabel: string;
  sourceRecordType: string;
  verificationState: 'verified' | 'unverified' | 'proposed' | 'draft';
  canonical: boolean;
  identitySensitive: boolean;
}

export interface IntakeAgeView {
  ageYears: number | null;
  derivedFromDob: boolean;
  isApproximate: boolean;
  dobKnown: boolean;
}

export interface IntakeMrAliasView {
  system: string;
  systemLabel: string;
  value: string;
}

export interface IntakeConsentPosture {
  serviceLabel: string;
  serviceAllowed: boolean | null;
  serviceNote: string;
  promotionalLabel: string;
  promotionalAllowed: boolean | null;
  promotionalNote: string;
  doNotContact: boolean;
}

export type SavedRegistrationContactField =
  | {state:'saved'; value:string}
  | {state:'withheld'|'not_supplied'|'unavailable'; value:null};
export type SavedRegistrationContacts = Record<'alt_mobile'|'alt_mobile_relation'|'alt_mobile_relation_name'|
  'emergency_contact_name'|'emergency_contact_relation'|'emergency_contact_phone', SavedRegistrationContactField>;

export interface IntakePrefillResult {
  intakeSessionId?: number | null;
  registrationRecovery?: {requestId:string;canResume:boolean;contacts?:SavedRegistrationContacts} | null;
  registrationContacts?: {
    altMobile: string | null; altMobileRelation: string | null; altMobileRelationName: string | null;
    emergencyContactName: string | null; emergencyContactRelation: string | null; emergencyContactPhone: string | null;
  } | null;
  registrationGuardian?: {name:string;relationship:string;consentGrantedBy:string|null;consentChannel:string|null} | null;

  registrationVersion?: string | null;
  appointmentId: number;
  clinicName: string | null;
  clinicTimezone: string;
  doctorName: string | null;
  doctorNote: string | null;
  appointmentTime: string;
  fields: PrefillFieldDescriptor[];
  age: IntakeAgeView;
  patientId: string | null;
  patientUmr: string | null;
  hasLinkedPatient: boolean;
  mrAliases: IntakeMrAliasView[];
  noExternalMrCopy: string | null;
  consent: IntakeConsentPosture;
  /** RECEPTION-FIX-AND-POLISH-BATCH-V1 additions -- ALL optional (LES-057
   *  tolerant reads; an older wire degrades to the ask-instead posture). */
  /** FD-30: the linked mobile's LAST FOUR digits only; null/absent = ask. */
  phoneLast4?: string | null;
  /** F8 (AUG-12-WALK-BLOCKERS-V1, founder JIT ruling): the patient's OWN
   *  number in full on THEIR OWN screen -- lead-sourced, unlinked persons
   *  only; null/absent degrades to the presence/last-4 posture. Staff
   *  surfaces stay masked; this wire's one consumer is the patient mode. */
  phoneFull?: string | null;
  phoneRevealState?: 'revealed' | 'no_value' | 'unavailable' | 'not_applicable';
  /** T4 (SHIPPED-WORK-TRUTH-CORRECTIVE-V1): TRUE when a saved email exists
   *  for this registration (lead capture or the golden record's contact
   *  point). PRESENCE ONLY -- the address never rides this wire (D4); the
   *  reopened door renders the on-file line from this bit. Absent on an
   *  older payload = false (tolerant read). */
  emailOnFile?: boolean;
  /** FD-29/FD-40: the desk-captured complaint; null/absent = may ask. */
  complaintOnFile?: string | null;
  /** FD-40 + DR-01: the resolved complaint-chip vocabulary. */
  complaintCategories?: ReadonlyArray<{ code: string; label: string }>;
  /** FD-32: gender requiredness parity with registration config. */
  genderRequired?: boolean;
  /** AUG-12-WALK-2-REGISTRATION-TRUTH-V1 (F3): the org's registration
   *  required-field tokens; absent on an older wire (form renders the
   *  seeded-default field set). */
  registrationRequiredFields?: readonly string[];
  /** The org's handling-tag vocabulary; [] or absent = never rendered. */
  handlingCategories?: readonly string[];
  /** The org's occupation picklist vocabulary (REGISTRATION-CONTRACT.md 8.6
   *  RULING 3); [] or absent = the control degrades to free text. */
  occupationOptions?: readonly string[];
  /** The two title picklist vocabularies (REGISTRATION-CONTRACT-COMPLETE-
   *  PREP-V1, contract 2.2/2.3); [] or absent = free-text degrade. */
  nameTitleOptions?: readonly string[];
  relationTitleOptions?: readonly string[];
  /** Config-backed address prefill defaults (contract 2.3/2.4); '' or
   *  absent = no default. */
  stateDefault?: string;
  countryDefault?: string;
  /** REGISTRATION-PARTHA-CONTRACT-V1 (Part C/D): the banked relation
   *  vocabulary (the alt-number owner + emergency-contact "who"); [] or
   *  absent = free-text degrade (R-017). */
  relationOptions?: readonly string[];
  /** REGISTRATION-PARTHA-CONTRACT-V1 (Part E): the covered-state district
   *  lists for the City/District validated typeahead, keyed by UPPERCASE
   *  state name; an absent state renders coverage-pending free text (S-4). */
  stateDistricts?: Readonly<Record<string, readonly string[]>>;
  /** Desk-captured registration extras at lead rank; null/absent = ask. */
  registrationRelationName?: string | null;
  registrationOccupation?: string | null;
  registrationHandlingCategory?: string | null;
  /** 20260808_1 contract-field read-backs at lead rank. A pre-split
   *  legacy lead serves only registrationRelationName (the old combined
   *  value, VERBATIM) with registrationRelationTitle null -- the split is
   *  never fabricated client-side either. */
  registrationNameTitle?: string | null;
  registrationLastName?: string | null;
  registrationRelationTitle?: string | null;
  registrationState?: string | null;
  registrationPincode?: string | null;
  registrationAddressDoor?: string | null;
  registrationAddressStreet?: string | null;
  registrationAddressArea?: string | null;
  registrationAddressLandmark?: string | null;
  registrationAddressTaluka?: string | null;
  registrationAddressCountry?: string | null;
  /** F1: the ACTIVE session's pending proposed-detail changes -- the desk
   *  reviews and confirms them on the same form. Phone proposals carry no
   *  value by law (fingerprint-only evidence). */
  pendingCorrections?: ReadonlyArray<{
    field: string;
    proposedValue: string | null;
    reason: string;
  }>;
}

/** Backend GET /api/appointments/:id/intake/prefill. Evidence-backed,
 *  provenance-tagged values; staff wires stay zero-digit masked -- the
 *  patient-facing phoneFull carve-out is the F8 founder ruling above. */
export async function getIntakePrefill(
  appointmentId: number,
): Promise<IntakePrefillResult> {
  return portalApiGet<IntakePrefillResult>(
    `/appointments/${appointmentId}/intake/prefill`,
  );
}

/** Backend POST /api/appointments/:id/intake/cancel. Safe stop: only the
 *  session row closes; patient/lead/appointment/queue stay untouched. */
export async function cancelIntakeSession(
  appointmentId: number,
): Promise<{ cancelled: boolean }> {
  return portalApiPost<{ cancelled: boolean }>(
    `/appointments/${appointmentId}/intake/cancel`,
    {},
  );
}

// --- Pre-consultation questionnaire (PATIENT-PRE-CONSULTATION-QUESTIONNAIRE-
//     AND-DOCTOR-HANDOFF-V1) ------------------------------------------------

export interface PreconsultOptionSpec {
  code: string;
  label: string;
  requiresDetail: boolean;
}

export interface PreconsultQuestionSpec {
  key: string;
  prompt: string;
  answerKind: 'single_select' | 'multi_select' | 'short_text';
  required: boolean;
  options: PreconsultOptionSpec[];
}

export interface PreconsultCompletion {
  answeredCount: number;
  requiredCount: number;
  requiredAnswered: number;
  complete: boolean;
}

/** One answer as the client submits it (one of code / codes / text per kind). */
export interface PreconsultAnswerInput {
  questionKey: string;
  clear?: true;
  code?: string;
  codes?: string[];
  text?: string;
  details?: ClinicalQuestionDetails | null;
}

/** Proof-of-persistence: a resolved (non-throwing) result is the caller's green
 *  light to invoke the completion engine. A persistence failure THROWS a
 *  bounded retryable error (fail-closed) -- there is no soft-success. */
export interface SaveQuestionnaireResult {
  saved: boolean;
  answeredCount: number;
  completion: PreconsultCompletion;
  safetyFlagPresent: boolean;
}

/** One raw saved answer for patient-mode RESUME (form rehydration). */
export interface PreconsultResumeAnswer {
  questionKey: string;
  code: string | null;
  codes: string[] | null;
  text: string | null;
  details?: ClinicalQuestionDetails | null;
}

export interface DoctorPreconsultAnswerView {
  questionKey: string;
  prompt: string;
  answerKind: string;
  selectedLabels: string[];
  detailText: string | null;
  safetyFlag: boolean;
  capturedAt: string;
  details?: ClinicalQuestionDetails | null;
}

/** DOCTOR read: the patient-reported answers + provenance (clinical audience). */
export interface DoctorPreconsultView {
  audience: 'clinical';
  appointmentId: number;
  questionnairePresent: boolean;
  /** TRUE when a #268 identity review blocks the session: answers are withheld. */
  underIdentityReview: boolean;
  provenanceLabel: string;
  answers: DoctorPreconsultAnswerView[];
  questions: PreconsultQuestionSpec[];
  /** REGISTRATION-CONTRACT-AND-WALK-CLOSURE-V1 (D5): the raw answer state
   *  backing the doctor's EDITABLE Start Consultation intake block (same
   *  answers as above, un-labelled). Empty under identity review.
   *  OPTIONAL on the client so a build served by an older API (or an older
   *  fixture) degrades to a read-only block rather than a type error. */
  resumeAnswers?: PreconsultResumeAnswer[];
  completion: PreconsultCompletion;
  safetyFlagPresent: boolean;
  capturedAt: string | null;
  /** The review-complete stamp (patient_intake_sessions.reviewed_at): non-null
   *  once the explicit doctor "Mark intake reviewed" act recorded it; null =
   *  not yet reviewed. Drives the panel's reviewed line + the action button. */
  reviewedAt: string | null;
  /** DOCTOR-INTAKE-HISTORY-V1 (LAKSHMI-3): the Continue-Care projection of
   *  THE ONE continuity summary (the LAKSHMI-2 owner's contract fields this
   *  consumer is chartered to render; `nextPlanned` deliberately not served
   *  on this wire -- the consult glass owns live next-visit truth). PRESENT
   *  only for a RETURNING patient (prior finalized visit); OPTIONAL so an
   *  older server degrades to the new-patient posture. DOCTOR READ ONLY. */
  continuity?: ConsultContinuityWire;
  /** DOCTOR-INTAKE-HISTORY-V1: the prior finalized visit's saved intake in resume
   *  shape -- the carry-forward prefill source (marked "from <date>";
   *  nothing writes until the doctor's explicit confirm/update act). */
  carryForward?: ConsultCarryForwardWire;
  /** True only when the structured-detail storage seam is available. */
  clinicalDetailsAvailable?: boolean;
}

/** The Continue-Care projection of VisitContinuitySummaryWire (the consult
 *  consumer's chartered fields; one contract, one owner). */
export type ConsultContinuityWire = Omit<VisitContinuitySummaryWire, 'nextPlanned'> & {
  /** Missing on older payloads; never proof that no intake was saved. */
  readonly intakeAvailability?: 'available' | 'unavailable' | 'withheld';
};

/** DOCTOR-INTAKE-HISTORY-V1: the prior-visit carry payload (doctor read). */
export interface ConsultCarryForwardWire {
  readonly fromAppointmentId: number;
  readonly fromVisitDateIso: string;
  readonly capturedAt: string | null;
  readonly answers: readonly PreconsultResumeAnswer[];
}

/** RECEPTIONIST / intake-authority read: STATUS ONLY (never the doctor answers)
 *  + the patient-mode RESUME payload for an active in-progress session. */
export interface StatusPreconsultView {
  audience: 'status';
  appointmentId: number;
  questionnairePresent: boolean;
  underIdentityReview: boolean;
  questions: PreconsultQuestionSpec[];
  resumeAnswers: PreconsultResumeAnswer[];
  completion: PreconsultCompletion;
  safetyFlagPresent: boolean;
  reviewedAt: string | null;
}

export type PreconsultReadResult = DoctorPreconsultView | StatusPreconsultView;

/** Backend GET /api/appointments/:id/intake/questionnaire. Role-shaped: the
 *  doctor receives the answers; the receptionist/intake authority receives
 *  status only (both receive the renderable question spec). */
export async function getIntakeQuestionnaire(
  appointmentId: number,
): Promise<PreconsultReadResult> {
  return portalApiGet<PreconsultReadResult>(
    `/appointments/${appointmentId}/intake/questionnaire`,
  );
}

/** Backend POST /api/appointments/:id/intake/questionnaire. Captures the
 *  patient-reported answers as evidence; NEVER touches the intake completion
 *  engine or the case sheet. */
export async function saveIntakeQuestionnaire(
  appointmentId: number,
  body: { answers: PreconsultAnswerInput[] },
  expectedBearer?: string,
): Promise<SaveQuestionnaireResult> {
  return portalApiPost<SaveQuestionnaireResult>(
    `/appointments/${appointmentId}/intake/questionnaire`,
    body,
    expectedBearer,
  );
}

/* DOCTOR-INTAKE-ONE-FLOW-V1 (R1): the explicit doctor "Mark intake reviewed"
 * act's client + result type (VISIT-ACTOR-ATTRIBUTION-V1 #277) are REMOVED
 * with their route -- the review ceremony is retired by founder ruling
 * 2026-08-14. `reviewedAt` stays on the read views below (the desk
 * paper-completion path still stamps it). */

/** Backend POST /api/appointments/:id/consult-started. No body. The doctor's
 *  consult-started signal (rider R-07): EVENT-ONLY, doctor-gated,
 *  IDEMPOTENT server-side (one event per appointment, ever). Fired
 *  fire-and-forget on consult-workspace entry; until the founder applies
 *  migration 20260711_1 the server answers recorded:false and every display
 *  keeps the draft-signal fallback.
 *
 *  DOCTOR-OS-EXPERIENCE-V1 (LEAD-UNTIL-CONSULT ruling, 2026-08-04): the
 *  start now RESOLVES IDENTITY -- an existing patient links, a registered
 *  lead CONVERTS (mint + UMR) through the one writer, and the visit's one
 *  case-sheet entry is created. The new result fields mirror the backend
 *  ConsultStartedResult; OPTIONAL here so an older server degrades to the
 *  pre-ruling shape. 409 codes the workspace handles: registration_
 *  incomplete (message lists the exact missing fields -- rendered
 *  VERBATIM), identity_review_required, patient_record_required,
 *  registration_enrichment_unavailable. */
export async function markConsultStarted(
  appointmentId: number,
): Promise<{
  outcome: 'consult_started';
  appointmentId: number;
  recorded: boolean;
  alreadyRecorded: boolean;
  eventWarning?: string;
  /** The resolved patient of this visit (linked or freshly converted). */
  patientId?: string;
  /** The UMR when THIS call minted (the conversion moment); else null. */
  patientUmr?: string | null;
  /** TRUE only when THIS start converted the lead (minted patient + UMR). */
  convertedAtConsult?: boolean;
  /** The visit's ONE case-sheet entry (created or found). */
  caseSheetId?: number | null;
  intakeBypassReasonCode?: string;
}> {
  return portalApiPost(`/appointments/${appointmentId}/consult-started`, {});
}

/** Existing protected fee writer; identity/version are retained unchanged on retry. */
export interface ConsultationFeeEnsureResult {
  readonly requestId: string;
  readonly appointmentId: number;
  readonly billId: number;
  readonly itemId: number;
  readonly created: boolean;
}
/** Explicit Doctor Save/Finish only. Price, basis and actor are server resolved. */
export function ensureConsultationFee(appointmentId: number, requestId: string, expectedBearer: string): Promise<ConsultationFeeEnsureResult> {
  return portalApiPost(`/appointments/${appointmentId}/consultation-actions`, { action: 'ensure_fee', requestId }, expectedBearer);
}

export interface ConsultationFeeAmountInput {
  readonly action: 'set_fee_amount';
  readonly reason?: string | null;
  readonly billId: number;
  readonly amountMinorUnits: number;
  readonly expectedVersion: number;
  readonly requestId: string;
}
export function setConsultationFeeAmount(appointmentId: number, input: ConsultationFeeAmountInput | {readonly action: 'waive_fee'; readonly billId: number; readonly reason: string}, expectedBearer: string): Promise<unknown> {
  return portalApiPost(`/appointments/${appointmentId}/consultation-actions`, input, expectedBearer);
}

/** Backend POST /api/appointments/:id/fulfilled. No PHI body. */
export async function markAppointmentFulfilled(
  appointmentId: number,
): Promise<AppointmentActionResultView> {
  return portalApiPost<AppointmentActionResultView>(
    `/appointments/${appointmentId}/fulfilled`,
    {},
  );
}

/** Backend POST /api/appointments/:id/not-converted. Body is ONLY { reason }. */
export async function markAppointmentNotConverted(
  appointmentId: number,
  reason: string,
): Promise<AppointmentActionResultView> {
  return portalApiPost<AppointmentActionResultView>(
    `/appointments/${appointmentId}/not-converted`,
    { reason },
  );
}

// ─── BACK-HALF-CLINICAL-WORKBENCH-UI-01 - categorical consultation outcomes ──
//
// Record ONE categorical outcome over a FULFILLED appointment (the id is in the
// PATH). advised / accepted send NO body; declined sends ONLY { reason } - an
// OPTIONAL controlled OUTCOME_REASONS token (NEVER free text, NEVER PHI, NEVER a
// clinical note). The backend enforces the OUTCOME role gate (coc / doctor), org
// scope, IDOR, and the fulfilled-only transition guard (a non-fulfilled
// appointment is a 409 `appointment_not_actionable`) and is the security
// boundary. Append-only: no appointments.status change, several outcomes may be
// recorded over one fulfilled appointment.

/** Backend POST /api/appointments/:id/converted - record lead_converted (the
 *  positive conversion outcome of record) over a fulfilled appointment. No
 *  body. DOCTOR-CLINICAL-WORKSPACE-V1: the checkout strip's positive leg. */
export async function markLeadConverted(
  appointmentId: number,
): Promise<AppointmentActionResultView> {
  return portalApiPost<AppointmentActionResultView>(
    `/appointments/${appointmentId}/converted`,
    {},
  );
}

/** Backend POST /api/appointments/:id/treatment-advised. No body. */
export async function markTreatmentAdvised(
  appointmentId: number,
): Promise<AppointmentActionResultView> {
  return portalApiPost<AppointmentActionResultView>(
    `/appointments/${appointmentId}/treatment-advised`,
    {},
  );
}

/** Backend POST /api/appointments/:id/treatment-accepted. No body. */
export async function markTreatmentAccepted(
  appointmentId: number,
): Promise<AppointmentActionResultView> {
  return portalApiPost<AppointmentActionResultView>(
    `/appointments/${appointmentId}/treatment-accepted`,
    {},
  );
}

/**
 * Backend POST /api/appointments/:id/treatment-declined. Body is the OPTIONAL
 * { reason }: omit it (no follow-up) or pass ONE controlled OUTCOME_REASONS
 * token. A present reason in the recoverable set (price / timing / no_response)
 * spawns the PIECE 3 follow-up server-side; the response does NOT echo that
 * spawn (only `appointmentEventEmitted`), so the UI derives it.
 */
export async function markTreatmentDeclined(
  appointmentId: number,
  reason?: string,
): Promise<AppointmentActionResultView> {
  return portalApiPost<AppointmentActionResultView>(
    `/appointments/${appointmentId}/treatment-declined`,
    reason === undefined ? {} : { reason },
  );
}

// --- Work Queue (S1-P5-WORK-QUEUE-FIRST-SLICE) ----------------------------
//
// Two session-authed, role/clinic-scoped calls over the existing lead_actions
// work objects. PHI-free: ids / role / status / template label / timestamps +
// the carved lead name display string only - never a phone / mobile_number /
// contact_id / fingerprint. No flag (a scoped read + a single status flip).

/**
 * Mirror of the backend MyWorkActionsResult (api/routes/work-actions.ts):
 * the Open Follow-ups rows PLUS the derived "To schedule" rows
 * (DOCTOR-FLOW-AFTER-INTAKE-V1 D1). `scheduleRows` is optional on the mirror
 * (an older server omits it); the fetch normalizes absent -> [].
 */
export interface MyWorkActionsResultView {
  rows: FollowupRow[];
  scheduleRows?: NextVisitScheduleRowView[];
}

/**
 * Backend GET /api/me/work-actions - the caller's Open Follow-ups (OPEN
 * lead_actions assigned to the caller's role OR the caller's own staff id,
 * clinic-fenced to the caller's scope, excluding null-clinic rows; oldest
 * first) plus the derived "To schedule" rows (finalized case sheets with a
 * follow-up intent and NO active next booking; a row ceases to derive once
 * the next appointment is booked). `portalApiGet` unwraps the
 * `{ success, data }` envelope, so the resolved value is
 * `{ rows, scheduleRows }`.
 */
export interface ReturnSchedulingRequestView {
  readonly targetDate: string | null;
  readonly requestedDoctorId: number | null;
  readonly requestedDoctorName: string | null;
  readonly durationMinutes: number | null;
  readonly forPlanItemId: number | null;
}

export async function fetchMyWorkActionsResult(patientId?: string): Promise<MyWorkActionsResultView> {
  const data = await portalApiGet<{
    rows: Record<string, unknown>[];
    scheduleRows?: Record<string, unknown>[];
  }>(patientId === undefined ? '/me/work-actions' : `/me/work-actions?patientId=${encodeURIComponent(patientId)}`);
  return {
    rows: (data?.rows ?? []).map((r) => ({
      id: Number(r.id),
      leadId: Number(r.leadId),
      patientName: String(r.patientName ?? ''),
      reason: String(r.reason ?? ''),
      dueContext: String(r.dueContext ?? ''),
      channel: String(r.channel ?? ''),
      status: String(r.status ?? ''),
      createdAt: r.createdAt == null ? null : String(r.createdAt),
    })),
    scheduleRows: (data?.scheduleRows ?? []).map((r) => ({
      appointmentId: Number(r.appointmentId),
      caseSheetId: Number(r.caseSheetId),
      clinicId: Number(r.clinicId),
      patientLabel: String(r.patientLabel ?? ''),
      optionKey: String(r.optionKey ?? ''),
      ...(r.returnRequest && typeof r.returnRequest === 'object' ? {returnRequest:r.returnRequest as ReturnSchedulingRequestView} : {}),
      optionLabel: r.optionLabel == null ? null : String(r.optionLabel),
      note: r.note == null ? null : String(r.note),
      treatmentDoctorName:
        r.treatmentDoctorName == null ? null : String(r.treatmentDoctorName),
      closedAt: r.closedAt == null ? null : String(r.closedAt),
      // LAKSHMI-5 CORRECTIVE: the booking identity keys (absent on an older
      // server -> null -> the row renders door-less, honest degradation).
      patientId: r.patientId == null ? null : String(r.patientId),
      leadId: r.leadId == null ? null : Number(r.leadId),
    })),
  };
}

/**
 * The rows-only view of the same read - existing consumers (Workboard, COC
 * cockpit, lead cockpit) keep their exact contract; one GET either way.
 */
export async function fetchMyWorkActions(): Promise<FollowupRow[]> {
  return (await fetchMyWorkActionsResult()).rows;
}

// --- CALLCENTER-QUEUE-QUALIFY-V1 (V2-P1): the command-center read ---------
//
// Mirrors api/routes/callcenter-queue.ts CallcenterQueueResult. PHI-free: ids /
// carved administrative name / source token / stage token / timestamps / the
// owner view + counts only. `ownerAssignment` is null when the V2-P0 flag is
// off (the strip degrades to open + oldest; owner column hidden).

export interface CallcenterQueueRowView {
  readonly safeLeadId: string;
  readonly leadId: number;
  readonly patientName: string;
  readonly sourceLabel: string | null;
  readonly stage: string | null;
  readonly nextActionLabel: string;
  readonly clinicId: number;
  readonly ageMinutes: number | null;
  readonly createdAtIso: string | null;
  readonly ownerAssignment: LeadOwnershipView | null;
  /**
   * RETURNING-PATIENT-RECOGNITION-AND-LEAD-LINKAGE-V1 (Refs #315): the lead-time
   * returning-patient recognition TIER -- the CHIP STATE ONLY ('confident' |
   * 'ambiguous'), null when the lead is new / already linked / lookup could not
   * run. The fenced FACTS live in the Lead 360 panel, never on this list wire.
   */
  readonly returningVisitorTier: 'confident' | 'ambiguous' | null;
  /**
   * LEAD-INBOX-SLA-AGING-AND-QUEUE-HEALTH-V1 (Refs #315): the per-lead SLA
   * breach state (the EXISTING V4-P0 classifier surfaced onto the queue wire).
   * NARROW: each breach carries the engine's controlled tokens ONLY (class /
   * severity / whole waiting-age minutes) -- no name / phone / note / clinical
   * field. null when the row has NO active breach or the fail-soft overlay
   * could not run. Display-only; the per-class detail lives on Lead 360.
   */
  readonly slaState: Lead360SlaState | null;
}

export interface PreVisitQueueRowView {
  readonly safeLeadId: string;
  readonly leadId: number;
  readonly patientName: string;
  readonly clinicId: number;
  readonly appointmentTimeIso: string | null;
  readonly attemptCount: number;
}

export interface IntakeQueueHealthView {
  readonly open: number;
  readonly unassigned: number;
  readonly overdue: number;
  readonly oldestAgeMinutes: number | null;
  readonly ownershipAvailable: boolean;
  /**
   * LEAD-INBOX-SLA-AGING-AND-QUEUE-HEALTH-V1 (Refs #315): per-class SLA breach
   * counts over the SAME rows the list renders (strip == list). Every engine
   * class key is present (0 when none). Keyed by the five-value engine class
   * vocabulary; a key the frontend does not know renders through the
   * count-only path, never echoed raw.
   */
  readonly slaBreachCounts: Readonly<Record<string, number>>;
}

/** V2-P3: the attempt discipline for one chase lead (mirror of the pure
 *  PrevisitDisciplineState -- attempts / next-due / not-reachable eligibility). */
export interface ChaseDisciplineView {
  readonly attemptCount: number;
  readonly maxAttempts: number;
  readonly attemptsRemaining: number;
  readonly attemptsExhausted: boolean;
  readonly notReachableEligible: boolean;
  readonly nextAttemptDueAtIso: string | null;
  readonly nextAttemptDue: boolean;
  readonly windowDeadlineIso: string | null;
  readonly withinWindow: boolean;
  readonly whatsappFollowupAvailable: boolean;
}

/** V2-P3: one row of the pre-visit chase queue. */
export interface ChaseQueueRowView {
  readonly safeLeadId: string;
  readonly leadId: number;
  readonly displayName: string;
  readonly clinicId: number | null;
  readonly clinicLabel: string | null;
  readonly assignedLabel: string | null;
  readonly stageLabel: string | null;
  readonly chaseClass: 'enquired_never_booked' | 'booked_never_visited';
  readonly breachClass: string;
  readonly stuckSinceIso: string | null;
  readonly ageMinutes: number;
  readonly appointmentDatePassed: boolean;
  readonly discipline: ChaseDisciplineView;
}

export interface ChaseQueueView {
  readonly rows: ChaseQueueRowView[];
  readonly discipline: {
    readonly maxAttempts: number;
    readonly windowDaysMin: number;
    readonly windowDaysMax: number;
  };
  readonly capped: boolean;
}

/** V2-P3: one config-backed closure-disposition reason the picker offers. */
export interface PrevisitDispositionReasonView {
  readonly id: string;
  readonly label: string;
  readonly eventType: 'not_reachable' | 'duplicate';
  readonly requiresAttemptsExhausted: boolean;
  readonly provisional: boolean;
}

/**
 * OMNICHANNEL-CONTACT-CENTER-OS-V1 Phase 3 -- the four identity states an
 * inbound call resolves to. Wire tokens, verbatim: the label always rides the
 * row, so the UI never maps a token to a word of its own invention.
 */
export type InboundCallIdentityStateView =
  | 'matched_lead'
  | 'matched_patient'
  | 'unknown_caller'
  | 'resolution_failed';

export type InboundCallOutcomeView =
  | 'answered'
  | 'missed'
  | 'busy'
  | 'unknown';

export interface InboundCallActivityRowView {
  readonly callId: number;
  readonly occurredAtIso: string;
  readonly channel: 'phone';
  readonly channelLabel: string;
  readonly identity: InboundCallIdentityStateView;
  readonly identityLabel: string;
  readonly displayName: string | null;
  readonly leadId: number | null;
  readonly patientId: string | null;
  readonly clinicId: number | null;
  readonly outcome: InboundCallOutcomeView;
  readonly outcomeLabel: string;
  readonly nextAction: string;
  readonly campaignPresent: boolean;
}

export interface InboundCallActivityView {
  readonly rows: InboundCallActivityRowView[];
  readonly windowHours: number;
  readonly total: number;
  readonly countsByIdentity: Readonly<
    Record<InboundCallIdentityStateView, number>
  >;
  readonly needsIdentification: number;
  readonly emptyReason:
    | 'capture_not_enabled'
    | 'no_calls_in_window'
    | 'read_unavailable'
    | null;
  readonly emptyLabel: string | null;
}

export interface CallcenterQueueView {
  readonly deskLabel: string;
  readonly firstTouchMinutes: number;
  readonly qualificationChecklist: string[];
  readonly intake: {
    readonly rows: CallcenterQueueRowView[];
    readonly health: IntakeQueueHealthView;
  };
  readonly previsit: { readonly rows: PreVisitQueueRowView[] };
  readonly chase: ChaseQueueView;
  readonly previsitDispositionReasons: PrevisitDispositionReasonView[];
  readonly bookedToday: number;
  readonly pending: number;
  /**
   * Phase 3. OPTIONAL ON PURPOSE: a backend older than this package genuinely
   * omits the block, so the type says so and every consumer must handle its
   * absence (the fail-soft product law -- an additive read-side field must be
   * backward-compatible). `normalizeCallcenterQueue` fills it with the honest
   * empty block, and `InboundCallPanel` tolerates undefined at the one place
   * every caller passes through.
   */
  readonly inboundCalls?: InboundCallActivityView;
}

/** The fail-soft stand-in when a backend has no inbound-call block at all. */
export function emptyInboundCallActivity(): InboundCallActivityView {
  return {
    rows: [],
    windowHours: 24,
    total: 0,
    countsByIdentity: {
      matched_lead: 0,
      matched_patient: 0,
      unknown_caller: 0,
      resolution_failed: 0,
    },
    needsIdentification: 0,
    emptyReason: 'read_unavailable',
    emptyLabel:
      'Call activity could not be loaded just now. The rest of the queue is unaffected.',
  };
}

/** V2-P3: the result of recording a pre-visit closure disposition. */
export interface RecordPrevisitDispositionView {
  readonly ok: boolean;
  readonly alreadyRecorded: boolean;
  readonly leadId: number;
  readonly reasonId: string;
  readonly eventType: string;
  readonly attemptCount: number | null;
}

/**
 * Backend POST /api/leads/:id/previsit-disposition (PREVISIT-SLA-DISPOSITIONS-V1)
 * -- record a lawful pre-visit closure (not_reachable / duplicate). The server
 * enforces the writer role, the org/clinic fence, the reason vocabulary, the
 * not_reachable attempt-exhaustion precondition, idempotency, and audit.
 */
export async function recordPrevisitDisposition(args: {
  leadId: number;
  reasonId: string;
}): Promise<RecordPrevisitDispositionView> {
  return portalApiPost<RecordPrevisitDispositionView>(
    `/leads/${args.leadId}/previsit-disposition`,
    { reasonId: args.reasonId },
  );
}

/** All-zero per-class SLA breach counts (every engine class key present). */
function zeroSlaBreachCounts(): Record<string, number> {
  const out: Record<string, number> = {};
  for (const cls of SLA_CLASS_ORDER) out[cls] = 0;
  return out;
}

/**
 * LEAD-INBOX-SLA-AGING-AND-QUEUE-HEALTH-V1 (Refs #315): backward-compatibility
 * normalization at THE ONE wire-entry boundary. The read-side SLA fields
 * (per-row `slaState`, `health.slaBreachCounts`) are ADDITIVE -- an older or
 * partial `/callcenter/queue` response (a backend mid-rollout, or a seeded test
 * fixture that predates the fields) may OMIT them. Fill the truthful
 * absent-defaults HERE, once, so every downstream consumer (chip / strip / sort
 * / anyBreached) sees the invariant shape and NONE crashes or blocks the
 * command center (fail-soft product law: a new read-side field must be
 * backward-compatible). Absent `slaState` -> null (no chip -- never a fabricated
 * breach); absent `slaBreachCounts` -> all five classes at 0 (no strip).
 */
export function normalizeCallcenterQueue(
  view: CallcenterQueueView,
): CallcenterQueueView {
  const rows = view.intake.rows.map((r) => {
    const slaState = (r as Partial<CallcenterQueueRowView>).slaState;
    return slaState === undefined ? { ...r, slaState: null } : r;
  });
  const counts = (view.intake.health as Partial<IntakeQueueHealthView>)
    .slaBreachCounts;
  const health =
    counts === undefined
      ? { ...view.intake.health, slaBreachCounts: zeroSlaBreachCounts() }
      : view.intake.health;
  // Phase 3: an older backend has no inboundCalls block. Substitute the
  // honest empty one rather than letting the panel read undefined -- the
  // command center must never crash on an additive field.
  const inboundCalls = view.inboundCalls ?? emptyInboundCallActivity();
  return {
    ...view,
    intake: { ...view.intake, rows, health },
    inboundCalls,
  };
}

/**
 * Backend GET /api/callcenter/queue - the operator command center (summary
 * strip + Q1 intake list + Q2 pre-visit list), all computed by the ONE
 * queue-health module so the strip counts equal the list partitions.
 * `portalApiGet` unwraps the { success, data } envelope. The response is passed
 * through `normalizeCallcenterQueue` so an older/partial shape (missing the
 * additive SLA fields) can never crash or block the command center.
 */
export async function fetchCallcenterQueue(): Promise<CallcenterQueueView> {
  const view = await portalApiGet<CallcenterQueueView>('/callcenter/queue');
  return normalizeCallcenterQueue(view);
}

// ---- CALL-CENTER-CRM-WORKSPACE-V1 (GET /api/callcenter/workspace) ----------

/** The #330 work-reason contract tokens, verbatim -- never renamed on the
 *  wire (binding law 2; the human labels ride workReasonLabel per row). */
export type WorkspaceWorkReason =
  | 'new_lead'
  | 'call_now'
  | 'callback'
  | 'appointment_confirmation'
  | 'waiting_response'
  | 'no_show_recovery'
  | 'overdue_followup'
  | 'escalation'
  | 'routing_exception';

export type WorkspaceOwnerKindView = 'you' | 'staff' | 'exception' | 'unknown';

export interface WorkspaceOwnerView {
  readonly kind: WorkspaceOwnerKindView;
  readonly label: string | null;
  readonly staffId: number | null;
}

export type WorkspaceUrgencyView =
  | 'breach'
  | 'due_now'
  | 'morning_priority'
  | 'upcoming'
  | 'none';

export interface WorkspaceLastActivityView {
  readonly label: string;
  readonly atIso: string | null;
}

export interface CallcenterWorkspaceItemView {
  readonly source?: string | null;
  readonly sourceKind?: 'source' | 'unknown' | 'unlinked';
  readonly state?: string | null;
  readonly workReason: WorkspaceWorkReason;
  readonly workReasonLabel: string;
  readonly leadId: number | null;
  readonly safeLeadId: string | null;
  readonly appointmentId: number | null;
  readonly displayName: string;
  readonly clinicId: number | null;
  readonly clinicLabel: string | null;
  readonly owner: WorkspaceOwnerView;
  readonly stageLabel: string | null;
  readonly urgency: WorkspaceUrgencyView;
  readonly slaAnchorIso: string | null;
  readonly ageMinutes: number | null;
  readonly morningPriority: boolean;
  readonly exceptionReason: string | null;
  readonly nextActionLabel: string;
  readonly lastActivity: WorkspaceLastActivityView | null;
}

export interface CallcenterWorkspaceStripView {
  readonly newLeads: number;
  readonly dueNow: number;
  readonly overdue: number;
  readonly appointmentsAtRisk: number;
  readonly morningPriority: number;
  readonly exceptions: number;
}

export interface WorkspaceWorkloadRowView {
  readonly staffUserId: number;
  readonly label: string;
  readonly openCount: number;
  readonly overdueCount: number;
  readonly paused: boolean;
}

/** CALL-CENTER-TENANT-ROUTING-RULES-V1: the "Overnight leads awaiting release"
 *  count fragment for the Team-operations tile. Present ONLY when the
 *  overnight-hold seam is active (FAIL-SAME: absent = byte-identical wire). */
export interface OvernightCountView {
  readonly awaitingRelease: number;
  readonly capped: boolean;
  readonly scopeLabel: string;
}

export interface CallcenterWorkspaceManagerViewWire {
  readonly workload: readonly WorkspaceWorkloadRowView[] | null;
  readonly pausedWithNoOpenWork: readonly {
    readonly staffUserId: number;
    readonly label: string;
  }[];
  /** Present only when the overnight-hold seam is active. */
  readonly overnight?: OvernightCountView;
}

export interface CallcenterWorkspaceWorkingHoursView {
  readonly open: string;
  readonly close: string;
  readonly days: readonly number[];
}

export interface CallcenterWorkspaceView {
  readonly lens: 'agent' | 'manager';
  readonly generatedAtIso: string;
  /** IANA timezone the "As of" header renders in (clinics.timezone for the
   *  scope, tenant-default fallback) -- TRUE clinic-local, zone-labelled. */
  readonly generatedAtZone: string;
  readonly workingHours: CallcenterWorkspaceWorkingHoursView;
  readonly availableReasons: readonly WorkspaceWorkReason[];
  readonly items: readonly CallcenterWorkspaceItemView[];
  readonly strip: CallcenterWorkspaceStripView;
  readonly cappedSources: readonly string[];
  /** Counts cover permitted items only; these sources are intentionally denied. */
  readonly unavailableSources?: readonly {
    readonly source: 'no_show_recovery';
    readonly reason: 'permission_denied';
  }[];
  readonly manager?: CallcenterWorkspaceManagerViewWire;
}

/**
 * Backend GET /api/callcenter/workspace (CALL-CENTER-CRM-WORKSPACE-V1) - the
 * ONE-QUEUE workspace: the agent My Work own-scope lens or the manager Team
 * operations lens, decided server-side by role. Gated server-side on the
 * per-org callcenter.workspace_enabled config row (seeded OFF) + the
 * workspace audience; a disabled org or unauthorized role gets a 403 (the
 * guard redirects before this fires). `portalApiGet` unwraps the
 * { success, data } envelope.
 */
export async function fetchCallcenterWorkspace(): Promise<CallcenterWorkspaceView> {
  return portalApiGet<CallcenterWorkspaceView>('/callcenter/workspace');
}

/**
 * Backend GET /api/manager/tracking (MANAGER-TRACKING-VIEW-V1, V1-P4) - the
 * leadership queue-health aggregate (item-9 six-tile strip + clinic x metric
 * matrix + per-staff load), all computed by the ONE queue-health module so the
 * tile counts equal the matrix column sums and the whole-feed health.
 * Audience-gated server-side to owner / gm_agm / regional_head (read-only).
 * `portalApiGet` unwraps the { success, data } envelope.
 */
export async function fetchManagerTracking(): Promise<ManagerTrackingView> {
  return portalApiGet<ManagerTrackingView>('/manager/tracking');
}

/** TRAIN-D-V4-P0-SLA-LEAKAGE-ENGINE-V1: the deterministic work-leakage read
 *  (GET /api/sla-leakage). Only called when me.slaLeakageEnabled is present. */
export async function fetchSlaLeakage(): Promise<SlaLeakageResult> {
  return portalApiGet<SlaLeakageResult>('/sla-leakage');
}

/** CALLCENTRE-EOD-V1: the call-centre agent's date-navigable Daily report
 *  (GET /api/callcenter/daily-report). `date` is an IST business date
 *  ('YYYY-MM-DD'); omitted = today, server-resolved (never a client clock). */
export async function fetchCallcenterDailyReport(params?: {
  date?: string;
}): Promise<CallcenterDailyReportWire> {
  const q = params?.date ? `?date=${encodeURIComponent(params.date)}` : '';
  return portalApiGet<CallcenterDailyReportWire>(`/callcenter/daily-report${q}`);
}

/**
 * Backend POST /api/work-actions/:id/transition - flip ONE open action to
 * 'done' or 'dismissed'. The id is in the PATH; the body carries ONLY
 * { status }. The backend requires the caller's staff identity, enforces
 * org/clinic scope, and is the security boundary. Returns the new status.
 */
export async function transitionWorkAction(
  id: number,
  status: 'done' | 'dismissed',
): Promise<{ id: number; status: string }> {
  return portalApiPost<{ id: number; status: string }>(
    `/work-actions/${encodeURIComponent(String(id))}/transition`,
    { status },
  );
}

/**
 * Backend GET /api/org-intake/queue (ORG-INTAKE-QUEUE-V1, B2 / Story 5.4)
 * - the org-intake pending-clinic queue (initially configured for COC in
 * the Partha workflow): OPEN org-level pending leads (clinic_id NULL) for
 * the caller's org with real-field badges, B1 reconciliation-review
 * markers, and the org's clinic picker. The backend resolves the
 * CONFIGURED owner role (org-default new_lead_followup routing_config;
 * default 'coc'; owner supervises) and 401s any other role.
 * `portalApiGet` unwraps the `{ success, data }` envelope. PHI-carved:
 * carved name only - never a phone / fingerprint / dedupe key.
 */
export async function fetchOrgIntakeQueue(): Promise<OrgIntakeQueueResult> {
  return portalApiGet<OrgIntakeQueueResult>('/org-intake/queue');
}

/**
 * Backend POST /api/leads/:leadId/set-clinic (ORG-INTAKE-QUEUE-V1) - the
 * SET-ONCE manual clinic assignment on ONE org-level pending lead. The id
 * is in the PATH; the body carries ONLY { clinicId }. The backend is the
 * boundary: same owner-config gate as the queue read, org-fenced lead
 * load, clinic-in-org validation, and B1's CAS upgrade core (a non-null
 * clinic_id always 409s - change-clinic is a parked fast-follow). 409
 * codes: clinic_already_set / lead_not_open / org_pending_clinic_conflict
 * / parallel_clinic_journey_exists.
 */
export async function setOrgIntakeClinic(
  leadId: number,
  clinicId: number,
): Promise<SetOrgIntakeClinicResult> {
  return portalApiPost<SetOrgIntakeClinicResult>(
    `/leads/${encodeURIComponent(String(leadId))}/set-clinic`,
    { clinicId },
  );
}

/**
 * Backend GET /api/lead-inbox (ALL-SOURCE-LEAD-INBOX-V1, B3 / Story 2.2)
 * - the org-level all-source lead inbox: a bounded, newest-first,
 * org-fenced window of leads from EVERY capture source with honest
 * status chips (each derived from a real stored field), wire-side
 * display labels (the ONE shared label helper), the vault-backed
 * phoneAvailability, and per-org aging config. Same config-backed
 * owner-role gate as org-intake (401 for any other role). Params are the
 * five filters + offset ('all'/default values are omitted by the caller;
 * see leadInboxQueryParams). `portalApiGet` unwraps the
 * `{ success, data }` envelope. PHI-carved: carved name only - never a
 * phone / fingerprint / external_record_id.
 */
/**
 * OWNER-FUNNEL-FOLDIN-V1 (Story 6.2): the owner funnel snapshot. One
 * org-fenced owner read; every metric carries the A1 contract
 * (value/label/tooltip/status/sourceFields) with copy from the ONE
 * backend source -- rendered verbatim. Params: range (+from/to when
 * custom), clinicId, source ('all'/defaults omitted by the caller; see
 * ownerFunnelQueryParams). PHI-carved: aggregates only.
 */
export async function fetchOwnerFunnelSnapshot(
  params: Record<string, string>,
): Promise<OwnerFunnelSnapshot> {
  const entries = Object.entries(params);
  const qs =
    entries.length > 0
      ? `?${entries
          .map(
            ([k, v]) =>
              `${encodeURIComponent(k)}=${encodeURIComponent(v)}`,
          )
          .join('&')}`
      : '';
  return portalApiGet<OwnerFunnelSnapshot>(`/owner/funnel-snapshot${qs}`);
}

export async function fetchLeadInbox(
  params: Record<string, string>,
): Promise<LeadInboxResult> {
  const entries = Object.entries(params);
  const qs =
    entries.length > 0
      ? `?${entries
          .map(
            ([k, v]) =>
              `${encodeURIComponent(k)}=${encodeURIComponent(v)}`,
          )
          .join('&')}`
      : '';
  return portalApiGet<LeadInboxResult>(`/lead-inbox${qs}`);
}

// --- Lead stage advance (C2-WORKBOARD-UI-BUILD-01 / VS1, ADR-0017 Decision 4)
//
// The FRONT-HALF stage ladder the Workboard may advance a lead through. The
// backend (api/routes/lead-stage.ts) is the legality + role-authority boundary:
// `canAdvanceStage` is the sole legality gate, STAGE_CONTRACTS[to].ownerRoleKey
// the role authority, and a concurrent advance returns 409. It REJECTS
// `contacted` (disposition-derived) and the DEFERRED `patient` /
// `converted_to_revenue` positive-conversion gate (Phase E). This client type
// therefore admits ONLY the four front-half targets - patient /
// converted_to_revenue are not representable here.

/** The only stage targets the Workboard advances into (front-half). */
export type LeadStageTarget =
  | 'qualified'
  | 'appointment_booked'
  | 'arrived'
  | 'lost';

/** PHI-free echo of a committed stage advance (mirror of backend AdvanceLeadStageResult). */
export interface AdvanceLeadStageResult {
  readonly leadId: number;
  readonly fromStage: string;
  readonly toStage: string;
  readonly caseStatus: 'Open' | 'Close';
  readonly eventRecorded: boolean;
  readonly workItemClosed: boolean;
}

/**
 * Backend POST /api/leads/:id/stage - advance ONE lead along the front-half
 * LeadStage ladder. The id is in the PATH; the body carries ONLY { toStage }.
 * The backend enforces stage legality, role authority, and org/clinic scope and
 * is the security boundary; an illegal/unauthorized target throws a
 * PortalApiError ({ status: 400/401 }) and a concurrent advance a 409. Returns
 * the committed transition (also closes the lead's open follow-up work item).
 */
export async function advanceLeadStage(
  leadId: number,
  toStage: LeadStageTarget,
): Promise<AdvanceLeadStageResult> {
  return portalApiPost<AdvanceLeadStageResult>(
    `/leads/${encodeURIComponent(String(leadId))}/stage`,
    { toStage },
  );
}

// --- BACK-HALF positive-conversion stage targets (BACK-HALF-CLINICAL-WORKBENCH-
//     UI-01 / ADR-0018 Decision 1) -----------------------------------------
//
// The TWO positive-conversion stage targets the clinical workbench advances a
// lead into. They are LIVE / default-on (ADR-0016 internal-surface policy - no
// feature flag). The backend (api/routes/lead-stage.ts) is the legality +
// role-authority boundary: STAGE_CONTRACTS owns the role gate (`patient` -> coc,
// `converted_to_revenue` -> owner) and an illegal/unauthorized target throws a
// PortalApiError ({ status: 400/401 }), a concurrent advance a 409
// (`lead_stage_conflict`). Deliberately a SEPARATE type + function from the
// front-half `advanceLeadStage` so the Workboard's front-half client can NEVER
// represent these two (its HARD DATA RULE), and this client can never represent
// the four front-half targets.

/** The only stage targets the clinical workbench advances into (back-half). */
export type BackHalfLeadStageTarget = 'patient' | 'converted_to_revenue';

/**
 * Backend POST /api/leads/:id/stage - advance ONE lead into a positive-
 * conversion stage. The id is in the PATH; the body carries ONLY { toStage }.
 * Mirrors `advanceLeadStage` exactly but admits ONLY the back-half targets.
 */
export async function advanceLeadStageBackHalf(
  leadId: number,
  toStage: BackHalfLeadStageTarget,
): Promise<AdvanceLeadStageResult> {
  return portalApiPost<AdvanceLeadStageResult>(
    `/leads/${encodeURIComponent(String(leadId))}/stage`,
    { toStage },
  );
}

// --- Lead record ownership (LEAD-ASSIGNMENT-OWNERSHIP-V1 / V2-P0) ----------
//
// The single write path that changes WHO owns a lead: claim / assign(reassign)
// / release. DEFAULT-ON (the internal-surface env flag was retired by
// TRAIN-B flag-sweep-2); these fetchers are only reached from the owner
// controls AFTER the `leadAssignmentEnabled` /me signal (now a flag-free
// AUDIENCE signal) is present. The id is in the PATH; assign sends ONLY
// { targetStaffId } (a positive integer, never PHI); claim / release send NO
// body. The backend enforces the role gate + org/clinic scope + IDOR + the
// concurrent-claim CAS (409 on a lost update) and is the security boundary. On
// failure portalApiPost throws a PortalApiError carrying { status, code } the
// UI maps to a friendly message. PHI-free: ids / action / event tokens only.

/** PHI-free echo of a committed ownership change (mirror of backend LeadAssignmentResult). */
export interface LeadAssignmentResultView {
  readonly leadId: number;
  readonly action: 'claim' | 'assign' | 'reassign' | 'release';
  readonly eventType: string;
  readonly previousStaffId: number | null;
  readonly newStaffId: number | null;
  readonly eventRecorded: boolean;
  readonly auditRecorded: boolean;
}

/** Backend POST /api/leads/:id/claim -- self-claim a pooled lead. No body. */
export async function claimLead(
  leadId: number,
): Promise<LeadAssignmentResultView> {
  return portalApiPost<LeadAssignmentResultView>(
    `/leads/${encodeURIComponent(String(leadId))}/claim`,
    {},
  );
}

/**
 * Backend POST /api/leads/:id/assign -- leadership assign / reassign to another
 * person. The id is in the PATH; the body is EXACTLY { targetStaffId }.
 */
export async function assignLead(
  leadId: number,
  targetStaffId: number,
): Promise<LeadAssignmentResultView> {
  return portalApiPost<LeadAssignmentResultView>(
    `/leads/${encodeURIComponent(String(leadId))}/assign`,
    { targetStaffId },
  );
}

/** Backend POST /api/leads/:id/release -- release a lead back to the pool. No body. */
export async function releaseLead(
  leadId: number,
): Promise<LeadAssignmentResultView> {
  return portalApiPost<LeadAssignmentResultView>(
    `/leads/${encodeURIComponent(String(leadId))}/release`,
    {},
  );
}

/** CALL-CENTER-TENANT-ROUTING-RULES-V1: one agent's tally in the release
 *  summary (to whom the overnight bucket was dealt). PHI-free. */
export interface OvernightReleaseAgentTallyView {
  readonly staffUserId: number;
  readonly label: string;
  readonly count: number;
  readonly viaContinuity: number;
}

/** PHI-free result of a manager overnight release (mirror of backend
 *  OvernightReleaseResult). */
export interface OvernightReleaseResultView {
  readonly heldCount: number;
  readonly releasedCount: number;
  readonly continuityCount: number;
  readonly dealtCount: number;
  readonly skippedCount: number;
  readonly availableAgentCount: number;
  readonly perAgent: readonly OvernightReleaseAgentTallyView[];
  readonly scopeLabel: string;
}

/**
 * Backend POST /api/callcenter/overnight/release -- the MANAGER action that
 * deals the overnight hold bucket (per-lead continuity, then a round-robin deal
 * of the remainder). No body. Only reached AFTER the `overnightReleaseEnabled`
 * /me signal is present; the backend re-checks gate + hold + audience + assign
 * tier and is the security boundary. Returns the result summary the confirm
 * dialog renders.
 */
export async function releaseOvernightLeads(): Promise<OvernightReleaseResultView> {
  return portalApiPost<OvernightReleaseResultView>(
    '/callcenter/overnight/release',
    {},
  );
}

/** One assignable staff option (mirror of backend AssignableStaff). */
export interface AssignableStaffOption {
  readonly staffUserId: number;
  readonly label: string;
}

/**
 * Backend GET /api/leads/assignable-staff -- the org-scoped assignable-staff
 * picker (leadership tiers only; 503 when the flag is off). `portalApiGet`
 * unwraps the `{ success, data }` envelope, so the resolved value is
 * `{ staff }`. PHI-free: staff ids + display labels only (work data, never a
 * patient value).
 */
export async function fetchAssignableStaff(): Promise<AssignableStaffOption[]> {
  const data = await portalApiGet<{ staff: AssignableStaffOption[] }>(
    '/leads/assignable-staff',
  );
  return data?.staff ?? [];
}

// --- Case Sheet V1 doctor cockpit (SANDBOX-CASE-SHEET-WORKBENCH-V1 / #181) ---
//
// Typed client for the AdoraCare-owned three-layer clinical spine
// (treatment_courses -> case_sheets -> treatment_plan_items). The backend
// (api/routes/case-sheets.ts) is the security boundary: the DOCTOR-only author
// gate, the role-safe field PROJECTION (this client only ever receives the
// DOCTOR 'clinical' view), IDOR + clinic scope, and data_mode='sandbox' are all
// enforced server-side. These types MIRROR the backend read-view DTOs
// (lib/domain/clinical/case-sheet-read-view.ts) exactly - hand-written, like
// AppointmentActionResultView above, since the frontend never imports backend
// lib. actor_principal is on NO read shape (the compliance wall excludes it).
// NEVER flatten planItems - they are a SEPARATE array (three layers, ADR-0019).

/** Case-sheet (visit) status token (mirror of the backend CaseSheetStatus). */
export type CaseSheetStatusView = 'draft' | 'finalized';

/** Plan-item status token (mirror of the backend TreatmentPlanItemStatus). NO
 *  'completed' - it is not in the vocabulary. */
export type PlanItemStatusView =
  | 'advised'
  | 'accepted'
  | 'declined'
  | 'pending'
  | 'deferred';

/** The org tooth-notation display token (TOOTH-NOTATION-INTERIM-V1; mirror of
 *  ToothNotationDisplay -- 'fdi' is the seeded default, ruling R1). */
export type ToothNotationDisplayView = 'fdi' | 'universal';

/** ONE structured tooth reference as the CLINICAL view returns it (mirror of
 *  ToothRefView): the canonical code + BOTH stored display mappings + the
 *  canonical name, resolved SERVER-SIDE -- the client renders, it never
 *  translates notations itself (clinical safety: no silent translation). */
export interface ToothRefView {
  readonly toothCode: string;
  readonly fdiCode: string;
  readonly universalCode: string;
  readonly canonicalName: string;
}

/** One canonical tooth reference row on the doctor consult-context wire
 *  (mirror of ToothReference; the odontogram's render source). */
export interface ToothReferenceView {
  readonly toothCode: string;
  readonly dentition: 'permanent' | 'primary';
  readonly fdiCode: string;
  readonly universalCode: string;
  readonly canonicalName: string;
}

/** Existing clinical readback only; no capture control or endpoint is added. */
export type ClinicalCompletionPerformanceView =
  | { readonly status: 'available'; readonly fact: {
      readonly quantity: number;
      readonly unit: 'procedure' | 'unit' | 'session' | 'visit' | 'case';
      readonly kind: 'procedure' | 'stage';
      readonly performerDoctorId: number | null;
      readonly performerStatus: 'recorded' | 'unavailable';
      readonly clinicId: number;
      readonly completedAt: string;
      readonly completingCaseSheetId: number | null;
      readonly source: 'completion_capture_v1';
    } }
  | { readonly status: 'unavailable'; readonly reason: 'schema_pending' | 'not_recorded' | 'source_mismatch' };

/** One treatment-plan line item as the DOCTOR clinical view returns it (mirror
 *  of ClinicalPlanItemView). A separate row - never folded into the sheet. */
export interface ClinicalPlanItemView {
  readonly id: number;
  /** Exact saved item revision. Absent on older wires: guarded removal stays unavailable. */
  readonly updatedAt?: string;
  readonly procedureName: string;
  readonly toothArea: string | null;
  /** Structured tooth references (TOOTH-NOTATION-INTERIM-V1). OPTIONAL so
   *  pre-migration fixtures stay valid; absent/empty = none recorded. The
   *  free-text toothArea above stays rendered untouched. */
  readonly toothRefs?: readonly ToothRefView[];
  /** Whole INR rupees (BIGINT money-of-record); null on a draft item. */
  readonly estimatedValue: number | null;
  readonly priorityRank: number;
  readonly status: PlanItemStatusView;
  /** One controlled OUTCOME_REASONS token (never PHI); non-null only if declined. */
  readonly declineReason: string | null;
  /** Coded-catalog SNAPSHOT (V6-P3; clinical audience only). null / absent =
   *  legacy free-text item. The default snapshot is INTEGER MINOR UNITS --
   *  the sibling estimatedValue stays whole INR rupees. OPTIONAL so
   *  pre-catalog fixtures stay valid. */
  readonly catalogCode?: string | null;
  readonly catalogPricingUnit?: string | null;
  readonly catalogDefaultAmountMinorUnits?: number | null;
  /** Per-visit clinical COMPLETION evidence (MULTI-VISIT-TREATMENT-JOURNEY-V1,
   *  FILE-ONLY 20260717_1). OPTIONAL so pre-completion fixtures stay valid;
   *  null AND absent both mean "still planned" (never silently completed).
   *  DISTINCT from `status` -- a completed item stays 'accepted'. */
  readonly completedAt?: string | null;
  readonly completedInCaseSheetId?: number | null;
  /** DOCTOR-CONSULT-DELTA-V1: the doctor assigned to PERFORM this line
   *  (treatment-specific; treatment_plan_items.performing_doctor_id).
   *  CLINICAL AUDIENCE ONLY. OPTIONAL so pre-package fixtures stay valid;
   *  null AND absent both mean "not assigned" -- never inferred from the
   *  visit's own doctor. */
  readonly performingDoctorId?: number | null;
  /** DOCTOR-CONSENT-REMARKS-V1: the doctor's per-line REMARK
   *  (treatment_plan_items.item_note). CLINICAL AUDIENCE ONLY -- the
   *  operational / billing / analytics views never carry the key at all.
   *  OPTIONAL so pre-package fixtures stay valid; null AND absent both mean
   *  "no remark". DISTINCT from the visit-grain clinicalNote and from a
   *  consent document's own notes. */
  readonly itemNote?: string | null;
  readonly completionPerformance?: ClinicalCompletionPerformanceView;
}

/** The full DOCTOR case-sheet clinical view (mirror of CaseSheetClinicalView -
 *  the ONLY role-safe view that carries the free-text clinical fields). */
export interface ClinicalPrescriptionLineView {
  readonly repeatedFrom?: { readonly caseSheetId: number; readonly revision: number; readonly medicineId: string };
  readonly id: string;
  readonly name: string;
  readonly strength: string;
  readonly dose: string;
  readonly route: string;
  readonly frequency: string;
  readonly duration: string;
  readonly instructions: string;
}
export interface ClinicalPrescriptionView {
  readonly version: 1;
  readonly noMedicinesToday: boolean;
  readonly medicines: readonly ClinicalPrescriptionLineView[];
}

export interface ClinicalPrescriptionHistoryView {
  readonly available: boolean;
  readonly more: boolean;
  readonly versions: readonly { readonly revision: number; readonly prescription: ClinicalPrescriptionView | null;
    readonly recordedAt: string; readonly actorStaffUserId: number | null; readonly actorRole: 'doctor' }[];
}
/** Saved clinician request. Does not represent a booked appointment. */
export interface ClinicalFinishReviewView {
 readonly version:1;
 readonly outcome:'consultation_only'|'treatment_started'|'treatment_completed'|null;
 readonly googleReviewAsked:boolean|null;
}

export interface ClinicalReturnRequestView {
  readonly version: 1;
  readonly timing: 'in_3_days'|'in_1_week'|'in_2_weeks'|'other'|'after_treatment'|'none';
  readonly basedOnDate: string;
  readonly onDate: string | null;
  readonly interval: { readonly count: number; readonly unit: 'days'|'weeks'|'months' } | null;
  readonly forPlanItemId: number | null;
  readonly details: string;
  readonly requestedDoctorId: number | null;
  readonly durationMinutes: 15|30|45|60|90|null;
}

export interface CaseSheetClinicalView {
  readonly finishReview?: ClinicalFinishReviewView | null;
  readonly finishReviewRevision?: number;
  readonly finishReviewRecordedAt?: string | null;
  readonly finishReviewActorStaffUserId?: number | null;
  readonly googleReviewAskedAt?: string | null;
  readonly googleReviewAskedBy?: number | null;
  readonly returnRequest?: ClinicalReturnRequestView | null;
  readonly returnRequestRevision?: number;
  readonly returnRequestRecordedAt?: string | null;
  readonly returnRequestActorStaffUserId?: number | null;
  readonly followUpDueDate?: string | null;
  /** Saved estimate amounts only, admitted by the existing clinical read.
   * Missing means an older/unavailable reader, never an assumed zero. */
  readonly estimateAdjustment?: { readonly status: 'unavailable' } | {
    readonly status: 'available'; readonly adjustment: {
      readonly originalAcceptedValueInr: number;
      readonly discountAmountInr: number;
      readonly finalAcceptedValueInr: number;
    } | null;
  };
  readonly prescriptionHistory?: ClinicalPrescriptionHistoryView;
  readonly prescription?: ClinicalPrescriptionView | null;
  readonly prescriptionRevision?: number;
  readonly prescriptionRecordedAt?: string | null;
  readonly prescriptionActorStaffUserId?: number | null;
  readonly examination?: ClinicalExaminationView | null;
  readonly examinationRevision?: number;
  readonly audience: 'clinical';
  readonly id: number;
  readonly appointmentId: number;
  readonly treatmentCourseId: number | null;
  readonly leadId: number | null;
  readonly patientId: string | null;
  readonly chiefComplaint: string | null;
  readonly findingsDiagnosis: string | null;
  readonly clinicalNote: string | null;
  /** PHI-light categorical tokens (per-clinic vocabulary, 20260703_2).
   *  OPTIONAL in the mirror so pre-category fixtures stay valid. */
  readonly chiefComplaintCategory?: string | null;
  readonly diagnosisCategory?: string | null;
  /** Config-backed encounter sections + follow-up recommendation
   *  (20260728_1; DOCTOR-CLINICAL-ENCOUNTER-FOUNDATION-V1). OPTIONAL in the
   *  mirror so pre-migration fixtures stay valid; null/absent = not
   *  recorded (or storage pre-apply). */
  readonly medicalDentalHistory?: string | null;
  readonly examinationFindings?: string | null;
  readonly followUpOption?: string | null;
  readonly followUpNote?: string | null;
  /** D2 visit-grain attribution (20260815_1; DOCTOR-FLOW-AFTER-INTAKE-V1).
   *  OPTIONAL in the mirror so pre-migration fixtures stay valid;
   *  null/absent = not stamped (or storage pre-apply). Clinical audience
   *  only on this view. */
  readonly conversionDoctorId?: number | null;
  readonly treatmentDoctorId?: number | null;
  /** Diagnosis-grain structured tooth references (TOOTH-NOTATION-INTERIM-V1;
   *  parent = the case sheet, ruling R6). OPTIONAL so pre-migration fixtures
   *  stay valid. toothRefsAvailable=false = storage pre-apply ("unavailable",
   *  never rendered as "no teeth recorded"). */
  readonly toothRefs?: readonly ToothRefView[];
  readonly toothRefsAvailable?: boolean;
  readonly status: CaseSheetStatusView;
  readonly dataMode: string;
  readonly authoredByRole: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly planItems: readonly ClinicalPlanItemView[];
}

/** GET /api/appointments/:id/case-sheet result: the view, or null when none
 *  exists yet (so the cockpit knows to create one). */
export interface CaseSheetByAppointmentResult {
  readonly caseSheet: CaseSheetClinicalView | null;
}

/** The categorical treatment-course view (mirror of TreatmentCourseView; a
 *  course carries no free-text clinical narrative). */
export interface TreatmentCourseView {
  readonly id: number;
  readonly courseName: string | null;
  readonly status: string;
  readonly estimatedTotalValue: number | null;
  readonly openedAt: string | null;
  readonly closedAt: string | null;
  readonly dataMode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Body for POST /api/case-sheets (create). Only these fields are read
 *  server-side; data_mode is hard-pinned to 'sandbox' by the backend. */
export interface CaseSheetCreateInput {
  readonly examination?: ClinicalExaminationView | null;
  readonly appointmentId: number;
  readonly treatmentCourseId?: number | null;
  readonly chiefComplaint?: string | null;
  readonly findingsDiagnosis?: string | null;
  readonly clinicalNote?: string | null;
  /** Controlled per-clinic tokens (server validates fail-closed). */
  readonly chiefComplaintCategory?: string | null;
  readonly diagnosisCategory?: string | null;
  /** Encounter sections + follow-up (20260728_1). ABSENT keys never ride the
   *  wire (pre-apply the payload stays byte-identical to today); the
   *  follow-up token validates server-side against the org vocabulary. */
  readonly medicalDentalHistory?: string | null;
  readonly examinationFindings?: string | null;
  readonly followUpOption?: string | null;
  readonly followUpNote?: string | null;
  /** Diagnosis-grain tooth codes (canonical; server validates fail-closed --
   *  an invalid code is a 400, impossible to save). ABSENT = the tooth
   *  tables are never named on this request. */
  readonly toothRefs?: readonly string[];
}

/** Body for PUT /api/case-sheets/:id (update). At least one field (the backend
 *  400s an empty patch). status advances draft -> finalized. */
export interface CaseSheetUpdateInput {
  readonly finishReview?: ClinicalFinishReviewView | null;
  readonly finishReviewRevision?: number;
  readonly returnRequest?: ClinicalReturnRequestView | null;
  readonly returnRequestRevision?: number;
  readonly prescription?: ClinicalPrescriptionView | null;
  readonly prescriptionRevision?: number;
  readonly examination?: ClinicalExaminationView | null;
  readonly examinationRevision?: number;
  readonly chiefComplaint?: string | null;
  readonly findingsDiagnosis?: string | null;
  readonly clinicalNote?: string | null;
  /** Controlled per-clinic tokens (server validates fail-closed). */
  readonly chiefComplaintCategory?: string | null;
  readonly diagnosisCategory?: string | null;
  /** Encounter sections + follow-up (20260728_1). ABSENT keys never ride the
   *  wire; included only when the served section layout carries them. */
  readonly medicalDentalHistory?: string | null;
  readonly examinationFindings?: string | null;
  readonly followUpOption?: string | null;
  readonly followUpNote?: string | null;
  /** D2 (20260815_1): the TREATMENT doctor handoff pick (roster-validated
   *  server-side; null clears). ABSENT never rides the wire -- pre-apply the
   *  panel is hidden, so no request can name the column. conversion doctor
   *  has NO input key by law (set-once server stamp). */
  readonly treatmentDoctorId?: number | null;
  readonly status?: CaseSheetStatusView;
  readonly treatmentCourseId?: number | null;
  /** Diagnosis-grain tooth codes: full-set replace ([] clears; absent =
   *  untouched; server validates fail-closed). */
  readonly toothRefs?: readonly string[];
}

/** Body for POST /api/case-sheets/:id/plan-items (add ONE item).
 *  CODED-PICKER path (V6-P3): send catalogItemId INSTEAD of procedureName --
 *  the server resolves the catalog item, writes its display name as the
 *  label, and snapshots code/unit/default onto the row. LEGACY path: send
 *  procedureName exactly as today (byte-identical). */
export interface PlanItemCreateInput {
  /** Stable per-attempt key; retry an uncertain save with the original payload. */
  readonly requestId?: string;
  readonly procedureName?: string;
  readonly catalogItemId?: number;
  readonly toothArea?: string | null;
  /** Item-grain canonical tooth codes (server validates fail-closed). */
  readonly toothRefs?: readonly string[];
  readonly estimatedValue?: number | null;
  readonly priorityRank?: number;
  readonly status?: PlanItemStatusView;
  readonly declineReason?: string | null;
  /** The doctor's per-line remark. Absent = none recorded. */
  readonly itemNote?: string | null;
}

/** Body for PUT /api/case-sheets/:id/plan-items/:itemId (edit ONE item). All
 *  optional; the backend 400s an empty patch. */
export interface PlanItemUpdateInput {
  readonly procedureName?: string;
  readonly toothArea?: string | null;
  /** Item-grain canonical tooth codes: full-set replace ([] clears; absent =
   *  untouched; server validates fail-closed). */
  readonly toothRefs?: readonly string[];
  readonly estimatedValue?: number | null;
  readonly priorityRank?: number;
  readonly status?: PlanItemStatusView;
  readonly declineReason?: string | null;
  /** The treatment-specific performing doctor: a roster id, or null to clear
   *  the assignment. Absent = untouched. The server roster-fences a non-null
   *  id (an ACTIVE doctor in the caller's org) before any write. */
  readonly performingDoctorId?: number | null;
  /** The doctor's per-line remark. ABSENT = untouched (the saved remark
   *  survives a patch that does not name it); null = an EXPLICIT clear. */
  readonly itemNote?: string | null;
}

/** GET /api/appointments/:id/case-sheet - the case sheet for an appointment
 *  (the DOCTOR clinical view), or { caseSheet: null } when none exists yet. */
export async function getCaseSheetByAppointment(
  appointmentId: number,
): Promise<CaseSheetByAppointmentResult> {
  return portalApiGet<CaseSheetByAppointmentResult>(
    `/appointments/${encodeURIComponent(String(appointmentId))}/case-sheet`,
  );
}

// ---- Multi-visit treatment journey (MULTI-VISIT-TREATMENT-JOURNEY-V1) -----

/** Mirror of the backend CreateCourseForSheetResult: the new course + the
 *  refreshed doctor view of the anchoring sheet. */
export interface CreateCourseForSheetResult {
  readonly course: TreatmentCourseView;
  readonly caseSheet: CaseSheetClinicalView;
}

/** POST /api/case-sheets/:id/treatment-course -- create a treatment course
 *  and link this (draft) visit to it in one act. Doctor-only server-side. */
export async function createTreatmentCourseForSheet(
  caseSheetId: number,
  input: { readonly courseName?: string | null },
): Promise<CreateCourseForSheetResult> {
  return portalApiPost<CreateCourseForSheetResult>(
    `/case-sheets/${encodeURIComponent(String(caseSheetId))}/treatment-course`,
    input,
  );
}

/** POST /api/case-sheets/:id/plan-items/:itemId/complete -- record that a
 *  visit performed one planned item (doctor-only; idempotent replay). The
 *  completing visit defaults to the OWNING sheet; pass completedInCaseSheetId
 *  when working from a later visit of the same course. */
export async function completePlanItem(
  caseSheetId: number,
  itemId: number,
  input: { readonly completedInCaseSheetId?: number },
): Promise<CaseSheetClinicalView> {
  return portalApiPost<CaseSheetClinicalView>(
    `/case-sheets/${encodeURIComponent(String(caseSheetId))}/plan-items/${encodeURIComponent(String(itemId))}/complete`,
    input,
  );
}

/** One visit on the journey wire (mirror of CourseJourneyVisitWire). The
 *  per-role plan projection rides each visit; money rides visitMoney. */
export interface CourseJourneyVisitView {
  readonly caseSheetId: number;
  readonly appointmentId: number;
  readonly appointmentTimeIso: string | null;
  readonly storedStatus: string;
  readonly sheetStatus: string;
  readonly itemCount: number;
  readonly acceptedCount: number;
  readonly completedHereCount: number | null;
  /** Role-projected plan view; planItems present for clinical/operational/
   *  billing audiences (analytics ships counts only). */
  readonly plan: {
    readonly audience: string;
    readonly estimateAdjustment?: CaseSheetClinicalView['estimateAdjustment'];
    /** The visit sheet's follow-up token (clinical audience serves the full
     *  view; this mirror names the one extra field the D1 same-as-last-time
     *  suggestion reads -- already on the wire, type-widening only). */
    readonly followUpOption?: string | null;
    readonly planItems?: readonly {
      readonly id: number;
      readonly procedureName: string;
      readonly estimatedValue: number | null;
      readonly status: PlanItemStatusView;
      readonly completedAt?: string | null;
    }[];
  };
}

/** Mirror of the backend CourseLedger (whole INR sums; minor-unit money). */
export interface CourseLedgerView {
  readonly perVisit: readonly {
    readonly state: string;
    readonly outstandingMinorUnits: number | null;
  }[];
  readonly closedAcceptedCount: number;
  readonly closedAcceptedValuedSumInr: number | null;
  readonly closedNotConfiguredCount: number;
  readonly totalAcceptedCount: number;
  readonly collectedMinorUnits: number;
  readonly state:
    | 'outstanding'
    | 'not_configured'
    | 'settled'
    | 'no_accepted_plan'
    | 'not_closed';
  readonly outstandingMinorUnits: number | null;
}

/** Mirror of the backend CourseJourneyResponse. */
export interface CourseJourneyResponse {
  readonly courseId: number;
  readonly clinicId: number;
  readonly courseName: string | null;
  readonly courseStatus: string;
  /** ROUND-3 fence amendment: the course's opened_at, served for the
   *  journey header's "opened <date>". OPTIONAL in the mirror so older
   *  servers/fixtures degrade to an absent date (never invented). */
  readonly openedAt?: string | null;
  readonly estimatedTotalValueInr: number | null;
  readonly visitCount: number;
  readonly decisionCounts: {
    readonly itemCount: number;
    readonly acceptedCount: number;
    readonly declinedCount: number;
    readonly deferredCount: number;
    readonly undecidedCount: number;
  };
  readonly progress: {
    readonly itemCount: number;
    readonly acceptedCount: number;
    readonly completedCount: number;
    readonly remainingPlannedCount: number;
  };
  readonly completionAvailability: 'available' | 'unavailable';
  readonly visits: readonly CourseJourneyVisitView[];
  /** Explicit current-course decisions. Historical visit plan rows remain as recorded. */
  readonly currentDecisions?: { readonly status:'available'; readonly items:readonly {
    readonly planItemId:number; readonly owningCaseSheetId:number; readonly recordedStatus:PlanItemStatusView;
    readonly effectiveStatus:PlanItemStatusView; readonly revision:0|1;
    readonly acceptance:null|{readonly requestId:string;readonly caseSheetId:number;readonly appointmentId:number;
      readonly recordedAt:string;readonly actorStaffUserId:number|null;readonly acceptanceDoctorId:number|null};
  }[] };
  readonly collectionTotals?: {readonly basis: 'accepted_before_discount'; readonly acceptedMinorUnits: number | null; readonly collectedMinorUnits: number | null; readonly outstandingMinorUnits: number | null} | null;
  readonly ledger: CourseLedgerView | null;
  readonly visitMoney:
    | readonly {
        readonly appointmentId: number;
        readonly collectedMinorUnits: number;
        readonly voidedCount: number;
        readonly state: string;
        readonly outstandingMinorUnits: number | null;
      }[]
    | null;
  readonly discounts:
    | readonly {
        readonly caseSheetId: number;
        readonly originalAcceptedValueInr: number;
        readonly discountAmountInr: number;
        readonly finalAcceptedValueInr: number;
        readonly reasonCode: string;
      }[]
    | null;
  readonly nextAppointment: 'scheduled' | 'none' | 'unavailable';
}

/** GET /api/treatment-courses/:id/journey -- the multi-visit journey read. */
export async function fetchTreatmentCourseJourney(
  courseId: number,
  expectedBearer?: string,
): Promise<CourseJourneyResponse> {
  return portalApiGet<CourseJourneyResponse>(
    `/treatment-courses/${encodeURIComponent(String(courseId))}/journey`,
    expectedBearer,
  );
}

// ---- Treatment catalog (TREATMENT-CATALOG-AND-PLAN-PICKER-V1, V6-P3) ------

/** One ACTIVE catalog item as the picker read ships it (mirror of
 *  TreatmentCatalogItem). Reference master data only -- never patient
 *  activity, never clinical text. defaultAmountMinorUnits is INTEGER MINOR
 *  UNITS (paise); null = Not configured (never zero). */
export interface TreatmentCatalogItemView {
  readonly id: number;
  readonly serviceCode: string;
  readonly serviceName: string;
  readonly serviceGroup: string;
  readonly pricingUnit: 'procedure' | 'unit' | 'session' | 'visit' | 'case';
  readonly defaultAmountMinorUnits: number | null;
  readonly currency: string;
  readonly active: boolean;
  readonly clinicId: number | null;
  /** Provenance: when the current default took effect (nullable). */
  readonly priceEffectiveFrom?: string | null;
  /** The ABSOLUTE MINIMUM PERMITTED treatment quote (TREATMENT-PRICE-FLOOR-V1;
   *  INTEGER MINOR UNITS, paise); null = no floor configured (never zero).
   *  INDEPENDENT of defaultAmountMinorUnits. */
  readonly minimumAmountMinorUnits: number | null;
}

/** GET /api/treatment-catalog result. available=false = the pricing contract
 *  is not applied yet (pre-20260711_3) -- every picker surface renders the
 *  legacy free-text flow byte-identical (the D-6 fail-safe law). */
export interface TreatmentCatalogResult {
  readonly available: boolean;
  readonly items: readonly TreatmentCatalogItemView[];
  readonly capped: boolean;
}

/** GET /api/treatment-catalog -- active catalog items for the caller's org,
 *  clinic-override-resolved when clinicId is passed (the picker's context).
 *  includeInactive=1 (ADDITIVE; CLINIC-CATALOG-PRICING-TRUTH-S1-V1) is the
 *  leadership provenance read -- the SERVER ignores it for the doctor
 *  (active-only stays enforced role-side; this flag never widens a read). */
export async function fetchTreatmentCatalog(
  clinicId?: number | null,
  opts: { readonly includeInactive?: boolean } = {},
): Promise<TreatmentCatalogResult> {
  const params = new URLSearchParams();
  if (typeof clinicId === 'number' && Number.isInteger(clinicId) && clinicId > 0) {
    params.set('clinicId', String(clinicId));
  }
  if (opts.includeInactive === true) {
    params.set('includeInactive', '1');
  }
  const qs = params.toString();
  return portalApiGet<TreatmentCatalogResult>(
    `/treatment-catalog${qs === '' ? '' : `?${qs}`}`,
  );
}

// ---- Doctor consult workspace (DOCTOR-CLINICAL-WORKSPACE-V1) --------------

/** Banner identity (mirror of ConsultPatientBanner). NO phone / contact of
 *  any kind is ever on this wire (PHI bound, test-pinned). */
export interface ConsultPatientBanner {
  readonly linked: boolean;
  readonly patientName: string | null;
  readonly umr: string | null;
  readonly ageYears: number | null;
  readonly gender: string | null;
  readonly patientId: string | null;
}

export interface ConsultAppointmentFacts {
  readonly id: number;
  readonly appointmentTime: string;
  readonly status: string;
  readonly clinicId: number;
  readonly clinicName: string | null;
  readonly leadId: number | null;
  readonly specialty: string | null;
}

/** Server-computed checkout capability for the caller (R-1; both legs of
 *  the checkout act ride the ONE org config key). */
export interface ConsultCheckoutCapabilities {
  readonly canFulfil: boolean;
  readonly canRecordNotConverted: boolean;
}

/** One payment recorded against THIS visit (read-only billing-on-sheet;
 *  mirror of the backend VisitPaymentView). */
export interface VisitPaymentView {
  readonly amountMinorUnits: number;
  readonly method: string;
  readonly occurredAt: string;
}

/** Mirror of the backend DoctorConsultContext (doctor-only endpoint). */
/** Mirror of the backend VisitCategoryOption ({code,label} chip grammar --
 *  config vocabulary, never PHI). DOCTOR-JOURNEY-COMPOSE-V1 (SCOPE 3). */
export interface VisitCategoryOptionView {
  readonly code: string;
  readonly label: string;
}

export interface DoctorConsultContext {
  readonly evidenceCaptureConfig?: CaseSheetEvidenceList['config'] | null;
  readonly finishReviewAvailable?: boolean;
  readonly returnRequestAvailable?: boolean;
  readonly completionAvailable?: boolean;
  readonly prescriptionAvailable?: boolean;
  readonly findingOptions?: readonly VisitCategoryOptionView[];
  readonly frequentTreatmentCodes?: readonly string[];
  readonly examinationAvailable?: boolean;
  readonly appointment: ConsultAppointmentFacts;
  readonly patient: ConsultPatientBanner;
  readonly caseSheet: CaseSheetClinicalView | null;
  readonly clinicSpecialties: readonly string[];
  /** DOCTOR-JOURNEY-COMPOSE-V1 (SCOPE 3): the RESOLVED visit-category
   *  vocabularies (clinic specialties when configured, else the
   *  founder-seeded org keys clinical.complaint_categories /
   *  clinical.diagnosis_categories) -- the SAME resolution the case-sheet
   *  write validates against. OPTIONAL in the mirror so an older server
   *  degrades to the legacy clinicSpecialties options; an EMPTY resolved
   *  list renders clean absence (never a raw "Not configured." line). */
  readonly complaintCategoryOptions?: readonly VisitCategoryOptionView[];
  readonly diagnosisCategoryOptions?: readonly VisitCategoryOptionView[];
  /** DAY'S-APPOINTMENT LAW: today's active appointment for the same lead
   *  when it differs from the opened one; null otherwise. */
  readonly todaysAppointmentId: number | null;
  /** Recorded outcome-event tokens (controlled vocabulary, PHI-free). */
  readonly outcomeEvents: readonly string[];
  readonly capabilities: ConsultCheckoutCapabilities;
  /** Payments recorded against THIS visit (non-voided; oldest first). */
  readonly paymentsForVisit: readonly VisitPaymentView[];
  /** The org-configured clinical note-section layout, server-resolved +
   *  availability-filtered (DOCTOR-CLINICAL-ENCOUNTER-FOUNDATION-V1).
   *  OPTIONAL in the mirror so older fixtures/servers degrade to the
   *  legacy three-section layout client-side. */
  readonly noteSections?: readonly CaseSheetNoteSectionKeyView[];
  /** The org-configured follow-up recommendation options ({key,label}). */
  readonly followUpOptions?: readonly CaseSheetFollowUpOptionView[];
  /** True when the 20260728_1 encounter-extension columns are live. */
  readonly encounterSectionsAvailable?: boolean;
  /** D2 visit-grain attribution block (20260815_1; DOCTOR-FLOW-AFTER-INTAKE-
   *  V1). OPTIONAL in the mirror so older fixtures/servers degrade to
   *  "attribution not offered" (the rows render nothing false). Names are
   *  roster admin facts (doctors.name -- the board-wire class). */
  readonly attribution?: {
    readonly available: boolean;
    readonly conversionDoctorId: number | null;
    readonly conversionDoctorName: string | null;
    readonly treatmentDoctorId: number | null;
    readonly treatmentDoctorName: string | null;
    readonly assignedDoctorId: number | null;
    readonly assignedDoctorName: string | null;
  };
  /** The org's active doctor roster for the handoff pick (id/name/clinicId
   *  only; bounded; OPTIONAL in the mirror -- absent = no picker). */
  readonly clinicDoctors?: ReadonlyArray<{
    readonly id: number;
    readonly name: string;
    readonly clinicId: number;
    /** DOCTOR-HANDS-V1 (BUILD 2): doctors.specialty -- the roster staffing
     *  attribute the per-line picker rests on. OPTIONAL in the mirror, so an
     *  older server (or a fixture that predates the field) degrades to
     *  "capability unrecorded", which never hides a doctor. */
    readonly specialty?: string | null;
  }>;
  /** DOCTOR-FINDABILITY-AT-SCALE-V1: true when the org's active roster
   *  exceeded the wire's bound and clinicDoctors is an INCOMPLETE slice --
   *  the picker renders the incompleteness (never a capped list presented
   *  as complete). OPTIONAL in the mirror; absent = not truncated. */
  readonly clinicDoctorsTruncated?: boolean;
  /** DOCTOR-HANDS-V1 (BUILD 2): the procedure -> required-specialty map for
   *  the procedures on THIS sheet's plan (smallest wire). OPTIONAL in the
   *  mirror; absent or empty = nothing mapped = nothing filtered, which is
   *  also what a config read fault degrades to. Never a fence. */
  readonly procedureSpecialties?: Readonly<Record<string, readonly string[]>>;
  /** TOOTH-NOTATION-INTERIM-V1 (doctor-only wire). OPTIONAL in the mirror so
   *  older fixtures/servers degrade to "tooth entry not offered" -- the
   *  odontogram renders ONLY when the reference set is served AND storage is
   *  live (never a diagram that cannot save). */
  readonly toothNotationDisplay?: ToothNotationDisplayView;
  readonly toothReferences?: readonly ToothReferenceView[];
  readonly toothRefsAvailable?: boolean;
  /** DOCTOR-OS-EXPERIENCE-V1 (LEAD-UNTIL-CONSULT ruling): the lead's
   *  registration truth for the snapshot header + the B2 blocked state.
   *  NULL when the appointment carries no unlinked lead. OPTIONAL in the
   *  mirror so older fixtures/servers degrade to "no verdict". */
  readonly registrationState?: ConsultRegistrationState | null;
  /** C6 ALLERGY DURABILITY: patient-durable clinical alert truth (doctor-
   *  only wire). OPTIONAL in the mirror; absent = older server = no band. */
  readonly clinicalProfile?: PatientClinicalProfileView;
  /** False = migration 20260804_2 pending (honest absence). */
  readonly clinicalProfileAvailable?: boolean;
}

/** Mirror of the backend registrationState fragment (doctor consult wire).
 *  Field tokens are the closed registration vocabulary -- never PHI. */
export interface ConsultRegistrationState {
  readonly complete: boolean;
  readonly missingFields: readonly string[];
  readonly capturedAt: string | null;
}

/** Mirror of the backend PatientClinicalProfileView (patient-durable allergy
 *  truth; DOCTOR-ONLY wire -- it rides ONLY the consult context read). */
export interface PatientClinicalProfileView {
  readonly anaesthesiaAllergy: 'yes' | 'no' | null;
  readonly medicationAllergies: string | null;
  readonly medicalAlerts: string | null;
  readonly lastConfirmedAt: string | null;
  readonly lastConfirmedCaseSheetId: number | null;
}

/** PUT /api/patients/:patientId/clinical-profile body (tri-state honest:
 *  an ABSENT key is untouched; null explicitly clears -- audited). */
export interface UpdateClinicalProfileInput {
  readonly caseSheetId: number;
  readonly anaesthesiaAllergy?: 'yes' | 'no' | null;
  readonly medicationAllergies?: string | null;
  readonly medicalAlerts?: string | null;
}

/** PUT /api/patients/:patientId/clinical-profile - the doctor-authored
 *  durable truth update + per-visit confirm in ONE act (DOCTOR-OS-
 *  EXPERIENCE-V1 C6). 409 clinical_profile_unavailable pre-apply. */
export async function updatePatientClinicalProfileApi(
  patientId: string,
  input: UpdateClinicalProfileInput,
): Promise<{
  outcome: 'updated';
  profile: PatientClinicalProfileView;
  changedFields: readonly string[];
}> {
  return portalApiPut(
    `/patients/${encodeURIComponent(patientId)}/clinical-profile`,
    input,
  );
}

/** POST /api/patients/:patientId/clinical-profile/confirm - the per-visit
 *  CONFIRM act (no value changes; the doctor attests the durable truth was
 *  reviewed this visit). */
export async function confirmPatientClinicalProfileApi(
  patientId: string,
  caseSheetId: number,
): Promise<{ outcome: 'confirmed'; profile: PatientClinicalProfileView }> {
  return portalApiPost(
    `/patients/${encodeURIComponent(patientId)}/clinical-profile/confirm`,
    { caseSheetId },
  );
}

/** Mirror of the closed backend section-key vocabulary (lib/domain/clinical/
 *  case-sheet.ts CASE_SHEET_NOTE_SECTION_KEYS). */
export type CaseSheetNoteSectionKeyView =
  | 'chief_complaint'
  | 'medical_dental_history'
  | 'examination_findings'
  | 'diagnosis'
  | 'clinical_notes';

/** Mirror of the backend CaseSheetFollowUpOption (config vocabulary). */
export interface CaseSheetFollowUpOptionView {
  readonly key: string;
  readonly label: string;
}

/** GET /api/doctor/consult-context/:appointmentId - the consult workspace
 *  context (doctor-only; org+clinic fenced; scope-honest 403 copy). */
export async function fetchDoctorConsultContext(
  appointmentId: number,
  expectedBearer?: string,
): Promise<DoctorConsultContext> {
  return portalApiGet<DoctorConsultContext>(
    `/doctor/consult-context/${encodeURIComponent(String(appointmentId))}`,
    expectedBearer,
  );
}

/** Mirror of the backend DoctorQueuePlanSummary: COUNTS + the accepted
 *  estimate sum ONLY -- no procedure names on the drawer wire. Whole INR. */
export interface DoctorQueuePlanSummary {
  readonly advised: number;
  readonly accepted: number;
  readonly declined: number;
  readonly pending: number;
  readonly acceptedValue: number | null;
}

/** Mirror of the backend DoctorQueueLeadContext -- the safe lead-context
 *  EXTRAS block; present ONLY when the org's doctor.context_visibility row
 *  grants appointment_plus_safe_lead_context. Never phone, never
 *  campaign/attribution. */
export interface DoctorQueueLeadContext {
  readonly patientLinked: boolean;
  readonly umr: string | null;
  readonly enquiryChannel: string | null;
  readonly enquiryDate: string | null;
}

/** Mirror of the backend DoctorQueueContext (the doctor-safe drawer read;
 *  CHECKOUT-HANDOFF-AND-DOCTOR-CONTEXT-V1). `leadContext` is structurally
 *  ABSENT at the seeded appointment_only default. */
export interface DoctorQueueContext {
  readonly appointment: {
    readonly id: number;
    readonly appointmentTime: string;
    readonly status: string;
    readonly clinicId: number;
    readonly clinicName: string | null;
    readonly specialty: string | null;
    /** ACTUAL arrival time (patient_arrived occurred_at); null when not
     *  arrived. ARRIVAL TRUTH: rendered as a second fact, never replacing
     *  the scheduled time. */
    readonly arrivedAt: string | null;
  };
  readonly identity: {
    readonly nameLabel: string | null;
    readonly namePending: boolean;
    readonly pendingChannel: string | null;
  };
  readonly visit: {
    readonly visitStatus: 'none' | 'draft' | 'finalized';
    readonly outcomeEvents: readonly string[];
    /** True when a consult_started event exists (rider R-07). */
    readonly consultStarted: boolean;
  };
  readonly plan: DoctorQueuePlanSummary | null;
  readonly payment: {
    readonly paymentRecorded: boolean;
    readonly suggestedCollectAmount: number | null;
  };
  readonly leadContext?: DoctorQueueLeadContext;
}

/** GET /api/doctor/queue-context/:appointmentId - the doctor-safe queue
 *  drawer read (doctor-only; org+clinic fenced; contract fields only). */
export async function fetchDoctorQueueContext(
  appointmentId: number,
): Promise<DoctorQueueContext> {
  return portalApiGet<DoctorQueueContext>(
    `/doctor/queue-context/${encodeURIComponent(String(appointmentId))}`,
  );
}

/** One case-history rail entry (mirror of CaseHistoryEntry; doctor-only
 *  wire -- finalized visits only). */
export interface CaseHistoryEntry {
  readonly caseSheetId: number;
  readonly appointmentId: number;
  readonly clinicId: number;
  readonly createdAt: string;
  readonly chiefComplaint: string | null;
  readonly chiefComplaintCategory: string | null;
  readonly diagnosisCategory: string | null;
  readonly planItemCount: number;
  readonly estimatedTotal: number;
  /** Tier-2 evidence badges (DOCTOR-CLINICAL-EVIDENCE-TIER2-V1): ACTIVE
   *  evidence count per class. PRESENT only when the B4 flag is on --
   *  absence renders NO badge (S10: absent, never "0"). */
  readonly evidenceCounts?: Readonly<Record<string, number>>;
  /** C7 TREATMENT-LEDGER PARITY (DOCTOR-OS-EXPERIENCE-V1): procedures
   *  COMPLETED at this visit + the payments recorded against it (the paper
   *  running-log columns, read-only). The payment row id is the product's
   *  honest receipt identity -- no receipt-number series exists. OPTIONAL
   *  in the mirror so older fixtures/servers degrade to an empty ledger. */
  readonly completedProcedures?: readonly string[];
  readonly payments?: readonly VisitPaymentView[];
  readonly paymentIds?: readonly number[];
  /** PATIENT-360-V1 CONTINUITY (LAKSHMI-2 ruling 2026-08-16): the recorded
   *  findings + note ride the doctor-only wire for the Earlier Visits
   *  full-visit expansion. OPTIONAL in the mirror (older payloads). */
  readonly findingsDiagnosis?: string | null;
  readonly clinicalNote?: string | null;
  /** Saved intake from this finalized visit, with patient-reported provenance.
   *  Absence is interpreted with historyIntakeAvailability, never on its own. */
  readonly intake?: {
    readonly captureStatus?: 'completed' | 'in_progress';
    readonly answers: readonly DoctorPreconsultAnswerViewWire[];
    readonly capturedAt: string | null;
  };
}

/** Mirror of the backend DoctorPreconsultAnswerView (patient-preconsult.ts)
 *  as served inside the lens case-history entries. */
export interface DoctorPreconsultAnswerViewWire {
  readonly questionKey: string;
  readonly prompt: string;
  readonly answerKind: string;
  readonly selectedLabels: readonly string[];
  readonly detailText: string | null;
  readonly safetyFlag: boolean;
  readonly capturedAt: string;
}

/** Mirror of THE ONE continuity-summary contract
 *  (lib/domain/clinical/visit-continuity.ts -- the LAKSHMI-2 owner; the
 *  DOCTOR-INTAKE-HISTORY-V1 consult panel consumes the same shape). */
export interface VisitContinuitySummaryWire {
  readonly lastVisit: {
    readonly caseSheetId: number;
    readonly appointmentId: number;
    readonly visitDateIso: string;
    readonly summaryLine: string;
    readonly complaint: string | null;
    readonly diagnosis: string | null;
    readonly planItemCount: number;
  } | null;
  readonly intakeFacts: {
    readonly allergies: readonly string[];
    readonly medications: readonly string[];
    readonly conditions: readonly string[];
    readonly onsetLabel: string | null;
    readonly severityLabel: string | null;
    readonly provenanceLabel: string;
    readonly capturedAt: string | null;
  } | null;
  readonly treatmentCounts: {
    readonly acceptedItemCount: number;
    readonly completedItemCount: number | null;
  } | null;
  readonly nextPlanned:
    | {
        readonly kind: 'booking';
        readonly scheduledAtIso: string;
        readonly doctorName: string | null;
      }
    | {
        readonly kind: 'intent';
        readonly optionKey: string;
        readonly optionLabel: string | null;
        readonly treatmentDoctorName: string | null;
      }
    | { readonly kind: 'none' };
  readonly changedSince: boolean | null;
}

export interface DoctorCaseHistory {
  readonly entries: readonly CaseHistoryEntry[];
  /** Finalized visits at org clinics outside the caller's scope -- an
   *  honest COUNT only, never content. */
  readonly crossClinicCount: number;
}

/** GET /api/doctor/case-history/:patientId - the patient's past finalized
 *  visits (doctor-only; clinic-fenced; newest first; capped).
 *  Normalized at the fetch boundary (LES-057): a malformed body degrades to
 *  an empty history, never a crashed workspace. */
export async function fetchDoctorCaseHistory(
  patientId: string,
): Promise<DoctorCaseHistory> {
  const wire = await portalApiGet<Partial<DoctorCaseHistory> | null>(
    `/doctor/case-history/${encodeURIComponent(patientId)}`,
  );
  return {
    entries: Array.isArray(wire?.entries) ? wire.entries : [],
    crossClinicCount:
      typeof wire?.crossClinicCount === 'number' ? wire.crossClinicCount : 0,
  };
}

// ---- Treatment consents (DOCTOR-OS-EXPERIENCE-V1 B3) -----------------------
// The consent DOCUMENT engine over the case-sheet spine: list / generate /
// sign-digital / attach-scan / supersede. DOCTOR-ONLY server-side; a signed
// artifact is IMMUTABLE (corrections only by VOID-SUPERSEDE, both rows
// retained). Pre-apply (migration 20260804_2) the list answers
// { available: false } and every write maps to ONE bounded 409
// 'treatment_consents_unavailable'.

/** Mirror of the backend TreatmentConsentState (the four stored tokens). */
export type TreatmentConsentStateView =
  | 'generated'
  | 'signed_digital'
  | 'signed_physical_captured'
  | 'voided_superseded';

/** Mirror of the controlled void/supersede reason vocabulary. */
export type TreatmentConsentVoidReasonView =
  | 'wrong_patient'
  | 'wrong_treatment'
  | 'wording_version_superseded'
  | 'signer_error'
  | 'generated_in_error'
  | 'other';

export type TreatmentConsentSignerTypeView = 'patient' | 'guardian';

/** ONE treatment consent on the doctor wire (mirror of the backend
 *  TreatmentConsentView). Deliberate structural ABSENCES: signature bytes
 *  and actor principals never ride this shape. */
export interface TreatmentConsentView {
  readonly id: number;
  /** Trigger-issued per-clinic document number (e.g. 'CN-000001'). */
  readonly consentNumber: string;
  readonly purpose?: 'consultation' | 'treatment';
  readonly state: TreatmentConsentStateView;
  readonly templateVersion: number | null;
  readonly recordSource?: 'issued' | 'uploaded_original';
  readonly doctorName: string | null;
  readonly signerType: TreatmentConsentSignerTypeView | null;
  readonly signerName: string | null;
  readonly guardianRelationship: string | null;
  readonly witnessName: string | null;
  readonly notes: string | null;
  readonly signedAt: string | null;
  readonly signedEvidenceId: string | null;
  readonly supersededByConsentId: number | null;
  readonly voidReason: TreatmentConsentVoidReasonView | null;
  readonly createdAt: string;
  readonly treatmentPlanItemIds: readonly number[];
}

/** The org's ACTIVE tenant-approved wording (LEGAL TEXT LAW -- rendered
 *  VERBATIM on the print sheet only, never editable in-product). */
export interface ParthaConsentDocumentView {
 readonly format: string;
 readonly sourceSha256: string;
 readonly bodySha256: string;
 readonly logoSha256: string;
 readonly identity: { readonly patientId: string; readonly caseSheetId: number; readonly clinicId: number;
  readonly appointmentId: number; readonly patientName: string; readonly clinicName: string; readonly visitAt: string };
}
export interface ConsentTemplateWireView {
  readonly id: number;
  readonly version: number;
  readonly title: string;
  readonly brandLine: string | null;
  readonly jurisdictionCity: string | null;
  readonly authorisationText: string;
  readonly document?: ParthaConsentDocumentView;
}

/** Mirror of the backend CaseSheetConsentsWire (the consent panel read). */
export interface CaseSheetConsentsWire {
  /** false = migration 20260804_2 not applied yet (honest absence). */
  readonly available: boolean;
  readonly consents: readonly TreatmentConsentView[];
  readonly template: ConsentTemplateWireView | null;
  readonly historicalTemplates?: Readonly<Record<string, ConsentTemplateWireView | null>>;
  readonly captureAvailable?: boolean;
  readonly consultationConsents?: readonly (TreatmentConsentView & { caseSheetId: number; clinicId: number; originalAvailable: boolean })[];
  readonly consultationForm?: ConsentTemplateWireView | null;
  /** The org's consent.required_for_procedure config (seeded TRUE). */
  readonly requiredForProcedure: boolean;
  /** ACCEPTED lines still lacking a SIGNED consent (completion gating). */
  readonly uncoveredAcceptedItemIds: readonly number[];
}

/** GET /api/case-sheets/:id/consents - the visit's consent panel read.
 *  Normalized at the fetch boundary (LES-057): the real wire always carries
 *  every key, but a malformed body must degrade to safe empties, never
 *  crash the workspace. */
export async function fetchCaseSheetConsents(
  caseSheetId: number,
  expectedBearer?: string,
): Promise<CaseSheetConsentsWire> {
  const wire = await portalApiGet<Partial<CaseSheetConsentsWire> | null>(
    `/case-sheets/${encodeURIComponent(String(caseSheetId))}/consents`,
    expectedBearer,
  );
  return {
    available: wire?.available === true && Array.isArray(wire.consents),
    consents: Array.isArray(wire?.consents) ? wire.consents : [],
    template: wire?.template ?? null,
    captureAvailable: wire?.captureAvailable === true,
    consultationConsents: Array.isArray(wire?.consultationConsents) ? wire.consultationConsents : [],
    consultationForm: wire?.consultationForm ?? null,
    historicalTemplates: wire?.historicalTemplates && typeof wire.historicalTemplates === 'object' && !Array.isArray(wire.historicalTemplates) ? wire.historicalTemplates : {},
    requiredForProcedure: wire?.requiredForProcedure !== false,
    uncoveredAcceptedItemIds: Array.isArray(wire?.uncoveredAcceptedItemIds)
      ? wire.uncoveredAcceptedItemIds
      : [],
  };
}

export interface GenerateTreatmentConsentInput {
  readonly treatmentPlanItemIds: readonly number[];
  readonly signerType: TreatmentConsentSignerTypeView;
  readonly signerName?: string;
  readonly guardianRelationship?: string;
  readonly witnessName?: string;
  readonly notes?: string;
}

/** POST /api/case-sheets/:id/consents - generate ONE numbered consent
 *  document for ACCEPTED lines of this visit (B3: declined / pending /
 *  advised / deferred lines never generate). The response carries the
 *  trigger-issued consentNumber. */
export async function generateTreatmentConsentApi(
  caseSheetId: number,
  input: GenerateTreatmentConsentInput,
): Promise<TreatmentConsentView> {
  return portalApiPost<TreatmentConsentView>(
    `/case-sheets/${encodeURIComponent(String(caseSheetId))}/consents`,
    input,
  );
}

/** POST /api/treatment-consents/:id/sign-digital - capture the in-product
 *  signature onto a GENERATED consent (state -> signed_digital; immutable
 *  afterwards). signatureBase64 = bare base64 PNG (data: prefix stripped). */
export async function signTreatmentConsentDigitalApi(
  consentId: number,
  input: {
    readonly signatureBase64: string;
    readonly signerName?: string;
    readonly witnessName?: string;
  },
): Promise<TreatmentConsentView> {
  return portalApiPost<TreatmentConsentView>(
    `/treatment-consents/${encodeURIComponent(String(consentId))}/sign-digital`,
    input,
  );
}

/** POST /api/treatment-consents/:id/attach-scan - link the signed-paper
 *  photo (clinical_evidence class 'consent_scan', same visit) to a
 *  GENERATED consent (state -> signed_physical_captured). */
export async function attachTreatmentConsentScanApi(
  consentId: number,
  evidenceId: string,
  expectedBearer?: string,
): Promise<TreatmentConsentView> {
  return portalApiPost<TreatmentConsentView>(
    `/treatment-consents/${encodeURIComponent(String(consentId))}/attach-scan`,
    { evidenceId },
    expectedBearer,
  );
}

/** POST /api/treatment-consents/:id/supersede - THE correction act
 *  (VOID-SUPERSEDE): a signed artifact is never edited; the successor is
 *  created first, then the old row flips to voided_superseded. An unsigned
 *  draft may be voided WITHOUT a successor (withoutSuccessor: true). */
export async function supersedeTreatmentConsentApi(
  consentId: number,
  input: {
    readonly voidReason: TreatmentConsentVoidReasonView;
    readonly withoutSuccessor?: boolean;
    readonly treatmentPlanItemIds?: readonly number[];
  },
): Promise<{
  oldConsent: TreatmentConsentView;
  newConsent: TreatmentConsentView | null;
}> {
  return portalApiPost(
    `/treatment-consents/${encodeURIComponent(String(consentId))}/supersede`,
    input,
  );
}

// ─── DOCTOR-WORKSPACE-COMPLETION-V1: the doctor lens (a doctor-shaped 360) ──

/** Mirror of the backend DoctorPatientSearchResult (doctor-patient-lens.ts).
 *  Name + UMR only; a contact/phone lookup is refused (contactSearchWithheld
 *  -- gap.doctor-contact-search). Candidates carry NO phone. */
export interface DoctorPatientSearchResult {
  readonly searchMode: 'name' | 'umr' | 'phone';
  readonly count: number;
  readonly candidates: readonly PatientCandidateCard[];
  readonly contactSearchWithheld: boolean;
}

/** GET /api/doctor/patient-search?q=... - the doctor's lawful patient search
 *  (doctor-only; org-scoped; clinic-fenced; name + UMR only). */
export async function fetchDoctorPatientSearch(
  q: string,
): Promise<DoctorPatientSearchResult> {
  return portalApiGet<DoctorPatientSearchResult>(
    `/doctor/patient-search?q=${encodeURIComponent(q)}`,
  );
}

/** Mirror of the backend DoctorLensContact: contact stays masked on the
 *  doctor lens (no reveal action, no raw phone). gap.doctor-masked-contact. */
export interface DoctorLensContactWire {
  readonly state: 'withheld_masked';
  readonly gap: 'doctor-masked-contact';
}

/** Mirror of the backend DoctorLensContinueCare: the server-computed
 *  continue-care door fragment (the check-in gate is UNCHANGED -- a
 *  not-yet-lawful consult renders the honest reason, never a broken button). */
export interface DoctorLensContinueCareWire {
  readonly kind: 'consult' | 'view' | 'blocked' | 'none';
  readonly appointmentId: number | null;
  readonly label:
    | 'Start consult'
    | 'Resume consult'
    | 'View case sheet'
    | null;
  readonly reason: string | null;
}

/** Mirror of the backend Patient360PaymentEvidence (evidence rows; no balance). */
export interface DoctorLensPaymentsWire {
  readonly state: PaymentEvidenceState;
  readonly items: readonly Patient360PaymentItemWire[];
  readonly balance: 'not_available';
}

/** Mirror of the backend Patient360PaymentHistory, doctor-projected: the
 *  supervisor-tier void reason + actor are ABSENT (the doctor is not a
 *  billing supervisor). */
export interface DoctorLensPaymentHistoryWire {
  readonly state: 'has_voided' | 'none' | 'unavailable';
  readonly voided: readonly Patient360VoidedPaymentItemWire[];
}

/** Mirror of the backend DoctorPatientLens (a doctor-shaped Patient 360).
 *  Reuses the Patient 360 wire grammar; adds the doctor-only clinical case
 *  history + the masked-contact + continue-care fragments. */
export interface DoctorPatientLens {
  readonly patient: {
    readonly patientId: string;
    readonly umr: string;
    readonly fullName: string | null;
    readonly gender: 'male' | 'female' | 'other' | 'unknown' | null;
    readonly ageYears: number | null;
    readonly ageIsApproximate: boolean;
    readonly status: string;
    readonly registeredClinicLabel: string | null;
  };
  readonly contact: DoctorLensContactWire;
  readonly todayAppointments: readonly Patient360AppointmentWire[];
  readonly todayFocus: Patient360AppointmentWire | null;
  readonly recentVisits: readonly Patient360AppointmentWire[];
  readonly upcomingAppointments: readonly Patient360AppointmentWire[];
  readonly nextAppointment: Patient360AppointmentWire | null;
  readonly crossClinicVisitCount: number;
  readonly payments: DoctorLensPaymentsWire;
  readonly paymentHistory: DoctorLensPaymentHistoryWire;
  readonly followUp: {
    readonly state: FollowUpEvidenceState;
    readonly scheduledFor: string | null;
  };
  readonly treatmentCheckout: Patient360TreatmentCheckoutWire;
  readonly treatmentJourney: Patient360TreatmentJourneyWire;
  readonly caseHistory: DoctorCaseHistory;
  readonly historyIntakeAvailability?: 'available' | 'unavailable' | 'withheld';
  readonly continueCare: DoctorLensContinueCareWire;
  readonly externalClinicalRecord: 'not_connected';
  /** PATIENT-360-V1 (founder ruling D-360c, 2026-08-16): consent STATUS
   *  tokens from THE consent_records owner -- type + granted-at ONLY.
   *  OPTIONAL so older payloads keep rendering (additive contract). */
  readonly consents?: readonly DoctorLensConsentStatusWire[];
  /** Missing on older payloads: an empty list alone cannot prove absence. */
  readonly consentAvailability?: 'available' | 'unavailable';
  /** PATIENT-360-V1 (D-360e): the patient-narrowed D1 next-visit intent. */
  readonly nextVisitIntent?: PatientNextVisitIntentWire | null;
  /** PATIENT-360-V1 (the #567 D2 columns, live): attribution NAMES only. */
  readonly attribution?: DoctorLensAttributionWire | null;
  /** PATIENT-360-V1 CONTINUITY (LAKSHMI-2): THE ONE continuity summary.
   *  OPTIONAL in the mirror so older payloads keep rendering. */
  readonly continuity?: VisitContinuitySummaryWire;
}

/** Mirror of the backend DoctorLensConsentStatus (doctor-patient-lens.ts). */
export interface DoctorLensConsentStatusWire {
  readonly consentType: string;
  readonly grantedAt: string;
}

/** Mirror of the backend DoctorLensAttribution -- names, never ids. */
export interface DoctorLensAttributionWire {
  readonly conversionDoctorName: string | null;
  readonly treatmentDoctorName: string | null;
}

/** GET /api/doctor/patient-lens/:patientId - the doctor-shaped Patient 360
 *  (doctor-only; org fence == not-found; clinic reachability fence). */
export async function fetchDoctorPatientLens(
  patientId: string,
): Promise<DoctorPatientLens> {
  return portalApiGet<DoctorPatientLens>(
    `/doctor/patient-lens/${encodeURIComponent(patientId)}`,
  );
}

// --- DOCTOR-WORKSPACE-EXPERIENCE-V2: the clinic patients LIST + Recent panel -

/** Mirror of the backend DoctorPatientListItem (doctor-patient-lens.ts). The
 *  doctor-shaped LIST row: identity + care demographics + a last-visit
 *  indicator. NO contact value, ZERO clinical column (CLINICAL WIRE LAW). */
export interface DoctorPatientListItem {
  readonly patientId: string;
  readonly umr: string;
  readonly fullName: string | null;
  readonly gender: 'male' | 'female' | 'other' | 'unknown' | null;
  readonly ageYears: number | null;
  readonly ageIsApproximate: boolean;
  readonly lastVisitAt: string | null;
}

export interface DoctorPatientListResult {
  readonly clinicId: number;
  readonly patients: readonly DoctorPatientListItem[];
  readonly capped: boolean;
}

/** GET /api/doctor/patient-list - the doctor's OWN-clinic patient roster as a
 *  LIST (doctor-only; org + own-clinic fenced; doctor-shaped columns only). */
export async function fetchDoctorPatientList(
  clinicId?: number | null,
): Promise<DoctorPatientListResult> {
  const qs =
    clinicId !== undefined && clinicId !== null
      ? `?clinicId=${encodeURIComponent(String(clinicId))}`
      : '';
  return portalApiGet<DoctorPatientListResult>(`/doctor/patient-list${qs}`);
}

/** Mirror of the backend DoctorRecentPatientItem: a patient recently ACTIVE at
 *  the doctor's clinic(s); same doctor-shaped columns + the last-activity time.
 *  NO contact value, ZERO clinical column. */
export interface DoctorRecentPatientItem {
  readonly patientId: string;
  readonly umr: string;
  readonly fullName: string | null;
  readonly gender: 'male' | 'female' | 'other' | 'unknown' | null;
  readonly ageYears: number | null;
  readonly ageIsApproximate: boolean;
  readonly lastActivityAt: string;
}

export interface DoctorRecentPatientsResult {
  readonly patients: readonly DoctorRecentPatientItem[];
}

/** GET /api/doctor/recent-patients - the latest patients by activity at the
 *  doctor's own clinic(s) (doctor-only; clinic-fenced; rows open the lens). */
export async function fetchDoctorRecentPatients(): Promise<DoctorRecentPatientsResult> {
  return portalApiGet<DoctorRecentPatientsResult>('/doctor/recent-patients');
}

/** POST /api/case-sheets - create a draft case sheet for an appointment. */
export async function createCaseSheet(
  input: CaseSheetCreateInput,
): Promise<CaseSheetClinicalView> {
  return portalApiPost<CaseSheetClinicalView>('/case-sheets', input);
}

/** The by-id read can return any role-safe audience shape; the clinical
 *  view is the only one the doctor surfaces consume (others are narrowed
 *  and rejected honestly at the call site). */
export type CaseSheetReadViewAny =
  | CaseSheetClinicalView
  | { readonly audience: 'operational' | 'billing' | 'analytics' };

/** GET /api/case-sheets/:id - one case sheet, role-safe projection
 *  (DOCTOR-CLINICAL-WORKSPACE-V1: the history rail's read-only open). */
export async function getCaseSheet(
  caseSheetId: number,
): Promise<CaseSheetReadViewAny> {
  return portalApiGet<CaseSheetReadViewAny>(
    `/case-sheets/${encodeURIComponent(String(caseSheetId))}`,
  );
}

/** PUT /api/case-sheets/:id - update clinical fields / status / course link. */
export async function updateCaseSheet(
  caseSheetId: number,
  patch: CaseSheetUpdateInput,
): Promise<CaseSheetClinicalView> {
  return portalApiPut<CaseSheetClinicalView>(
    `/case-sheets/${encodeURIComponent(String(caseSheetId))}`,
    patch,
  );
}

/** POST /api/case-sheets/:id/plan-items - add ONE plan item (a separate row).
 *  Returns the REFRESHED parent view (all items). */
export async function createPlanItem(
  caseSheetId: number,
  input: PlanItemCreateInput,
): Promise<CaseSheetClinicalView> {
  return portalApiPost<CaseSheetClinicalView>(
    `/case-sheets/${encodeURIComponent(String(caseSheetId))}/plan-items`,
    input,
  );
}

/** PUT /api/case-sheets/:id/plan-items/:itemId - edit ONE plan item. Returns
 *  the REFRESHED parent view (all items). */
export interface PlanItemRemovalInput {
  requestId: string;
  expected: { updatedAt: string; procedureName: string; toothArea: string | null;
    estimatedValue: number | null; priorityRank: number; status: PlanItemStatusView;
    declineReason: string | null; performingDoctorId: number | null;
    itemNote: string | null; toothRefs: readonly string[] };
}
/** POST removal returns the fresh active plan; original receipts are retained. */
export async function removePlanItem(caseSheetId: number, itemId: number, input: PlanItemRemovalInput): Promise<CaseSheetClinicalView> {
  return portalApiPost<CaseSheetClinicalView>(
    `/case-sheets/${encodeURIComponent(String(caseSheetId))}/plan-items/${encodeURIComponent(String(itemId))}/remove`, input);
}

export interface ClinicalPlanAcceptanceInput {
  readonly requestId: string;
  readonly owningCaseSheetId: number;
  readonly planItemId: number;
  readonly expectedRevision: 0;
  readonly expected: Omit<PlanItemRemovalInput['expected'], 'status' | 'estimatedValue' | 'declineReason'> & {
    readonly status: 'advised'; readonly estimatedValue: number; readonly declineReason: null;
  };
}
export interface ClinicalPlanAcceptanceFact {
  readonly requestId: string; readonly planItemId: number; readonly owningCaseSheetId: number;
  readonly decisionCaseSheetId: number; readonly financialAppointmentId: number; readonly revision: 1;
  readonly recordedAt: string; readonly actorStaffUserId: number; readonly acceptanceDoctorId: number | null;
  readonly recoveryPending: boolean;
}
export async function listClinicalPlanAcceptances(caseSheetId: number, expectedBearer?:string): Promise<{ available: boolean; caseSheetId: number; facts: readonly ClinicalPlanAcceptanceFact[] }> {
  return portalApiGet(`/case-sheets/${encodeURIComponent(String(caseSheetId))}/plan-acceptances`,expectedBearer);
}
export async function acceptEarlierClinicalPlan(caseSheetId: number, input: ClinicalPlanAcceptanceInput, expectedBearer:string): Promise<{ fact: ClinicalPlanAcceptanceFact; replay: boolean }> {
  return portalApiPost(`/case-sheets/${encodeURIComponent(String(caseSheetId))}/plan-acceptances`, input,expectedBearer);
}

export async function updatePlanItem(
  caseSheetId: number,
  itemId: number,
  patch: PlanItemUpdateInput,
): Promise<CaseSheetClinicalView> {
  return portalApiPut<CaseSheetClinicalView>(
    `/case-sheets/${encodeURIComponent(String(caseSheetId))}/plan-items/${encodeURIComponent(String(itemId))}`,
    patch,
  );
}

/** GET /api/treatment-courses/:id - the LIGHT categorical course view (for the
 *  "Treatment Journey" link display; no create/edit UI in this package). */
export async function getTreatmentCourse(
  courseId: number,
): Promise<TreatmentCourseView> {
  return portalApiGet<TreatmentCourseView>(
    `/treatment-courses/${encodeURIComponent(String(courseId))}`,
  );
}

// ---- Clinical evidence (DOCTOR-CLINICAL-EVIDENCE-TIER2-V1) -----------------
// Every route below is flag-gated server-side (CLINICAL_ATTACHMENTS_V1_
// ENABLED, founder ruling B4): flag OFF answers 503 before auth. Doctor-only.
// List wires carry METADATA only -- bytes ride exclusively the two fenced
// single-item fetches (rendition / full quality), each audited per open.

/** One evidence item's metadata (bytes NEVER ride this shape). */
export interface ClinicalEvidenceItem {
  /** null/absent is legacy or unavailable, not an asserted empty selection. */
  readonly toothRefs?: readonly string[] | null;
  readonly photoPhase?: 'before' | 'after' | null;
  readonly id: string;
  readonly organizationId: number;
  readonly clinicId: number;
  readonly patientId: string;
  readonly caseSheetId: number;
  readonly evidenceClass:
    | 'case_sheet_page'
    | 'intraoral_photo'
    | 'xray'
    | 'consent_scan';
  readonly remark: string | null;
  readonly mimeType: string;
  readonly byteSize: number;
  readonly sha256: string;
  readonly hasRendition: boolean;
  readonly status: 'active' | 'voided';
  readonly voidReason: string | null;
  readonly createdAt: string;
}

export interface CaseSheetEvidenceList {
  readonly items: readonly ClinicalEvidenceItem[];
  /** THE one pending-kinds derivation (feeds the close summary; identical
   *  math to the Doctor Inbox -- one derivation, never two). */
  readonly owedKinds: readonly string[];
  readonly sheetStatus: 'draft' | 'finalized';
  readonly config: {
    readonly kinds: readonly string[];
    readonly captureSources: readonly string[];
    readonly maxBytes: number;
    readonly renditionMaxBytes: number;
    readonly allowedMime: readonly string[];
  };
}

export interface ClinicalEvidenceBytes {
  readonly mimeType: string;
  readonly byteSize: number;
  readonly sha256: string;
  readonly dataBase64: string;
}

export interface DoctorEvidenceInboxItem {
  readonly caseSheetId: number;
  readonly appointmentId: number;
  readonly clinicId: number;
  readonly kind: string;
  readonly sheetStatus: 'draft' | 'finalized';
  readonly visitDate: string;
  /** GOLDEN identity (patients.full_name first -- the name-truth law),
   *  server-resolved through the sheet stamp, else the appointment stamp
   *  (F6d corrective). Null only when no golden record links the visit. */
  readonly patient: {
    readonly id: string;
    readonly name: string | null;
    readonly umr: string | null;
  } | null;
  /** The admin-plane fallback name (appointments.patient_name) when NO
   *  golden record resolved -- the honest last rung before "Patient record
   *  pending" (F6d corrective). Absent on pre-corrective servers. */
  readonly appointmentPatientName?: string | null;
  /** The appointment's STORED status, so the rail can tell a real Resume
   *  door from a futile one (Lane 1 item 1 / D01). Read through THE SHARED
   *  eligibility rule (consult-eligibility.ts), never compared inline.
   *
   *  ABSENT (undefined) on a pre-corrective server -- which is NOT the same
   *  as ineligible, and the rule's tri-state verdict keeps them apart: an
   *  older deploy keeps every door it has today and the server re-fences the
   *  press. NULL means the server looked and the appointment did not resolve
   *  in the clinic fence. */
  readonly appointmentStatus?: string | null;
}

export interface DoctorPendingInvestigation {
  readonly id: string;
  readonly caseSheetId: number;
  readonly choice: 'RVG' | 'IOPA' | 'OPG' | 'CBCT' | 'Other test';
  readonly toothRefs: readonly string[];
  readonly area: string | null;
  readonly orderedAt: string;
  readonly reportState: 'missing' | 'unavailable';
}

export interface DoctorPendingVisit extends Omit<DoctorEvidenceInboxItem, 'kind'> {
  readonly clinicalMissingFields?: readonly string[];
  readonly canAddClinicalContent?: boolean;
  readonly draft: boolean;
  readonly owedEvidenceKinds: readonly string[];
  readonly investigations: readonly DoctorPendingInvestigation[];
  readonly consents?: readonly {readonly caseSheetId:number;readonly purpose:'consultation'|'treatment';readonly treatmentPlanItemIds:readonly number[]}[];
}

export interface DoctorEvidenceInbox {
  readonly clinicalAdditionsAvailable?: boolean;
  readonly todayWorkAvailable?: boolean;
  /** Absent on an older reader: unknown, never an empty complete queue. */
  readonly pendingVisits?: readonly DoctorPendingVisit[];
  readonly pendingVisitTotal?: number | null;
  readonly consentsAvailable?: boolean;
  readonly investigationsAvailable?: boolean;
  readonly reportsAvailable?: boolean;
  readonly items: readonly DoctorEvidenceInboxItem[];
  readonly total: number;
  readonly windowDays: number;
  readonly pendingKinds: readonly string[];
}

/** GET /api/case-sheets/:id/evidence - the visit's evidence metadata +
 *  owed kinds + the org capture config. */
export async function fetchCaseSheetEvidence(
  caseSheetId: number, expectedBearer?: string,
): Promise<CaseSheetEvidenceList> {
  return portalApiGet<CaseSheetEvidenceList>(
    `/case-sheets/${encodeURIComponent(String(caseSheetId))}/evidence`, expectedBearer,
  );
}

export interface AttachEvidenceInput {
  readonly requestId?: string;
  readonly toothRefs?: readonly string[];
  readonly photoPhase?: 'before' | 'after' | null;
  readonly evidenceClass: string;
  readonly imageBase64: string;
  readonly imageMimeType: string;
  readonly renditionBase64?: string;
  readonly remark?: string;
}

/** POST /api/case-sheets/:id/evidence - attach ONE evidence item (the
 *  original full-quality photo + the optional compressed rendition). */
export async function attachCaseSheetEvidenceApi(
  caseSheetId: number,
  input: AttachEvidenceInput,
  expectedBearer?: string,
): Promise<{ evidence: ClinicalEvidenceItem; owedKinds: readonly string[] }> {
  return portalApiPost<{
    evidence: ClinicalEvidenceItem;
    owedKinds: readonly string[];
  }>(
    `/case-sheets/${encodeURIComponent(String(caseSheetId))}/evidence`,
    input,
    expectedBearer,
  );
}

/** GET /api/clinical-evidence/:id/rendition?clinicId=N - the compressed
 *  delivery rendition (thumbnails; audited per open server-side). */
export async function fetchEvidenceRendition(
  evidenceId: string,
  clinicId: number,
): Promise<ClinicalEvidenceBytes> {
  return portalApiGet<ClinicalEvidenceBytes>(
    `/clinical-evidence/${encodeURIComponent(evidenceId)}/rendition?clinicId=${encodeURIComponent(String(clinicId))}`,
  );
}

/** GET /api/clinical-evidence/:id/full?clinicId=N - the FULL-QUALITY
 *  original (tap-to-open; audited per open server-side). */
export async function fetchEvidenceFullQuality(
  evidenceId: string,
  clinicId: number,
): Promise<ClinicalEvidenceBytes> {
  return portalApiGet<ClinicalEvidenceBytes>(
    `/clinical-evidence/${encodeURIComponent(evidenceId)}/full?clinicId=${encodeURIComponent(String(clinicId))}`,
  );
}

/** POST /api/clinical-evidence/:id/void - reason-coded soft void (the ONE
 *  correction path; bytes immutable, the row demotes and stays visible). */
export async function voidClinicalEvidenceApi(
  evidenceId: string,
  input: { readonly clinicId: number; readonly voidReason: string },
): Promise<{ evidence: ClinicalEvidenceItem }> {
  return portalApiPost<{ evidence: ClinicalEvidenceItem }>(
    `/clinical-evidence/${encodeURIComponent(evidenceId)}/void`,
    input,
  );
}

/** GET /api/doctor/evidence-inbox - the DERIVED pending-evidence list
 *  (attach clears by derivation; no stored item, no notification system). */
export async function fetchDoctorEvidenceInbox(expectedBearer?: string): Promise<DoctorEvidenceInbox> {
  return portalApiGet<DoctorEvidenceInbox>('/doctor/evidence-inbox', expectedBearer);
}

// ---- Patient payments (PATIENT-PAYMENTS-REVENUE-SPINE-V1, Epic 9 rung 2) --

/**
 * POST /api/patient-payments/:id/relink - FRONT-DESK-MONEY-AND-DOCUMENTS-V1
 * (PR3, L-07): attach ONE already-committed, UNALLOCATED payment to its
 * visit, so the due the desk reads finally reduces.  [MONEY WRITE]
 *
 * The caller supplies only the appointment the surface is already armed on.
 * Every fence -- same patient, same clinic, same org, not already linked,
 * not voided -- is the SERVER'S, and a refusal arrives as a thrown
 * PortalApiError carrying the product's own message.
 */
export interface PaymentAllocationResult {
  payment: PatientPaymentRow;
  previousAppointmentId: number | null;
  operationId: string;
  auditId: number;
}

export type PaymentAllocationStatus =
  | { outcome: 'unknown'; operationId: string }
  | { outcome: 'committed'; operationId: string; operationKind: 'attach' | 'undo';
      appointmentId: number; auditId: number; currentPayment: PatientPaymentRow };

export function relinkPatientPaymentApi(
  paymentId: number, appointmentId: number, operationId: string, expectedBearer?: string,
): Promise<PaymentAllocationResult> {
  return portalApiPost(`/patient-payments/${encodeURIComponent(String(paymentId))}/relink`,
    { appointmentId, operationId }, expectedBearer);
}

/** Undo only the named attachment; preserve the operation key across recovery. */
export function unlinkPatientPaymentApi(
  paymentId: number, appointmentId: number, operationId: string, expectedBearer?: string,
): Promise<PaymentAllocationResult> {
  return portalApiPost(`/patient-payments/${encodeURIComponent(String(paymentId))}/unlink`,
    { appointmentId, operationId }, expectedBearer);
}

export function fetchPaymentAllocationStatus(
  paymentId: number, operationId: string, expectedBearer?: string,
): Promise<PaymentAllocationStatus> {
  return portalApiGet(`/patient-payments/${encodeURIComponent(String(paymentId))}/allocation-operations/${encodeURIComponent(operationId)}`, expectedBearer);
}

/** GET /api/patient-payments/context - the billing-entry bootstrap read
 *  (fence-respecting clinic options + per-clinic category vocabulary +
 *  the caller's void capability). */
export async function fetchBillingEntryContext(): Promise<BillingEntryContext> {
  return portalApiGet<BillingEntryContext>('/patient-payments/context');
}

/** GET /api/patient-payments/recent?clinicId=N - the operator's
 *  confirmation/void list (PHI-carved: display name only, never a phone). */
export async function fetchRecentPatientPayments(
  clinicId: number,
  paymentId?: number,
): Promise<RecentPatientPaymentsResponse> {
  return portalApiGet<RecentPatientPaymentsResponse>(
    `/patient-payments/recent?clinicId=${encodeURIComponent(String(clinicId))}${paymentId === undefined ? '' : `&paymentId=${encodeURIComponent(String(paymentId))}&limit=1`}`,
  );
}

/** GET /api/patient-payments/recent?patientId= - L-17(b): ONE patient's
 *  receipts, newest first, across the caller's lawful clinic belt. THE SAME
 *  read the clinic list uses, narrowed; the rows are the same committed
 *  patient_payments rows, so a re-print can never restate a transaction. */
export async function fetchPatientReceipts(params: {
  patientId: string;
  clinicId?: number;
  limit?: number;
  /** B1: page offset. The response echoes the window and says hasMore. */
  offset?: number;
  /** CORRECTIVE 2 / B3: narrow to ONE receipt by its id, for the re-print
   *  refresh. The SAME read and therefore the SAME fences -- never a second
   *  route with a second auth model. */
  paymentId?: number;
}): Promise<RecentPatientPaymentsResponse> {
  const q = [`patientId=${encodeURIComponent(params.patientId)}`];
  if (params.clinicId !== undefined) q.push(`clinicId=${encodeURIComponent(String(params.clinicId))}`);
  if (params.paymentId !== undefined) {
    q.push(`paymentId=${encodeURIComponent(String(params.paymentId))}`);
  }
  if (params.limit !== undefined) {
    q.push(`limit=${encodeURIComponent(String(params.limit))}`);
  }
  if (params.offset !== undefined) {
    q.push(`offset=${encodeURIComponent(String(params.offset))}`);
  }
  return portalApiGet<RecentPatientPaymentsResponse>(
    `/patient-payments/recent?${q.join('&')}`,
  );
}

/** POST /api/patient-payments - record ONE collected payment through the
 *  ONE writer. Amounts are INTEGER MINOR UNITS (the rupee input is parsed
 *  by string math in billing-entry-view.ts -- a float never touches money);
 *  requestId makes retries idempotent (replay returns created: false). */
export interface BillCollectionPreview {
  digest: string;
  eligibleBills: readonly {billId:number;dueMinorUnits:number}[];
  allocations: readonly {billId:number;totalMinorUnits:number;paidBeforeMinorUnits:number;amountMinorUnits:number;dueAfterMinorUnits:number}[];
  unpaidBeforeMinorUnits:number; unpaidAfterMinorUnits:number; unappliedCreditMinorUnits:number;
}
export async function previewBillCollection(payload: {
  clinicId:number;patientId:string;amountMinorUnits:number;
  allocationIntent:{mode:'oldest_unpaid'}|{mode:'selected_bill';billId:number};
}): Promise<BillCollectionPreview> {
  const result=await portalApiPost<BillCollectionPreview>('/patient-payments/preview',payload);
  if(!/^[0-9a-f]{32}$/.test(result?.digest) || !Array.isArray(result.eligibleBills) || !Array.isArray(result.allocations) ||
    ![result.unpaidBeforeMinorUnits,result.unpaidAfterMinorUnits,result.unappliedCreditMinorUnits].every(n=>Number.isSafeInteger(n)&&n>=0))throw new Error('Bill allocation could not be verified.');
  return result;
}

export async function recordPatientPaymentApi(payload: {
  allocationIntent?: { mode: 'oldest_unpaid' } | { mode: 'selected_bill'; billId: number };
  previewDigest?: string;
  clinicId: number;
  patientId: string;
  appointmentId?: number | null;
  amountMinorUnits: number;
  method?: string | null;
  category?: string | null;
  /** Optional payment reference token (BILLING-MISSING-DELTA-V1, "Other
   *  Payment Fields") -- an identifier, validated server-side. */
  referenceNumber?: string | null;
  methodSubtype?: string | null;
  methodOtherName?: string | null;
  occurredAt: string;
  requestId: string;
}, expectedBearer?: string): Promise<RecordPatientPaymentResponse> {
  return portalApiPost<RecordPatientPaymentResponse>('/patient-payments', payload, expectedBearer);
}

/** POST /api/patient-payments/:id/void - append-only correction (coc/owner
 *  only). The amount is never edited; void then re-enter. */
export async function voidPatientPaymentApi(
  paymentId: number,
  reason: string,
): Promise<{ payment: PatientPaymentRow; operationId: string; auditId: number }> {
  return postPaymentVoid(paymentId,reason);
}

// ---- Itemized appointment bills (BILLING-MISSING-DELTA-V1) ----------------
// The bill delta over the flat-payment spine: THE ONE bill writer/reader
// (lib/patient-bills.ts) behind /api/patient-bills. Collections stay on the
// payment routes above -- a bill never records money. Pre-apply of
// 20260830_1 every call surfaces the honest 409 bill_store_not_available.

/** POST /api/patient-bills - mint a DRAFT bill for one appointment, seeded
 *  from the visit's accepted plan items (snapshot copy; the doctor plan is
 *  never written). */
export async function createPatientBillApi(payload: {
  clinicId: number;
  appointmentId: number;
  requestId: string;
}): Promise<CreatePatientBillResponseMirror> {
  return postBillMutation<CreatePatientBillResponseMirror>(
    '/patient-bills',
    {clinicId:payload.clinicId,appointmentId:payload.appointmentId},
  );
}

/** GET /api/patient-bills?appointmentId= / ?clinicId= - bills newest-first
 *  inside the caller's lawful clinic belt. */
export type ConsultationBillState = { readonly state: 'unavailable' } | {
  readonly state: 'ready'; readonly managedItemId: number | null; readonly feeRecorded: boolean;
  readonly waived: boolean; readonly reversed: boolean; readonly originalActor: boolean;
  readonly canRecordFee: boolean; readonly canWaive: boolean; readonly explanation: string;
};

export async function fetchPatientBills(params: {
  billId?: number;
  appointmentId?: number;
  /** L-17(b): every bill for ONE patient across their visits, newest first. */
  patientId?: string;
  clinicId?: number;
  limit?: number;
  /** B1: page offset. The response echoes the window and says hasMore. */
  offset?: number;
}): Promise<PatientBillListResponseMirror & { readonly consultationByBill?: Readonly<Record<string, ConsultationBillState>> }> {
  const q: string[] = [];
  if (params.billId !== undefined) q.push(`billId=${encodeURIComponent(String(params.billId))}`);
  if (params.appointmentId !== undefined) {
    q.push(`appointmentId=${encodeURIComponent(String(params.appointmentId))}`);
  }
  if (params.patientId !== undefined) {
    q.push(`patientId=${encodeURIComponent(params.patientId)}`);
  }
  if (params.clinicId !== undefined) {
    q.push(`clinicId=${encodeURIComponent(String(params.clinicId))}`);
  }
  if (params.limit !== undefined) {
    q.push(`limit=${encodeURIComponent(String(params.limit))}`);
  }
  if (params.offset !== undefined) {
    q.push(`offset=${encodeURIComponent(String(params.offset))}`);
  }
  return portalApiGet<PatientBillListResponseMirror>(
    `/patient-bills${q.length > 0 ? `?${q.join('&')}` : ''}`,
  );
}

export interface BillingDocumentActivity {
  events: Array<{ action: string; at: string; actor: string }>;
  hasMore: boolean;
}

/** Read audit details only when requested in the app; never on the paper. */
export function fetchBillingDocumentActivity(input: {
  kind: 'bill' | 'payment'; documentId: number; clinicId: number;
}): Promise<BillingDocumentActivity> {
  const query = new URLSearchParams({ kind: input.kind, documentId: String(input.documentId), clinicId: String(input.clinicId) });
  return portalApiGet<BillingDocumentActivity>(`/billing/print-events?${query}`);
}

export async function recordBillingPrintRequest(payload: {
  kind: 'bill' | 'payment'; documentId: number; clinicId: number;
}): Promise<{ recorded: true }> {
  const result = await portalApiPost<{ recorded: true }>('/billing/print-events', payload);
  if (result.recorded !== true) throw new Error('Print request was not recorded');
  return result;
}

export async function fetchPatientBill(
  billId: number,
): Promise<PatientBillResponseMirror> {
  return portalApiGet<PatientBillResponseMirror>(
    `/patient-bills/${encodeURIComponent(String(billId))}`,
  );
}

/** POST /api/patient-bills/:id/items - add one line (draft only). */
export async function addPatientBillItemApi(
  billId: number,
  payload: {
    treatmentName: string;
    performingDoctorId?: number | null;
    unitPriceMinorUnits?: number | null;
    quantity?: number;
  },
): Promise<PatientBillResponseMirror> {
  return postBillMutation<PatientBillResponseMirror>(
    `/patient-bills/${encodeURIComponent(String(billId))}/items`,
    payload,
  );
}

/** POST /api/patient-bills/:id/items/:itemId/update - edit one line's
 *  price / units / waive / doctor / name (draft only). */
export async function updatePatientBillItemApi(
  billId: number,
  itemId: number,
  payload: {
    treatmentName?: string;
    performingDoctorId?: number | null;
    unitPriceMinorUnits?: number | null;
    quantity?: number;
    waivedOff?: boolean;
  },
): Promise<PatientBillResponseMirror> {
  return postBillMutation<PatientBillResponseMirror>(
    `/patient-bills/${encodeURIComponent(String(billId))}/items/${encodeURIComponent(String(itemId))}/update`,
    payload,
  );
}

/** POST /api/patient-bills/:id/items/:itemId/delete - remove one line
 *  (draft only; a finalized bill is frozen). */
export async function deletePatientBillItemApi(
  billId: number,
  itemId: number,
): Promise<PatientBillResponseMirror> {
  return postBillMutation<PatientBillResponseMirror>(
    `/patient-bills/${encodeURIComponent(String(billId))}/items/${encodeURIComponent(String(itemId))}/delete`,
    {},
  );
}

/** POST /api/patient-bills/:id/gst - record/clear the bill's GST amount
 *  (an operator-entered amount; no rate is ever computed). */
export async function setPatientBillGstApi(
  billId: number,
  gstMinorUnits: number | null,
): Promise<PatientBillResponseMirror> {
  return postBillMutation<PatientBillResponseMirror>(
    `/patient-bills/${encodeURIComponent(String(billId))}/gst`,
    { gstMinorUnits },
  );
}

/** POST /api/patient-bills/:id/finalize - freeze the bill (every non-waived
 *  line must carry a price). */
export async function savePatientBillDraftApi(billId:number, payload:Record<string,unknown>):Promise<PatientBillResponseMirror> {
  return postBillMutation<PatientBillResponseMirror>(`/patient-bills/${billId}/save`,payload);
}

export async function finalizePatientBillApi(
  billId: number,
): Promise<PatientBillResponseMirror> {
  return postBillMutation<PatientBillResponseMirror>(
    `/patient-bills/${encodeURIComponent(String(billId))}/finalize`,
    {},
  );
}

/** POST /api/patient-bills/:id/cancel - the void posture: supervisory on a
 *  finalized bill; closed reason vocabulary; amounts frozen forever. */
export async function cancelPatientBillApi(
  billId: number,
  reason: string,
  expectedStatus: 'draft' | 'finalized' = 'finalized',
): Promise<PatientBillResponseMirror> {
  return postBillMutation<PatientBillResponseMirror>(
    `/patient-bills/${encodeURIComponent(String(billId))}/cancel`,
    { reason, expectedStatus },
  );
}

/** GET /api/patient-bills/doctor-options?clinicId= - the line-doctor picker
 *  roster (active doctors at the clinic; id + name only). */
export async function fetchBillDoctorOptions(
  clinicId: number,
): Promise<{ doctors: readonly BillDoctorOption[] }> {
  return portalApiGet<{ doctors: readonly BillDoctorOption[] }>(
    `/patient-bills/doctor-options?clinicId=${encodeURIComponent(String(clinicId))}`,
  );
}

/** GET /api/billing-queue - the cross-day accepted-but-uncollected billing
 *  queue (MONEY-COMPLETION-V1, V3-P3; the #252 per-episode netting). Role +
 *  clinic fenced server-side; the optional appointmentId narrows to ONE
 *  episode (the convert-moment card read, honest state regardless of queue
 *  membership). */
export async function fetchBillingQueue(params: {
  clinicId?: number | 'all';
  appointmentId?: number;
  /** RECEPTION-OS-BUILD-V1 (R-Q04): the Patient 360 financial view's
   *  patient narrowing over the SAME queue read. */
  patientId?: string;
}): Promise<BillingQueueResponse> {
  const q: string[] = [];
  if (params.clinicId !== undefined) {
    q.push(`clinicId=${encodeURIComponent(String(params.clinicId))}`);
  }
  if (params.appointmentId !== undefined) {
    q.push(`appointmentId=${encodeURIComponent(String(params.appointmentId))}`);
  }
  if (params.patientId !== undefined) {
    q.push(`patientId=${encodeURIComponent(params.patientId)}`);
  }
  return portalApiGet<BillingQueueResponse>(
    `/billing-queue${q.length > 0 ? `?${q.join('&')}` : ''}`,
  );
}

/** GET /api/checkout/handoff?appointmentId=N - the receptionist checkout
 *  summary for ONE episode (TREATMENT-ESTIMATE-ACCEPTANCE-AND-PATIENT-
 *  ACCOUNT-SPINE-V1): decision counts, honest estimate summary, discount
 *  evidence, payment evidence (accountBalance ALWAYS 'not_available'),
 *  next-appointment / follow-up state, and the EXACTLY-ONE primary action.
 *  Role + org/clinic fenced server-side (the billing-queue audience). */
export async function fetchCheckoutHandoff(
  appointmentId: number,
): Promise<CheckoutHandoffResponse> {
  return portalApiGet<CheckoutHandoffResponse>(
    `/checkout/handoff?appointmentId=${encodeURIComponent(String(appointmentId))}`,
  );
}

/** POST /api/case-sheets/:id/estimate-discount - record an approved
 *  discount (T-08). Authority is config-consulted server-side (management
 *  default UNION discount.approval_extra_roles); the reason is a CONTROLLED
 *  token from discount.reason_codes; requestId is the single-use
 *  idempotency key (the payments posture). */
export async function recordCaseSheetDiscount(
  caseSheetId: number,
  input: {
    discountKind: 'percent' | 'fixed';
    discountPercent?: number;
    discountAmountInr?: number;
    reasonCode: string;
    requestId: string;
  },
): Promise<{ discount: TreatmentDiscountWireMirror; created: boolean }> {
  return portalApiPost<{ discount: TreatmentDiscountWireMirror; created: boolean }>(
    `/case-sheets/${encodeURIComponent(String(caseSheetId))}/estimate-discount`,
    input,
  );
}

/** POST /api/treatment-discounts/:id/void - append-only discount
 *  correction (void first, then record the corrected discount). */
export async function voidTreatmentDiscountApi(
  discountId: number,
  reason: 'entry_error' | 'superseded' | 'other',
): Promise<{ discount: TreatmentDiscountWireMirror }> {
  return portalApiPost<{ discount: TreatmentDiscountWireMirror }>(
    `/treatment-discounts/${encodeURIComponent(String(discountId))}/void`,
    { reason },
  );
}

// ---- Test-clinic journey (ADORACARE-VISIT-CASE-SPINE-V1) -------------------
//
// Typed client for the ONE journey surface: lead -> patient -> visit/case ->
// treatment -> collected payment. These types MIRROR the backend wire
// (api/routes/journey.ts) exactly - hand-written, since the frontend never
// imports backend lib. The caseSheet field is the EXISTING role-safe
// case-sheet projection (four audiences); the journey mirrors all four so a
// non-doctor role renders its own honest, narrative-free shape.

/** Mirror of the backend CaseSheetOperationalView (COC). No narrative. */
export interface CaseSheetOperationalViewMirror {
  readonly audience: 'operational';
  readonly id: number;
  readonly appointmentId: number;
  readonly treatmentCourseId: number | null;
  readonly chiefComplaintCategory?: string | null;
  readonly diagnosisCategory?: string | null;
  readonly status: CaseSheetStatusView;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly declinedItemCount: number;
  readonly planItems: readonly {
    readonly id: number;
    readonly procedureName: string;
    readonly estimatedValue: number | null;
    readonly priorityRank: number;
    readonly status: PlanItemStatusView;
    readonly declineReason: string | null;
  }[];
}

/** Mirror of the backend CaseSheetBillingView (receptionist). */
export interface CaseSheetBillingViewMirror {
  readonly audience: 'billing';
  readonly id: number;
  readonly appointmentId: number;
  readonly status: CaseSheetStatusView;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly totalEstimatedValue: number;
  readonly planItems: readonly {
    readonly id: number;
    readonly procedureName: string;
    readonly estimatedValue: number | null;
    readonly status: PlanItemStatusView;
  }[];
}

/** Mirror of the backend CaseSheetAnalyticsView (owner / gm_agm): counts
 *  only - no narrative, no categories, no per-item rows. */
export interface CaseSheetAnalyticsViewMirror {
  readonly audience: 'analytics';
  readonly id: number;
  readonly appointmentId: number;
  readonly status: CaseSheetStatusView;
  readonly itemCount: number;
  readonly advisedCount: number;
  readonly acceptedCount: number;
  readonly declinedCount: number;
  readonly pendingCount: number;
  readonly totalEstimatedValue: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** The role-safe case-sheet view as the journey context carries it. */
export type JourneyCaseSheetView =
  | CaseSheetClinicalView
  | CaseSheetOperationalViewMirror
  | CaseSheetBillingViewMirror
  | CaseSheetAnalyticsViewMirror;

export interface JourneyLeadSummary {
  readonly leadId: number;
  readonly name: string | null;
  readonly clinicId: number | null;
  readonly clinicLabel: string | null;
  readonly sourceNormalized: string | null;
  readonly requirementBucket: string | null;
  readonly stage: string | null;
  readonly createdAt: string;
}

export interface JourneyConversionState {
  readonly contactConverted: boolean;
  readonly patientId: string | null;
}

export interface JourneyPatientSummary {
  readonly patientId: string;
  readonly umr: string | null;
  readonly fullName: string | null;
}

export interface JourneyAppointmentSummary {
  readonly appointmentId: number;
  readonly appointmentTime: string;
  readonly status: string;
  readonly clinicId: number;
}

export interface JourneyPaymentsSummary {
  readonly count: number;
  readonly totalMinorUnits: number;
  readonly lastPaymentAt: string | null;
  readonly scanComplete: boolean;
}

export interface JourneyLeadContext {
  readonly lead: JourneyLeadSummary;
  readonly conversion: JourneyConversionState;
  readonly patient: JourneyPatientSummary | null;
  readonly appointments: readonly JourneyAppointmentSummary[];
  readonly currentAppointmentId: number | null;
  readonly caseSheet: JourneyCaseSheetView | null;
  readonly payments: JourneyPaymentsSummary | null;
  /** Per-clinic category vocabulary (empty = none configured; honest). */
  readonly categoryOptions: readonly string[];
}

export interface JourneyPatientLinkResult {
  readonly milestoneCommitted: boolean;
  readonly patientIdStamped: boolean;
  /** Server outcome token. 'person_confirmation_required' is the DERIVED
   *  confirmation state (zero writes) the door answers with when no
   *  patientChoice was sent. */
  readonly reason: string;
  /** The people this lead's phone could belong to -- present only on the
   *  confirmation state. The SAME candidate card the convert door serves. */
  readonly candidates?: readonly PatientConvertCandidateView[];
}

/** GET /api/journey/leads/:leadId - the journey context for one lead. */
export async function fetchJourneyLeadContext(
  leadId: number,
): Promise<JourneyLeadContext> {
  return portalApiGet<JourneyLeadContext>(
    `/journey/leads/${encodeURIComponent(String(leadId))}`,
  );
}

/** POST /api/journey/leads/:leadId/patient-link - the operator-confirmed
 *  identity link (delegates to THE ONE conversion writer server-side).
 *
 *  JOURNEY-ATTACH-CANDIDACY-FENCE-V1: same call shape as the convert door
 *  (#406) -- omit patientChoice to SEE who this lead's number could belong
 *  to, send one to act. The server re-resolves those candidates and refuses
 *  any patientId it did not surface ('patient_not_a_candidate'), so this
 *  client is convenience, never the control. 'create_new' is not offered
 *  here: this door links an existing record and never mints one. */
export async function postJourneyPatientLinkApi(
  leadId: number,
  patientChoice?: { readonly action: 'link'; readonly patientId: string },
): Promise<JourneyPatientLinkResult> {
  return portalApiPost<JourneyPatientLinkResult>(
    `/journey/leads/${encodeURIComponent(String(leadId))}/patient-link`,
    patientChoice !== undefined ? { patientChoice } : {},
  );
}

// ---- Front-desk accounts (ACCOUNTS-PHASE-1-BUILD-V1) -----------------------

/** GET /api/accounts/workspace - THE ONE money workspace read
 *  (MONEY-DAY-CASH-TRUTH-V1; the founder workspace ruling R1-R7). Summary
 *  band + the selected view's rows for a From/To range, from one server
 *  assembly, so the band, the table and any export agree by construction.
 *  Role + org/clinic fenced server-side; patient names are absent unless
 *  the caller's role holds the configured grant. */
export async function fetchMoneyWorkspace(params: {
  clinicId: number;
  from?: string;
  to?: string;
  view?: string;
  filter?: string;
}): Promise<MoneyWorkspaceWire> {
  const q = [`clinicId=${encodeURIComponent(String(params.clinicId))}`];
  if (params.from) q.push(`from=${encodeURIComponent(params.from)}`);
  if (params.to) q.push(`to=${encodeURIComponent(params.to)}`);
  if (params.view) q.push(`view=${encodeURIComponent(params.view)}`);
  if (params.filter) q.push(`filter=${encodeURIComponent(params.filter)}`);
  return portalApiGet<MoneyWorkspaceWire>(`/accounts/workspace?${q.join('&')}`);
}

/** The governed CSV export path (A4/R6). Exported for tests so the URL
 *  contract is pinned without a network call. */
export function moneyExportPath(params: {
  clinicId: number;
  from: string;
  to: string;
  view: string;
  filter: string;
}): string {
  const q = [
    `clinicId=${encodeURIComponent(String(params.clinicId))}`,
    `from=${encodeURIComponent(params.from)}`,
    `to=${encodeURIComponent(params.to)}`,
    `view=${encodeURIComponent(params.view)}`,
    `filter=${encodeURIComponent(params.filter)}`,
  ];
  return `/accounts/export?${q.join('&')}`;
}

/** GET /api/accounts/export - the governed CSV download. The SERVER
 *  enforces the configured export role and writes the audit row before a
 *  byte is produced; this client only asks. A role without the privilege
 *  gets the same denial here as it does hitting the URL by hand -- the
 *  hidden button is never the fence. */
export async function downloadMoneyExport(params: {
  clinicId: number;
  from: string;
  to: string;
  view: string;
  filter: string;
}): Promise<{ blob: Blob; filename: string }> {
  return portalApiDownload(moneyExportPath(params));
}


/** GET /api/accounts/day?clinicId=N&date=YYYY-MM-DD - the ONE money
 *  workspace read (cash-in-drawer derivation + entries + tasks + sheet
 *  config). Role + org/clinic fenced server-side; collections rows are
 *  payments-plane reads (display name only, never a phone). */
export async function fetchMoneyDay(
  clinicId: number,
  date?: string,
): Promise<MoneyDayWire> {
  const q = [`clinicId=${encodeURIComponent(String(clinicId))}`];
  if (date !== undefined && date !== '') {
    q.push(`date=${encodeURIComponent(date)}`);
  }
  return portalApiGet<MoneyDayWire>(`/accounts/day?${q.join('&')}`);
}

/** POST /api/accounts/entries - record spent | petty cash through THE ONE
 *  drawer writer. Amounts are INTEGER MINOR UNITS; requestId makes retries
 *  idempotent. The voucher photo rides base64 through the FD-4 money-proof
 *  fences; without photo AND reference the C16 approver id is REQUIRED
 *  (server-enforced -- this client is convenience, never the control). */
export async function recordMoneyEntryApi(payload: {
  comment?: string | null;
  clinicId: number;
  /** MONEY-DAY-CASH-TRUTH-V1 C3: 'bank_deposit' is a drawer outflow that
   *  is NOT an expense -- it feeds the movement-out term and never the
   *  spent figure. It carries no category (bank rides paidTo, transaction
   *  reference rides proofReferenceNumber, remark rides particulars). */
  kind: 'spent' | 'petty_cash' | 'bank_deposit';
  category?: string | null;
  particulars: string;
  paidTo?: string | null;
  amountMinorUnits: number;
  proofReferenceNumber?: string | null;
  proofImageBase64?: string | null;
  proofImageMimeType?: string | null;
  noProofApprovedByUserRoleId?: number | null;
  requestId: string;
  businessDate?: string;
}): Promise<{ entry: MoneyDrawerEntryWire; created: boolean }> {
  return postDrawer<{ entry: MoneyDrawerEntryWire; created: boolean }>(
    '/accounts/entries',
    payload,
  );
}

/** POST /api/accounts/entries/:id/reverse - the FD-1 reversing entry
 *  (posts on the CURRENT day; the original row is never touched). */
export async function reverseMoneyEntryApi(
  entryId: number,
  payload: { clinicId: number; requestId: string },
): Promise<{ entry: MoneyDrawerEntryWire; created: boolean }> {
  return postDrawer<{ entry: MoneyDrawerEntryWire; created: boolean }>(
    `/accounts/entries/${encodeURIComponent(String(entryId))}/reverse`,
    payload,
  );
}

/** POST /api/accounts/entries/:id/proof - attach the promised photo
 *  (clears the "No photo yet" flag; CAS-guarded). */
export async function attachMoneyEntryProofApi(
  entryId: number,
  payload: { clinicId: number; imageBase64: string; imageMimeType: string; requestId?: string },
): Promise<{ entry: MoneyDrawerEntryWire }> {
  return postDrawer<{ entry: MoneyDrawerEntryWire }>(
    `/accounts/entries/${encodeURIComponent(String(entryId))}/proof`,
    payload,
  );
}

/** GET /api/accounts/entries/:id/proof-image - the fenced money-proof
 *  fetch (the ONLY read that carries image bytes). */
export async function fetchMoneyEntryProofImage(
  entryId: number,
  clinicId: number,
): Promise<{
  mimeType: string;
  byteSize: number;
  sha256: string;
  dataBase64: string;
}> {
  return portalApiGet<{
    mimeType: string;
    byteSize: number;
    sha256: string;
    dataBase64: string;
  }>(
    `/accounts/entries/${encodeURIComponent(String(entryId))}/proof-image?clinicId=${encodeURIComponent(String(clinicId))}`,
  );
}

/** POST /api/accounts/day/first-count - the labeled day-1 chain seed
 *  (configured supervisory roles only; refused once a chain exists). */
export async function recordMoneyFirstCountApi(payload: {
  clinicId: number;
  countedCashMinorUnits: number;
  requestId: string;
}): Promise<{ businessDate: string; dayStartMinorUnits: number; created: boolean }> {
  return portalApiPost<{
    businessDate: string;
    dayStartMinorUnits: number;
    created: boolean;
  }>('/accounts/day/first-count', payload);
}

/** POST /api/accounts/day/close - the acknowledge close (WF-9): ONE typed
 *  count; a difference REQUIRES the configured reason + own words; the
 *  verifier doctor rides config C7 (server-enforced). */
export async function closeMoneyDayApi(payload: {
  clinicId: number;
  businessDate: string;
  countedCashMinorUnits: number;
  differenceReasonCategory?: string | null;
  differenceReasonText?: string | null;
  verifiedByDoctorId?: number | null;
  requestId: string;
}): Promise<{
  closedDay: MoneyClosedDayWire;
  businessDate: string;
  created: boolean;
}> {
  return portalApiPost<{
    closedDay: MoneyClosedDayWire;
    businessDate: string;
    created: boolean;
  }>('/accounts/day/close', payload);
}

/**
 * OMNICHANNEL-TIMELINE-AND-LEAD-360-V1 -- the windowed Lead 360 activity
 * timeline read (GET /api/leads/:leadId/timeline). Entries NEWEST-FIRST;
 * offset/limit windowed with the total disclosed (never a silent cap).
 * The wire reuses the Lead360Event contract (+ note view events).
 */
export interface LeadTimelineResponse {
  readonly leadId: number;
  readonly entries: readonly Lead360Event[];
  readonly totalCount: number;
  readonly offset: number;
  readonly limit: number;
  readonly hasMore: boolean;
  readonly eventStreamStatus: 'available' | 'not_wired';
  readonly notesStreamStatus: 'available' | 'unavailable';
}

export async function fetchLeadTimeline(
  safeLeadId: string,
  window?: { offset?: number; limit?: number },
): Promise<LeadTimelineResponse> {
  const params = new URLSearchParams();
  if (window?.offset !== undefined) params.set('offset', String(window.offset));
  if (window?.limit !== undefined) params.set('limit', String(window.limit));
  const qs = params.toString();
  return portalApiGet<LeadTimelineResponse>(
    `/leads/${encodeURIComponent(safeLeadId)}/timeline${qs === '' ? '' : `?${qs}`}`,
  );
}

/** The composer vocabulary (config-backed activity types; fail-safe seeded
 *  server-side -- the disposition-options precedent). */
export interface LeadTimelineOptionsResponse {
  readonly activityTypes: ReadonlyArray<{
    readonly id: string;
    readonly label: string;
    readonly enabled: boolean;
  }>;
}

export async function fetchLeadTimelineOptions(): Promise<LeadTimelineOptionsResponse> {
  return portalApiGet<LeadTimelineOptionsResponse>('/leads/timeline-options');
}

/** ADD A NOTE (POST /api/leads/:leadId/activities) -- idempotent via the
 *  client-minted key; append-only; audited server-side. */
export interface LeadActivitySaveResponse {
  readonly ok: true;
  readonly activity: {
    readonly id: number;
    readonly leadId: number;
    readonly activityType: string;
    readonly body: string;
    readonly actorRole: string;
    readonly occurredAt: string;
    readonly voided: boolean;
    readonly duplicateOfRetry: boolean;
  };
}

export async function saveLeadNote(
  safeLeadId: string,
  body: { body: string; idempotencyKey: string; activityTypeId?: string },
): Promise<LeadActivitySaveResponse> {
  return portalApiPost<LeadActivitySaveResponse>(
    `/leads/${encodeURIComponent(safeLeadId)}/activities`,
    body,
  );
}

/** Void-supersede stamp (no hard edits; the voided note stays visible). */
export interface LeadActivityVoidResponse {
  readonly ok: true;
  readonly activity: {
    readonly id: number;
    readonly leadId: number;
    readonly voided: true;
    readonly voidedAt: string | null;
    readonly voidReason: string | null;
    readonly alreadyVoided: boolean;
  };
}

export async function voidLeadNote(
  safeLeadId: string,
  activityId: number,
  reason?: string,
): Promise<LeadActivityVoidResponse> {
  return portalApiPost<LeadActivityVoidResponse>(
    `/leads/${encodeURIComponent(safeLeadId)}/activities/${encodeURIComponent(String(activityId))}/void`,
    reason !== undefined ? { reason } : {},
  );
}

export async function fetchPatientOperationalTreatments(
  patientId: string,
): Promise<import('./types').OperationalTreatmentsResult> {
  const result = await portalApiGet<unknown>(
    `/patient-master/patients/${encodeURIComponent(patientId)}/operational-treatments`,
  );
  // A successful HTTP response is not proof of this endpoint's contract.
  // Malformed data is unavailable, never evidence of an empty treatment history.
  if (
    result === null || typeof result !== 'object' ||
    !('availability' in result) ||
    (result.availability !== 'available' && result.availability !== 'unavailable') ||
    !('rows' in result) || !Array.isArray(result.rows) ||
    !('truncated' in result) || typeof result.truncated !== 'boolean'
  ) {
    return { availability: 'unavailable', rows: [], truncated: false };
  }
  return result as import('./types').OperationalTreatmentsResult;
}

/** W2: same session-authorized report endpoint; the legacy day client is unchanged. */
export async function fetchCallcenterPeriodReport(params: import('./callcenter/daily-report/callcenter-daily-report-view').PeriodReportQuery) {
  const { reportQueryString } = await import('./callcenter/daily-report/callcenter-daily-report-view');
  const wire = await portalApiGet<import('./callcenter/daily-report/callcenter-daily-report-view').PeriodReportView>(`/callcenter/daily-report?${reportQueryString(params)}`);
  if (wire.reportKind !== 'period') throw new Error('Period report unavailable');
  return wire;
}
export async function downloadCallcenterPeriodReport(params: import('./callcenter/daily-report/callcenter-daily-report-view').PeriodReportQuery) {
  const { reportQueryString } = await import('./callcenter/daily-report/callcenter-daily-report-view');
  return portalApiDownload(`/callcenter/daily-report?${reportQueryString(params)}&format=xlsx`);
}
export async function fetchCallcenterPeriodDetails(params: import('./callcenter/daily-report/callcenter-daily-report-view').PeriodReportQuery, metric: import('./callcenter/daily-report/callcenter-daily-report-view').PeriodMetric, cursor?: string) {
  const { reportQueryString } = await import('./callcenter/daily-report/callcenter-daily-report-view');
  return portalApiGet<import('./callcenter/daily-report/callcenter-daily-report-view').PeriodDetailView>(`/callcenter/daily-report?${reportQueryString(params)}&metric=${metric}&limit=50${cursor ? '&cursor='+encodeURIComponent(cursor) : ''}`);
}

/** Explicit R19 patient booking extension; legacy/Doctor booking calls are unchanged. */
export interface PatientBookingSelection {
  durationMinutes?: number;
  careAppointmentId?: number;
  careCaseSheetId?: number;
  originWalkinId?: number;
  bookingRequestId: string;
  doctorId: number | null;
  visitType: string | null;
  bookingChannel: string | null;
  serviceCode: string | null;
  department: string | null;
}
export interface PatientBookingOptions {
  patientContext?: 'new' | 'returning' | null;
  patientId: string; clinicId: number; defaultDoctorId: number | null;
  doctors: Array<{id: number; name: string}>;
  treatments: Array<{serviceCode: string; serviceName: string; serviceGroup: string}>;
}
export interface PatientBookingDetails {
  durationMinutes?: number | null;
  appointmentTime?: string; updatedAt?: string;
  followUp?: {careId: number; label: string} | null;
  appointmentId: number; patientId: string | null; clinicId: number; doctorId: number | null;
  visitType: string | null; bookingChannel: string | null; bookingSource?: string | null;
  serviceCode: string | null; serviceName: string | null; department: string | null;
  descriptorReceiptRecorded: boolean;
}
export function fetchPatientBookingOptions(patientId: string, clinicId: number): Promise<PatientBookingOptions> {
  return portalApiGet(`/appointments/booking-treatments?patientId=${encodeURIComponent(patientId)}&clinicId=${clinicId}`);
}
export function fetchPatientBookingDetails(appointmentId: number): Promise<PatientBookingDetails> {
  return portalApiGet(`/appointments/${appointmentId}/booking-details`);
}

/** Only an opened exact receipt authorizes this audited billing contact read. */
export function openPatientPaymentReceiptContact(input:{paymentId:number;patientId:string;clinicId:number}):Promise<{paymentId:number;patientId:string;clinicId:number;mobile:string|null}>{
 return portalApiPost(`/patient-payments/${input.paymentId}/receipt-open`,{patientId:input.patientId,clinicId:input.clinicId});
}

export interface SavedBookingEdit {
 appointmentTime:string;
 bookingEdit:{requestId:string;patientId:string;doctorId:number;serviceCode:string;department:string;
 expected:{appointmentTime:string;updatedAt:string;doctorId:number|null;serviceCode:string|null;department:string|null}};
}
export function saveExistingBooking(appointmentId:number,input:SavedBookingEdit):Promise<AppointmentActionResultView>{
 return portalApiPost(`/appointments/${appointmentId}/reschedule`,input);
}

/** V14g visit investigations. Only the server chooses catalogue prices and actors. */
export interface ClinicalInvestigationInput {
 readonly requestId: string; readonly choice: 'RVG'|'IOPA'|'OPG'|'CBCT'|'Other test';
 readonly toothRefs: readonly string[]; readonly area: 'mouth'|'upper'|'lower'|'q1'|'q2'|'q3'|'q4'|null;
}
export interface ClinicalInvestigationOrder {
 readonly id:string;readonly case_sheet_id:number;readonly clinic_id:number;readonly patient_id:string;
 readonly choice:ClinicalInvestigationInput['choice'];readonly tooth_refs:readonly string[];readonly area:ClinicalInvestigationInput['area'];
 readonly list_amount_minor_units:number|null;readonly currency:string|null;readonly ordered_at:string;readonly actor_staff_user_id:number;
 readonly pricing?:{readonly status:'unavailable'}|{readonly status:'available';readonly amountMinorUnits:number;readonly version:number;readonly waiver?:InvestigationWaiverFact};
 readonly reports:readonly {readonly id:string;readonly evidence_id:string;readonly linked_at:string;readonly actor_staff_user_id:number}[];
}
export interface ClinicalFinalizedAdditionContent {
 readonly version: 1;
 readonly questionnaire?:readonly FinalizedQuestionnaireAnswer[];
 readonly treatmentDoctors?:readonly {readonly planItemId:number;readonly doctorId:number}[];
 readonly examination?: ClinicalExaminationView;
 readonly prescription?: ClinicalPrescriptionView;
 readonly returnRequest?: ClinicalReturnRequestView;
 readonly notes?: Readonly<Partial<Record<'chief_complaint'|'examination_findings'|'diagnosis'|'clinical_notes', string>>>;
}
export interface FinalizedQuestionnaireQuestion extends Omit<PreconsultQuestionSpec,'answerKind'|'options'> {
 readonly version:number;
 readonly answerKind:'short_text'|'single_select'|'multi_select';
 readonly options?:readonly {readonly code:string;readonly label:string;readonly requiresDetail?:boolean;readonly safetyFlag?:boolean}[];
}
export interface FinalizedQuestionnaireAnswer {
 readonly questionKey:string;readonly questionVersion:number;readonly answerKind:'short_text'|'single_select'|'multi_select';
 readonly answerCode:string|null;readonly answerCodes:readonly string[]|null;readonly answerText:string|null;
 readonly answerDetails?:ClinicalQuestionDetails|null;readonly questionSnapshot?:FinalizedQuestionnaireQuestion;
}
export interface FinalizedQuestionnaireView {
 readonly available:boolean;readonly questions:readonly FinalizedQuestionnaireQuestion[];readonly answers:readonly FinalizedQuestionnaireAnswer[];
 readonly missingKeys:readonly string[];readonly detailOnlyKeys:readonly string[];
}
export interface ClinicalFinalizedAddition {
 readonly id:string; readonly revision:number; readonly caseSheetId:number; readonly content:ClinicalFinalizedAdditionContent;
 readonly recordedAt:string; readonly actorStaffUserId:number; readonly actorRole:'doctor'; readonly actorName?:string|null;
}
export interface ClinicalFinalizedAdditionsView {
 readonly available:boolean; readonly revision:number|null; readonly additions:readonly ClinicalFinalizedAddition[];
 readonly questionnaire?:FinalizedQuestionnaireView;
}
export const fetchClinicalFinalizedAdditions=(caseSheetId:number):Promise<ClinicalFinalizedAdditionsView>=>portalApiGet(`/case-sheets/${caseSheetId}/finalized-additions`);
export const appendClinicalFinalizedAdditionApi=(caseSheetId:number,input:{requestId:string;expectedRevision:number;content:ClinicalFinalizedAdditionContent}):Promise<{addition:ClinicalFinalizedAddition}>=>portalApiPost(`/case-sheets/${caseSheetId}/finalized-additions`,input);

export interface ClinicalInvestigationsView {
 readonly available:boolean;readonly reportsAvailable:boolean;readonly orders:readonly ClinicalInvestigationOrder[];
 readonly choices:readonly {readonly choice:ClinicalInvestigationInput['choice'];readonly available:boolean;readonly listAmountMinorUnits:number|null;readonly currency:string|null;readonly reason:string|null}[];
}
export const fetchClinicalInvestigations=(caseSheetId:number):Promise<ClinicalInvestigationsView>=>portalApiGet(`/case-sheets/${caseSheetId}/investigations`);
export const orderClinicalInvestigationApi=(caseSheetId:number,input:ClinicalInvestigationInput):Promise<unknown>=>portalApiPost(`/case-sheets/${caseSheetId}/investigations`,input);
export const linkClinicalInvestigationReportApi=(caseSheetId:number,orderId:string,input:{requestId:string;evidenceId:string}):Promise<unknown>=>portalApiPost(`/case-sheets/${caseSheetId}/investigations/${encodeURIComponent(orderId)}/reports`,input);

/** Scoped ordinary price edits; the server rechecks current treating identity. */
export interface InvestigationWaiverFact {readonly reason:string;readonly actorStaffUserId:number;readonly occurredAt:string}
export type DoctorPriceInput = ({requestId:string;amountMinorUnits:number;expectedVersion:number}&(
 {kind:'extra_discount';expectedDiscountId:number|null;expectedAcceptedSet?:string}|{kind:'investigation';orderId:string})) | {requestId:string;kind:'investigation_waiver';orderId:string;expectedVersion:number;reason:string};
export interface DoctorOrdinaryPricingView {
 readonly available:boolean;readonly canDiscount:boolean;readonly canWaiveInvestigations?:boolean;
 readonly extra:{readonly status:'unavailable'}|{readonly status:'available';readonly amountMinorUnits:number;readonly version:number;readonly discountId:number|null;readonly acceptedSet?:string|null};
 readonly investigations:readonly {readonly orderId:string;readonly amountMinorUnits:number;readonly version:number;readonly waiver?:InvestigationWaiverFact}[];
}
/** Normalized at the fetch boundary (LES-057): a partial or malformed body
 *  reads as pricing unavailable -- never a crashed consult, never a discount. */
export function normalizeDoctorOrdinaryPricing(wire:unknown):DoctorOrdinaryPricingView{
 const w=(wire&&typeof wire==='object'?wire:{}) as Partial<DoctorOrdinaryPricingView>;
 const extra=w.extra&&typeof w.extra==='object'&&(w.extra.status==='available'||w.extra.status==='unavailable')?w.extra:null;
 return {available:w.available===true&&extra!==null,canDiscount:w.canDiscount===true&&extra!==null,canWaiveInvestigations:w.canWaiveInvestigations===true,
  extra:extra??{status:'unavailable'},investigations:Array.isArray(w.investigations)?w.investigations:[]};
}
export const fetchDoctorOrdinaryPricing=async(sheet:number,expectedBearer?:string):Promise<DoctorOrdinaryPricingView>=>normalizeDoctorOrdinaryPricing(await portalApiGet(`/case-sheets/${sheet}/doctor-pricing`,expectedBearer));
export const setDoctorOrdinaryPrice=(sheet:number,input:DoctorPriceInput,expectedBearer:string):Promise<{action:{request_id:string;version:number;after_amount_minor_units:number}}>=>portalApiPost(`/case-sheets/${sheet}/doctor-pricing`,input,expectedBearer);
/** Saved qualitative work and immutable clinic-configured after-care. */
export type ClinicalTodayStatus = 'done'|'started'|'not_today';
export interface ClinicalAftercareSnapshot {readonly key:string;readonly version:number;readonly title:string;readonly instructions:string;readonly sourceReference:string}
export interface ClinicalAftercareTemplate extends ClinicalAftercareSnapshot {readonly catalogItemIds:readonly number[]}
export interface ClinicalTodayWorkInput {readonly requestId:string;readonly expectedRevision:number;readonly owningCaseSheetId:number;readonly planItemId:number;readonly status:ClinicalTodayStatus;readonly careTemplateKey:string|null;readonly careTemplateVersion:number|null;readonly toldPatient:boolean}
export interface ClinicalTodayWorkFact {readonly id:string;readonly caseSheetId:number;readonly owningCaseSheetId:number;readonly planItemId:number;readonly revision:number;readonly status:ClinicalTodayStatus;readonly care:ClinicalAftercareSnapshot|null;readonly toldPatient:boolean;readonly recordKind:'visit_record'|'dated_addition';readonly changedFields:readonly ('status'|'care'|'toldPatient')[];readonly visitDate:string;readonly recordedAt:string;readonly actorStaffUserId:number}
export interface ClinicalTodayWorkView {readonly available:boolean;readonly caseSheetId:number;readonly facts:readonly ClinicalTodayWorkFact[];readonly templates:readonly ClinicalAftercareTemplate[];readonly aftercareChoices:readonly {planItemId:number;owningCaseSheetId:number;template:ClinicalAftercareSnapshot}[];readonly authors:readonly {readonly id:number;readonly name:string|null}[]}
export const fetchClinicalTodayWork=(sheetId:number):Promise<ClinicalTodayWorkView>=>portalApiGet(`/case-sheets/${sheetId}/today-work`);
export const saveClinicalTodayWork=(sheetId:number,input:ClinicalTodayWorkInput,finalizedAddition=false):Promise<{fact:ClinicalTodayWorkFact;replay:boolean}>=>portalApiPost(`/case-sheets/${sheetId}/today-work${finalizedAddition?'/additions':''}`,input);

export interface ClinicalConsentCaptureInput {
  readonly requestId: string;
  readonly evidenceId: string;
  readonly purpose: 'consultation' | 'treatment';
  readonly treatmentPlanItemIds: readonly number[];
  readonly supersedesConsentId?: number;
  readonly voidReason?: TreatmentConsentVoidReasonView;
}
export async function captureClinicalConsentApi(caseSheetId: number, input: ClinicalConsentCaptureInput, expectedBearer?: string): Promise<TreatmentConsentView> {
  return portalApiPost<TreatmentConsentView>(`/case-sheets/${caseSheetId}/consents/capture`, input, expectedBearer);
}
